import { parseCron, cronMinuteKey, cronMatches } from './chunk-OU3ITB5S.js';
import { createContextFromBindings, interpolateValue, deferEntityBindings } from './chunk-ZWAOJT6R.js';
import { seedRandom, randomArrayElement, randomInt, shuffleArray, randomPastDate } from './chunk-T4VDAB4C.js';
import { collectTraitRefsFromValue, collectTraitRefsFromEffects } from './chunk-SCRAHWOC.js';
import { createLogger, setNamespaceLevel } from '@almadar/logger';
import { evaluateGuard, SExpressionEvaluator } from '@almadar/evaluator';
import { omitFrameFields, isPersistBatchOperation, normalizeCallSiteConfigToValues, isInlineTrait, isEntityCall, OrbitalSchemaSchema, isEntityReference, parseEntityRef, parseImportedTraitRef, isPageReference, isPageReferenceString, isPageReferenceObject, parsePageRef, parseOrbitalRef, overrideDeclaredKnobs, asTraitId, asEntityId, asPageId, asOrbitalId, deriveId, isReferenceConfigType, configRefEventKnob, resolveConfigRefEventName, isCallSiteConfigDeclaration, asEventId, ledgerRename, idPrefix, eventListPropsOf } from '@almadar/core';
export { normalizeCallSiteConfigToValues } from '@almadar/core';
import { RESERVED_FIELD_NAMES, sampleRowCount, linkSelfRelationField, sampleRow, identityEntitiesOf, roleVocabularyOf } from '@almadar/core/mock';

var log = createLogger("almadar:runtime:eventbus");
var EventBus = class {
  listeners = /* @__PURE__ */ new Map();
  /** Maximum recursion depth before circuit breaker activates (RCG-05) */
  maxDepth;
  /** Current emission depth for circular loop detection */
  depth = 0;
  constructor(options = {}) {
    void options.debug;
    this.maxDepth = options.maxDepth ?? 10;
  }
  /**
   * Emit an event to all registered listeners.
   *
   * Includes circuit breaker (RCG-05): if emit is called recursively
   * beyond `maxDepth`, the event is dropped and an error is logged.
   * This prevents infinite loops from circular emit/listen chains.
   */
  emit(type, payload, source, routingKey) {
    if (this.depth >= this.maxDepth) {
      log.error("circular event loop dropped", { type, depth: this.depth, maxDepth: this.maxDepth });
      return;
    }
    const event = {
      type,
      payload,
      timestamp: Date.now(),
      source
    };
    const deliveryKey = routingKey ?? type;
    const primary = this.listeners.get(deliveryKey);
    const secondary = routingKey !== void 0 && routingKey !== type ? this.listeners.get(type) : void 0;
    const listenerCount = (primary?.size ?? 0) + (secondary?.size ?? 0);
    if (listenerCount > 0) {
      log.debug("emit", { type, listenerCount, depth: this.depth });
    } else {
      log.debug("emit no listeners", { type });
    }
    this.depth++;
    try {
      const delivered = /* @__PURE__ */ new Set();
      const deliverTo = (set) => {
        if (!set) return;
        for (const listener of Array.from(set)) {
          if (delivered.has(listener)) continue;
          delivered.add(listener);
          try {
            listener(event);
          } catch (error) {
            log.error("listener threw", { type, error: error instanceof Error ? error : String(error) });
          }
        }
      };
      deliverTo(primary);
      deliverTo(secondary);
      if (type !== "*") {
        const wildcardListeners = this.listeners.get("*");
        if (wildcardListeners) {
          for (const listener of Array.from(wildcardListeners)) {
            try {
              listener(event);
            } catch (error) {
              log.error("wildcard listener threw", { error: error instanceof Error ? error : String(error) });
            }
          }
        }
      }
    } finally {
      this.depth--;
    }
  }
  /**
   * Subscribe to an event type
   */
  on(type, listener) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, /* @__PURE__ */ new Set());
    }
    const listeners = this.listeners.get(type);
    listeners.add(listener);
    log.debug("subscribe", { type, total: listeners.size });
    return () => {
      listeners.delete(listener);
      log.debug("unsubscribe", { type, remaining: listeners.size });
      if (listeners.size === 0) {
        this.listeners.delete(type);
      }
    };
  }
  /**
   * Subscribe to ALL events (wildcard listener)
   * Useful for event tracking, logging, debugging
   */
  onAny(listener) {
    return this.on("*", listener);
  }
  /**
   * Check if there are listeners for an event type
   */
  hasListeners(type) {
    const listeners = this.listeners.get(type);
    return listeners !== void 0 && listeners.size > 0;
  }
  /**
   * Get all registered event types
   */
  getRegisteredEvents() {
    return Array.from(this.listeners.keys());
  }
  /**
   * Clear all listeners
   */
  clear() {
    log.debug("clear", { eventTypeCount: this.listeners.size });
    this.listeners.clear();
  }
  /**
   * Get listener count for an event type (for testing)
   */
  getListenerCount(type) {
    return this.listeners.get(type)?.size ?? 0;
  }
};

// src/identity/routing.ts
function eventRouteKey(eventName, eventId) {
  return eventId ? `@evt:${eventId}` : eventName;
}
function buildSourceMatcher(src, listenerOrbital) {
  if (src.kind === "any") return () => true;
  if (src.kind === "trait") {
    if (src.traitId !== void 0) {
      const wantedTraitId = src.traitId;
      return (source) => !!source && source.traitId === wantedTraitId;
    }
    const wantedTrait2 = src.trait;
    return (source) => !!source && source.orbital === listenerOrbital && source.trait === wantedTrait2;
  }
  if (src.orbitalId !== void 0 && src.traitId !== void 0) {
    const wantedOrbitalId = src.orbitalId;
    const wantedTraitId = src.traitId;
    return (source) => !!source && source.orbitalId === wantedOrbitalId && source.traitId === wantedTraitId;
  }
  const wantedOrbital = src.orbital;
  const wantedTrait = src.trait;
  return (source) => !!source && source.orbital === wantedOrbital && source.trait === wantedTrait;
}
function parseListenSource(listener, listenerOrbital) {
  const explicit = listener.source;
  if (explicit && typeof explicit === "object") {
    return {
      bareEvent: listener.event,
      matcher: buildSourceMatcher(explicit, listenerOrbital)
    };
  }
  const key = listener.event;
  const parts = key.split(".");
  if (parts.length === 1) {
    return { bareEvent: key, matcher: () => true };
  }
  if (parts.length === 2) {
    const [sourceOrStar, eventName] = parts;
    if (sourceOrStar === "*") {
      return { bareEvent: eventName, matcher: () => true };
    }
    return {
      bareEvent: eventName,
      matcher: buildSourceMatcher(
        { kind: "trait", trait: sourceOrStar },
        listenerOrbital
      )
    };
  }
  if (parts.length >= 3) {
    const eventName = parts[parts.length - 1];
    const trait = parts[parts.length - 2];
    const orbital = parts.slice(0, parts.length - 2).join(".");
    return {
      bareEvent: eventName,
      matcher: buildSourceMatcher({ kind: "orbital", orbital, trait }, listenerOrbital)
    };
  }
  return { bareEvent: key, matcher: () => true };
}

// src/TickScheduler.ts
var EVERY_PASS = 0;
var CRON_CHECK_INTERVAL_MS = 1e3;
var TickScheduler = class {
  ticks = /* @__PURE__ */ new Map();
  nextId = 1;
  running = false;
  paused = false;
  frameHandle = null;
  hasRaf;
  constructor() {
    this.hasRaf = typeof requestAnimationFrame === "function" && typeof cancelAnimationFrame === "function";
  }
  /**
   * Register a tick. `intervalMs <= 0` means "every pass" (a frame-interval
   * tick, matching LOLO's `interval: "frame"`). Returns a handle to stop
   * just this tick.
   */
  add(intervalMs, onDue) {
    const id = this.nextId++;
    this.ticks.set(id, {
      kind: "interval",
      intervalMs: intervalMs > 0 ? intervalMs : EVERY_PASS,
      accumulatedMs: 0,
      lastTimestamp: null,
      onDue
    });
    this.ensureRunning();
    return {
      stop: () => {
        this.ticks.delete(id);
        this.maybeStop();
      }
    };
  }
  /**
   * Register a cron-scheduled tick (`ticks { ... every "0 9 * * *" }`).
   * Fires at most once per matching calendar minute — the same dedup shape
   * `os/watch-cron` uses. Throws if `expression` isn't a valid 5-field cron
   * string; callers on a path the compiler has already validated (e.g.
   * generated code) won't hit this, but a raw runtime value might.
   */
  addCron(expression, onDue) {
    const fields = parseCron(expression);
    const id = this.nextId++;
    this.ticks.set(id, {
      kind: "cron",
      fields,
      accumulatedMs: CRON_CHECK_INTERVAL_MS,
      // check on the first pass, not after a full second
      lastTimestamp: null,
      lastFiredMinuteKey: -1,
      onDue
    });
    this.ensureRunning();
    return {
      stop: () => {
        this.ticks.delete(id);
        this.maybeStop();
      }
    };
  }
  /** Stop every tick and the shared loop. */
  stopAll() {
    this.ticks.clear();
    this.maybeStop();
  }
  /** True while the scheduler is paused (no tick callback fires and no backlog accumulates). */
  get isPaused() {
    return this.paused;
  }
  /**
   * Halt the shared loop without dropping registered ticks. No `onDue`
   * fires again until `resume()`. A no-op if already paused.
   */
  pause() {
    if (this.paused) return;
    this.paused = true;
    this.stopLoop();
  }
  /**
   * Restart the shared loop after `pause()`. Every registered tick's phase
   * is reset (`lastTimestamp = null`) so the paused duration is never
   * counted as elapsed time — the same "first pass just captures the
   * timestamp" behavior a freshly `add()`-ed tick gets, which is what keeps
   * resume from bursting a backlog of missed beats. A no-op if not paused.
   */
  resume() {
    if (!this.paused) return;
    this.paused = false;
    for (const tick of this.ticks.values()) {
      tick.lastTimestamp = null;
    }
    if (this.ticks.size > 0) {
      this.ensureRunning();
    }
  }
  ensureRunning() {
    if (this.running || this.paused) return;
    this.running = true;
    if (this.hasRaf) {
      const loop = (timestamp) => {
        if (!this.running) return;
        this.advance(timestamp);
        this.frameHandle = requestAnimationFrame(loop);
      };
      this.frameHandle = requestAnimationFrame(loop);
    } else {
      this.frameHandle = setInterval(() => this.advance(Date.now()), 16);
    }
  }
  maybeStop() {
    if (this.ticks.size > 0 || !this.running) return;
    this.stopLoop();
  }
  /** Cancel the active rAF/interval loop, if any, and clear the running flag. */
  stopLoop() {
    this.running = false;
    if (this.frameHandle !== null) {
      if (this.hasRaf) {
        cancelAnimationFrame(this.frameHandle);
      } else {
        clearInterval(this.frameHandle);
      }
      this.frameHandle = null;
    }
  }
  advance(timestamp) {
    for (const tick of this.ticks.values()) {
      if (tick.kind === "cron") {
        this.advanceCron(tick, timestamp);
        continue;
      }
      if (tick.intervalMs <= EVERY_PASS) {
        tick.onDue();
        continue;
      }
      if (tick.lastTimestamp === null) {
        tick.lastTimestamp = timestamp;
        continue;
      }
      tick.accumulatedMs += timestamp - tick.lastTimestamp;
      tick.lastTimestamp = timestamp;
      if (tick.accumulatedMs >= tick.intervalMs) {
        tick.onDue();
        tick.accumulatedMs = tick.accumulatedMs % tick.intervalMs;
      }
    }
  }
  /** Cron ticks check calendar-match at most once a second, deduped per matching minute. */
  advanceCron(tick, timestamp) {
    if (tick.lastTimestamp === null) {
      tick.lastTimestamp = timestamp;
      return;
    }
    tick.accumulatedMs += timestamp - tick.lastTimestamp;
    tick.lastTimestamp = timestamp;
    if (tick.accumulatedMs < CRON_CHECK_INTERVAL_MS) return;
    tick.accumulatedMs = 0;
    const now = /* @__PURE__ */ new Date();
    const minuteKey = cronMinuteKey(now);
    if (minuteKey !== tick.lastFiredMinuteKey && cronMatches(tick.fields, now)) {
      tick.lastFiredMinuteKey = minuteKey;
      tick.onDue();
    }
  }
};
function createTickScheduler() {
  return new TickScheduler();
}

// src/duration.ts
function parseDurationString(interval) {
  const match = interval.match(/^(\d+)(ms|s|m|h)?$/);
  if (!match) {
    throw new Error(
      `Invalid duration '${interval}': expected a shape like '5ms'/'5s'/'1m'/'1h'`
    );
  }
  const value = parseInt(match[1], 10);
  const unit = match[2] || "ms";
  switch (unit) {
    case "ms":
      return value;
    case "s":
      return value * 1e3;
    case "m":
      return value * 60 * 1e3;
    case "h":
      return value * 60 * 60 * 1e3;
    default:
      return value;
  }
}
function isValidDurationString(interval) {
  return /^(\d+)(ms|s|m|h)?$/.test(interval);
}
var smLog = createLogger("almadar:runtime:sm");
var LIFECYCLE_EVENTS = ["INIT", "LOAD", "$MOUNT"];
function findInitialState(trait) {
  if (!trait.states || trait.states.length === 0) {
    smLog.warn("trait-has-no-states", { trait: trait.name });
    return "unknown";
  }
  const markedInitial = trait.states.find((s) => s.isInitial)?.name;
  const firstState = trait.states[0]?.name;
  return markedInitial || firstState || "unknown";
}
function createInitialTraitState(trait) {
  return {
    traitName: trait.name,
    currentState: findInitialState(trait),
    previousState: null,
    lastEvent: null,
    context: {}
  };
}
function findMatchingTransitions(trait, currentState, eventKey, eventId) {
  if (!trait.transitions || trait.transitions.length === 0) {
    return [];
  }
  return trait.transitions.filter((t) => {
    const fromMatches = Array.isArray(t.from) ? t.from.includes(currentState) : t.from === currentState;
    if (!fromMatches) return false;
    if (eventId && t.eventId) {
      return t.eventId === eventId;
    }
    return t.event === eventKey;
  });
}
function findTransition(trait, currentState, eventKey, eventId) {
  return findMatchingTransitions(trait, currentState, eventKey, eventId)[0];
}
function normalizeEventKey(eventKey) {
  if (!eventKey) return "";
  return eventKey.startsWith("UI:") ? eventKey.slice(3) : eventKey;
}
function processEvent(options) {
  const {
    traitState,
    trait,
    eventKey,
    eventId,
    payload,
    entityData,
    config,
    user,
    guardMode = "permissive",
    strictBindings = false,
    contextExtensions
  } = options;
  const normalizedEvent = normalizeEventKey(eventKey);
  const candidates = findMatchingTransitions(trait, traitState.currentState, normalizedEvent, eventId);
  if (candidates.length === 0) {
    smLog.debug("noTransition", { trait: trait.name, event: normalizedEvent, currentState: traitState.currentState });
    return {
      executed: false,
      newState: traitState.currentState,
      previousState: traitState.currentState,
      effects: []
    };
  }
  let lastFailedGuardTransition;
  for (const transition of candidates) {
    smLog.debug("processEvent", { trait: trait.name, event: normalizedEvent, currentState: traitState.currentState, to: transition.to });
    if (transition.guard === void 0 || transition.guard === null) {
      return {
        executed: true,
        newState: transition.to,
        previousState: traitState.currentState,
        effects: transition.effects || [],
        transition: {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent
        }
      };
    }
    const ctx = createContextFromBindings({
      entity: entityData,
      payload,
      state: traitState.currentState,
      // Surface call-site config so `@config.X` resolves inside
      // guard expressions (matches the validator's allowed sigil
      // list as of v3.12.0). createContextFromBindings already
      // propagates `bindings.config` to ctx.config when present —
      // we just need to pass it through here.
      config,
      user
    }, strictBindings, contextExtensions);
    try {
      const guardPasses = evaluateGuard(
        transition.guard,
        ctx
      );
      smLog.debug("guard:evaluate", { trait: trait.name, event: normalizedEvent, guardResult: guardPasses });
      if (guardPasses) {
        return {
          executed: true,
          newState: transition.to,
          previousState: traitState.currentState,
          effects: transition.effects || [],
          transition: {
            from: traitState.currentState,
            to: transition.to,
            event: normalizedEvent
          },
          guardResult: true
        };
      }
      lastFailedGuardTransition = transition;
    } catch (error) {
      if (guardMode === "strict") {
        smLog.error("guard-error-blocks-transition", {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent,
          error: error instanceof Error ? error : String(error)
        });
        return {
          executed: false,
          newState: traitState.currentState,
          previousState: traitState.currentState,
          effects: [],
          transition: {
            from: traitState.currentState,
            to: transition.to,
            event: normalizedEvent
          },
          guardResult: false
        };
      }
      smLog.error("guard-evaluation-error", {
        error: error instanceof Error ? error : String(error)
      });
      return {
        executed: true,
        newState: transition.to,
        previousState: traitState.currentState,
        effects: transition.effects || [],
        transition: {
          from: traitState.currentState,
          to: transition.to,
          event: normalizedEvent
        },
        guardResult: true
      };
    }
  }
  return {
    executed: false,
    newState: traitState.currentState,
    previousState: traitState.currentState,
    effects: [],
    transition: lastFailedGuardTransition ? {
      from: traitState.currentState,
      to: lastFailedGuardTransition.to,
      event: normalizedEvent
    } : void 0,
    guardResult: false
  };
}
var SINGLETON_SCOPE = "__singleton__";
function scopeOf(entityData) {
  const id = entityData && entityData["id"];
  return id ?? SINGLETON_SCOPE;
}
function compositeKey(traitName, scope) {
  return `${traitName}::${scope}`;
}
function selectDispatchCandidates(traitNames, options) {
  const candidates = [];
  for (const traitName of traitNames) {
    if (options.targetTrait !== void 0) {
      if (traitName === options.targetTrait) candidates.push(traitName);
      continue;
    }
    if (options.activeTraits && !options.activeTraits.has(traitName)) continue;
    if (options.canHandle && !options.canHandle(traitName)) continue;
    candidates.push(traitName);
  }
  return candidates;
}
var StateMachineManager = class {
  traits = /* @__PURE__ */ new Map();
  /**
   * V4 identity index: trait id → trait name. Populated for traits that
   * carry an `id` (ledger-backed schemas). Lets callers resolve a trait's
   * state by its stable id, which survives a mid-session rename — the
   * name-keyed maps are re-pointed on rename via {@link renameTrait}, but
   * an id holder never has to observe the rename at all. Empty for legacy
   * id-free schemas, where every lookup stays name-keyed (unchanged).
   */
  traitIdToName = /* @__PURE__ */ new Map();
  /**
   * Per-trait call-site config, surfaced to guard expressions so
   * `@config.X` resolves at runtime. Populated by the orbital's
   * registration step (see `OrbitalServerRuntime.registerOrbital`'s
   * `configByTrait` projection). Empty for atom-scope traits whose
   * call-site config wasn't supplied — guards that read `@config.X`
   * in that case will see `undefined` and short-circuit accordingly.
   */
  traitConfigs = /* @__PURE__ */ new Map();
  /**
   * State map keyed by `${traitName}::${entityId | __singleton__}`.
   *
   * Each entity instance of an orbital gets its own copy of every
   * trait's state machine. This is what enables N parallel subagents
   * (one OrbitalProcess row per dispatched orbital) to all run their
   * own Build/Validate/Fix cycle without colliding on shared trait
   * state — the original cause of GAP-AGB-2.
   *
   * Initial state is created lazily on first event for each scope, so
   * adding a trait does not pre-allocate state for every potential
   * entity (and there's no need to know entity ids up front).
   */
  states = /* @__PURE__ */ new Map();
  config;
  observer;
  // Actor-model queues, also keyed by `${traitName}::${entityId}` so
  // each entity gets its own actor loop and concurrent subagents do
  // not serialize through a shared queue.
  queues = /* @__PURE__ */ new Map();
  processing = /* @__PURE__ */ new Set();
  constructor(traits = [], config = {}, observer) {
    this.config = config;
    this.observer = observer;
    for (const trait of traits) {
      this.addTrait(trait);
    }
  }
  /**
   * Set the transition observer for runtime verification.
   * Wire this to `verificationRegistry.recordTransition()` to enable
   * automatic verification tracking.
   */
  setObserver(observer) {
    this.observer = observer;
  }
  /**
   * Add a trait to the manager.
   */
  addTrait(trait) {
    this.traits.set(trait.name, trait);
    if (trait.id !== void 0) {
      this.traitIdToName.set(trait.id, trait.name);
    }
  }
  /**
   * Resolve a trait by its name, in the SAME `TraitDefinition` shape
   * `sendEvent`/`processEvent` use internally (flat `states`/`transitions`,
   * not `Trait` from `@almadar/core`'s nested `stateMachine.*` — the two
   * are NOT structurally interchangeable, confirmed by a `tsup` DTS build
   * failure when a caller tried passing a raw `Trait` to `runTraitCascade`
   * directly). Needed by callers that must re-invoke `processEvent` for a
   * trait outside `sendEvent`'s own loop (same-trait cascade continuation
   * — see `TraitCascade.ts`).
   */
  getTraitDefinition(traitName) {
    return this.traits.get(traitName);
  }
  /**
   * Resolve a trait by its V4 id. Returns undefined when no trait carries
   * that id (legacy schema, or unknown id). Exact-match only — no name
   * similarity.
   */
  getTraitById(traitId) {
    const name = this.traitIdToName.get(traitId);
    return name === void 0 ? void 0 : this.traits.get(name);
  }
  /**
   * Get a trait's current state by its V4 id (id-first lookup). Falls back
   * to `undefined` when the id is unknown. The id survives a rename, so a
   * holder of the id reads the right state without ever seeing the new name.
   */
  getStateById(traitId, entityId) {
    const name = this.traitIdToName.get(traitId);
    return name === void 0 ? void 0 : this.getState(name, entityId);
  }
  /**
   * Apply a mid-session trait rename: re-point the name-keyed maps from
   * `oldName` to `newName`, preserving live state, queues, and the id
   * index. A no-op when the trait isn't registered under `oldName`. This
   * is the interpreter-side of a ledger `curName` edit for the trait's own
   * name-keyed storage; id-keyed references need no update.
   */
  renameTrait(oldName, newName) {
    const trait = this.traits.get(oldName);
    if (!trait || oldName === newName) return;
    const renamed = { ...trait, name: newName };
    this.traits.delete(oldName);
    this.traits.set(newName, renamed);
    if (renamed.id !== void 0) {
      this.traitIdToName.set(renamed.id, newName);
    }
    const remap = (m) => {
      const prefix2 = `${oldName}::`;
      for (const key of [...m.keys()]) {
        if (key.startsWith(prefix2)) {
          const scope = key.slice(prefix2.length);
          const v = m.get(key);
          m.delete(key);
          m.set(`${newName}::${scope}`, v);
        }
      }
    };
    remap(this.states);
    remap(this.queues);
    const prefix = `${oldName}::`;
    for (const key of [...this.processing]) {
      if (key.startsWith(prefix)) {
        this.processing.delete(key);
        this.processing.add(`${newName}::${key.slice(prefix.length)}`);
      }
    }
    const cfg = this.traitConfigs.get(oldName);
    if (cfg !== void 0) {
      this.traitConfigs.set(newName, cfg);
      this.traitConfigs.delete(oldName);
    }
  }
  /**
   * Bind the call-site config for a trait so guard `@config.X`
   * resolves at runtime. Typically called by the orbital
   * registration step right after `addTrait`. Idempotent; passing
   * `undefined` clears the binding.
   */
  setTraitConfig(traitName, config) {
    if (config === void 0) {
      this.traitConfigs.delete(traitName);
    } else {
      this.traitConfigs.set(traitName, config);
    }
  }
  /**
   * Remove a trait from the manager.
   */
  removeTrait(traitName) {
    const removed = this.traits.get(traitName);
    if (removed?.id !== void 0) {
      this.traitIdToName.delete(removed.id);
    }
    this.traits.delete(traitName);
    const prefix = `${traitName}::`;
    for (const key of [...this.states.keys()]) {
      if (key.startsWith(prefix)) this.states.delete(key);
    }
    for (const key of [...this.queues.keys()]) {
      if (key.startsWith(prefix)) this.queues.delete(key);
    }
    for (const key of [...this.processing]) {
      if (key.startsWith(prefix)) this.processing.delete(key);
    }
  }
  /**
   * Get-or-init the state row for a (trait, entityId) pair.
   * Returns undefined if the trait isn't registered.
   */
  getOrInitState(traitName, scope) {
    const trait = this.traits.get(traitName);
    if (!trait) return void 0;
    const key = compositeKey(traitName, scope);
    let state = this.states.get(key);
    if (!state) {
      state = createInitialTraitState(trait);
      this.states.set(key, state);
    }
    return state;
  }
  /**
   * Get current state for a trait.
   *
   * For single-entity orbitals, omit `entityId` — returns the singleton
   * scope. For multi-entity orbitals (e.g. agent OrbitalSubagent), pass
   * the entity row id to look up that specific instance's state.
   */
  getState(traitName, entityId) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key);
    if (existing) return existing;
    return this.getOrInitState(traitName, scope);
  }
  /**
   * Get a flat traitName→state map. For multi-entity orbitals this
   * returns one entry per trait collapsed onto the singleton scope (or
   * the first observed scope if singleton is empty). Use
   * `getAllStatesByScope()` to get the full per-entity view.
   */
  getAllStates() {
    const out = /* @__PURE__ */ new Map();
    for (const traitName of this.traits.keys()) {
      const singleton = this.states.get(compositeKey(traitName, SINGLETON_SCOPE));
      if (singleton) {
        out.set(traitName, singleton);
        continue;
      }
      for (const [key, state] of this.states) {
        if (key.startsWith(`${traitName}::`)) {
          out.set(traitName, state);
          break;
        }
      }
    }
    return out;
  }
  /**
   * Get every per-entity state row, grouped by trait. Useful for
   * inspecting parallel subagent state.
   */
  getAllStatesByScope() {
    const grouped = /* @__PURE__ */ new Map();
    for (const [key, state] of this.states) {
      const sep = key.indexOf("::");
      if (sep < 0) continue;
      const traitName = key.slice(0, sep);
      const scope = key.slice(sep + 2);
      let bucket = grouped.get(traitName);
      if (!bucket) {
        bucket = /* @__PURE__ */ new Map();
        grouped.set(traitName, bucket);
      }
      bucket.set(scope, state);
    }
    return grouped;
  }
  /**
   * Check if a trait can handle an event from its current state.
   */
  canHandleEvent(traitName, eventKey, entityId, eventId) {
    const trait = this.traits.get(traitName);
    const state = this.getOrInitState(traitName, entityId ?? SINGLETON_SCOPE);
    if (!trait || !state) return false;
    return !!findTransition(trait, state.currentState, normalizeEventKey(eventKey), eventId);
  }
  /**
   * Send an event to all traits.
   *
   * `entityByTrait` lets callers supply per-trait entity rows for guard
   * evaluation when traits accumulate scalar state via `(set @entity.X)`
   * effects across transitions. The runtime UI hook builds this map from
   * its `traitFieldStatesRef` before each dispatch so guards reading
   * `@entity.X` see prior step writes — required for [runtime] entities
   * that have no persistence row to reload.
   *
   * `eventId` is the V4 dual-carry id sibling of `eventKey` (see
   * {@link ProcessEventOptions.eventId}) — additive and optional, threaded
   * through to `processEvent` for id-primary transition matching.
   *
   * @returns Array of transition results (one per trait that had a matching transition)
   */
  sendEvent(eventKey, payload, entityData, entityByTrait, eventId, targetTrait, user, allowedTraits) {
    const results = [];
    const scope = scopeOf(entityData);
    const candidates = new Set(
      selectDispatchCandidates(this.traits.keys(), { targetTrait, activeTraits: allowedTraits })
    );
    for (const [traitName, trait] of this.traits) {
      if (!candidates.has(traitName)) continue;
      const traitState = this.getOrInitState(traitName, scope);
      if (!traitState) continue;
      const key = compositeKey(traitName, scope);
      const perTraitEntity = entityByTrait?.[traitName] ?? entityData;
      const result = processEvent({
        traitState,
        trait,
        eventKey,
        eventId,
        payload,
        entityData: perTraitEntity,
        config: this.traitConfigs.get(traitName),
        user,
        guardMode: this.config.guardMode,
        strictBindings: this.config.strictBindings,
        contextExtensions: this.config.contextExtensions
      });
      if (result.executed) {
        this.states.set(key, {
          ...traitState,
          currentState: result.newState,
          previousState: result.previousState,
          lastEvent: normalizeEventKey(eventKey),
          context: { ...traitState.context, ...payload }
        });
        results.push({ traitName, result });
        if (this.observer && result.transition) {
          this.observer.onTransition({
            traitName,
            from: result.transition.from,
            to: result.transition.to,
            event: result.transition.event,
            guardResult: result.guardResult,
            // Effects will be traced when executed — placeholder here
            effects: []
          });
        }
      }
    }
    return results;
  }
  /**
   * Commit a same-trait cascade's FINAL state (see `runTraitCascade` in
   * `TraitCascade.ts`). `sendEvent` above already committed the state for
   * the single hop it computed; when a caller then drives that trait's own
   * emitted event through additional hops via `runTraitCascade` (bypassing
   * `sendEvent` for those extra hops, since they're same-trait continuations
   * rather than a new external dispatch), the manager's tracked state is
   * stale until this is called — otherwise `getAllStates()`/`canHandleEvent()`
   * would report the FIRST hop's state even though the trait actually
   * settled further along. A no-op if the trait has no tracked state yet
   * for this scope (nothing to update).
   */
  setCascadeFinalState(traitName, entityId, finalState, lastEvent) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    const existing = this.states.get(key);
    if (!existing) return;
    this.states.set(key, {
      ...existing,
      previousState: existing.currentState,
      currentState: finalState,
      lastEvent: normalizeEventKey(lastEvent)
    });
  }
  // ========================================================================
  // Actor-Model Queue API (opt-in, does not affect sendEvent)
  // ========================================================================
  /**
   * Enqueue an event into every trait's per-trait queue.
   *
   * Events are not processed immediately. Call `drainQueue()` for each
   * trait to process them sequentially (actor-model guarantee: one event
   * at a time per trait, effects fully awaited before the next event).
   */
  enqueueEvent(eventKey, payload, entityData, entityByTrait, eventId, user) {
    const scope = scopeOf(entityData);
    for (const [traitName] of this.traits) {
      const key = compositeKey(traitName, scope);
      const queue = this.queues.get(key) ?? [];
      queue.push({ eventKey, eventId, payload, entityData, entityByTrait, user });
      this.queues.set(key, queue);
    }
  }
  /**
   * Drain a single (trait, entity) pair's event queue, processing
   * events sequentially. Pass `entityId` to drain a specific entity
   * instance; omit it to drain the singleton scope.
   */
  async drainQueue(traitName, executeEffects, entityId) {
    const scope = entityId ?? SINGLETON_SCOPE;
    const key = compositeKey(traitName, scope);
    if (this.processing.has(key)) return;
    this.processing.add(key);
    const queue = this.queues.get(key) ?? [];
    while (queue.length > 0) {
      const entry = queue.shift();
      const trait = this.traits.get(traitName);
      const traitState = this.getOrInitState(traitName, scope);
      if (!trait || !traitState) continue;
      const perTraitEntity = entry.entityByTrait?.[traitName] ?? entry.entityData;
      const result = processEvent({
        traitState,
        trait,
        eventKey: entry.eventKey,
        eventId: entry.eventId,
        payload: entry.payload,
        entityData: perTraitEntity,
        config: this.traitConfigs.get(traitName),
        user: entry.user,
        guardMode: this.config.guardMode,
        strictBindings: this.config.strictBindings,
        contextExtensions: this.config.contextExtensions
      });
      if (result.executed) {
        this.states.set(key, {
          ...traitState,
          currentState: result.newState,
          previousState: result.previousState,
          lastEvent: normalizeEventKey(entry.eventKey),
          context: { ...traitState.context, ...entry.payload }
        });
        if (this.observer && result.transition) {
          this.observer.onTransition({
            traitName,
            from: result.transition.from,
            to: result.transition.to,
            event: result.transition.event,
            guardResult: result.guardResult,
            effects: []
          });
        }
        await executeEffects(traitName, result, entry.payload);
      }
    }
    this.processing.delete(key);
  }
  /**
   * Check whether a trait's queue is currently being drained.
   */
  isProcessing(traitName, entityId) {
    return this.processing.has(compositeKey(traitName, entityId ?? SINGLETON_SCOPE));
  }
  /**
   * Get the number of pending events in a trait's queue.
   */
  getQueueLength(traitName, entityId) {
    return this.queues.get(compositeKey(traitName, entityId ?? SINGLETON_SCOPE))?.length ?? 0;
  }
  /**
   * Reset a trait to its initial state.
   *
   * If `entityId` is provided, resets only that entity's scope.
   * Otherwise resets every entity scope known for the trait.
   */
  resetTrait(traitName, entityId) {
    const trait = this.traits.get(traitName);
    if (!trait) return;
    if (entityId) {
      this.states.set(compositeKey(traitName, entityId), createInitialTraitState(trait));
      return;
    }
    const prefix = `${traitName}::`;
    for (const key of [...this.states.keys()]) {
      if (key.startsWith(prefix)) {
        this.states.set(key, createInitialTraitState(trait));
      }
    }
  }
  /**
   * Reset all traits to initial states (every entity scope).
   */
  resetAll() {
    for (const [traitName, trait] of this.traits) {
      const prefix = `${traitName}::`;
      for (const key of [...this.states.keys()]) {
        if (key.startsWith(prefix)) {
          this.states.set(key, createInitialTraitState(trait));
        }
      }
    }
  }
};
var cascadeLog = createLogger("almadar:runtime:trait-cascade");
var DEFAULT_MAX_STEPS = 20;
async function runTraitCascade(options) {
  const {
    trait,
    fromState,
    eventKey,
    payload,
    getEntityData,
    config,
    user,
    guardMode,
    strictBindings,
    contextExtensions,
    runEffects,
    maxSteps = DEFAULT_MAX_STEPS,
    logContext
  } = options;
  const effectResults = [];
  const emitted = [];
  const visited = /* @__PURE__ */ new Set();
  const queue = [{ event: eventKey, payload }];
  let currentState = fromState;
  let steps = 0;
  let executed = false;
  let cappedAt;
  while (queue.length > 0 && steps < maxSteps) {
    const item = queue.shift();
    const stepFromState = currentState;
    const stepKey = `${item.event}:${stepFromState}:${JSON.stringify(item.payload) ?? ""}`;
    if (visited.has(stepKey)) continue;
    visited.add(stepKey);
    const entityData = await getEntityData();
    const result = processEvent({
      traitState: { traitName: trait.name, currentState: stepFromState, previousState: null, lastEvent: null, context: {} },
      trait,
      eventKey: item.event,
      payload: item.payload,
      entityData,
      config,
      user,
      guardMode,
      strictBindings,
      contextExtensions
    });
    if (!result.executed) continue;
    steps += 1;
    if (steps === 1) executed = true;
    currentState = result.newState;
    if (result.effects.length > 0) {
      const stepOutcome = await runEffects(result.effects, {
        fromState: stepFromState,
        toState: result.newState,
        event: item.event,
        payload: item.payload
      });
      effectResults.push(...stepOutcome.effectResults);
      emitted.push(...stepOutcome.emitted);
      for (const e of stepOutcome.emitted) {
        if (findMatchingTransitions(trait, result.newState, e.event).length === 0) continue;
        e.source = { ...e.source, dispatched: true };
        queue.push({ event: e.event, payload: e.payload });
      }
    }
  }
  if (steps >= maxSteps) {
    cappedAt = maxSteps;
    cascadeLog.warn("cascade:cap-hit", { trait: trait.name, maxSteps, ...logContext });
  }
  return { executed, finalState: currentState, effectResults, emitted, steps, cappedAt };
}

// src/types.ts
var HANDLER_MANIFEST = {
  client: ["render-ui", "render", "navigate", "navigate-back", "emit", "set", "log", "ref", "deref", "watch", "send-server", "browser/open-file-picker", "browser/clipboard-read", "browser/clipboard-write", "browser/geolocation-current"],
  server: ["persist", "fetch", "fetch-stream", "call-service", "emit", "set", "spawn", "despawn", "log", "ref", "deref", "swap!", "atomic", "os/watch-files", "os/watch-process", "os/watch-port", "os/watch-http", "os/watch-cron", "os/watch-signal", "os/watch-env", "os/debounce"],
  test: [
    "render-ui",
    "render",
    "navigate",
    "navigate-back",
    "emit",
    "set",
    "persist",
    "fetch",
    "call-service",
    "spawn",
    "despawn",
    "log",
    "ref",
    "deref",
    "swap!",
    "watch",
    "atomic",
    "send-server",
    "browser/open-file-picker",
    "browser/clipboard-read",
    "browser/clipboard-write",
    "browser/geolocation-current"
  ],
  ssr: ["render-ui", "render", "fetch", "emit", "set", "log", "ref", "deref"]
};
var effectLog = createLogger("almadar:runtime:effects");
setNamespaceLevel("almadar:runtime:effects", "WARN");
var persistLog = createLogger("almadar:runtime:persist");
function failureMessage(err) {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "message" in err && typeof err.message === "string") return err.message;
  return String(err);
}
function parseEffect(effect) {
  if (!Array.isArray(effect) || effect.length === 0) {
    return null;
  }
  const [operator, ...args] = effect;
  if (typeof operator !== "string") {
    return null;
  }
  return { operator, args };
}
function resolveArgs(args, bindings, strictBindings, contextExtensions) {
  const ctx = createContextFromBindings(bindings, strictBindings, contextExtensions);
  return args.map((arg) => interpolateValue(arg, ctx));
}
function interpolateFilterTraitRefs(value, ctx) {
  if (typeof value === "string") {
    return value === "@entity" || value.startsWith("@entity.") ? interpolateValue(value, ctx) : value;
  }
  if (Array.isArray(value)) {
    if (value.length >= 2 && value[0] === "object/get" && value[1] === "@entity") {
      return value;
    }
    return value.map((v) => interpolateFilterTraitRefs(v, ctx));
  }
  return value;
}
var EffectExecutor = class _EffectExecutor {
  handlers;
  bindings;
  context;
  debug;
  strictBindings;
  contextExtensions;
  evaluator;
  deferRenderBindings;
  resolveIntrinsicFields;
  resolveEntityFields;
  constructor(options) {
    this.handlers = options.handlers;
    this.bindings = options.bindings;
    this.context = options.context;
    this.debug = options.debug ?? false;
    this.strictBindings = options.strictBindings ?? false;
    this.contextExtensions = options.contextExtensions;
    this.evaluator = options.evaluator;
    this.deferRenderBindings = options.deferRenderBindings ?? false;
    this.resolveIntrinsicFields = options.resolveIntrinsicFields;
    this.resolveEntityFields = options.resolveEntityFields;
  }
  /**
   * `@intrinsic` fields are NEVER a persisted column — strips them from
   * `data` before a `persist create`/`persist update` write, regardless of
   * what expression produced `data` (bare `@entity`, an explicit literal
   * that names one, an `object/merge` result, …). Deterministic on the
   * entity's declared schema, not on how the value was constructed. A
   * no-op when no schema was supplied (`resolveIntrinsicFields` absent,
   * e.g. a browser/offline-preview handler set) or the entity declares no
   * intrinsic fields.
   */
  stripFrameFields(entityType, data) {
    if (data === void 0 || !this.resolveIntrinsicFields) return data;
    const intrinsicFields = this.resolveIntrinsicFields(entityType);
    return intrinsicFields.length > 0 ? omitFrameFields(data, intrinsicFields) : data;
  }
  /**
   * Names of `data`'s missing REQUIRED columns for a `persist create` —
   * shared with the Rust kernel's definition: an entity field with
   * `required: true`, excluding the framework-stamped columns
   * (`RESERVED_FIELD_NAMES` — id/audit timestamps the store mints, the
   * same set `@almadar/core`'s mock synthesis and `MockPersistenceAdapter`
   * already exempt), any `@intrinsic` field, any field declaring a
   * `default` (the store fills it in), and any field carrying `mergedFrom`
   * (a rebind-merged field's `required` is the imported atom's own write
   * contract, never this host writer's). "Missing" is key-absent or
   * `undefined`/`null`/`''`. A no-op (`[]`) when no schema was supplied
   * (`resolveEntityFields` absent).
   */
  missingRequiredFields(entityType, data) {
    if (!this.resolveEntityFields) return [];
    const row = data ?? {};
    const missing = [];
    for (const field of this.resolveEntityFields(entityType)) {
      if (field.required !== true || !field.name) continue;
      if (RESERVED_FIELD_NAMES.has(field.name)) continue;
      if (field.intrinsic === true) continue;
      if (field.default !== void 0) continue;
      if (field.mergedFrom !== void 0) continue;
      const value = row[field.name];
      if (value === void 0 || value === null || value === "") {
        missing.push(field.name);
      }
    }
    return missing;
  }
  _evaluator;
  getEvaluator() {
    if (this.evaluator) return this.evaluator;
    if (!this._evaluator) {
      this._evaluator = new SExpressionEvaluator();
    }
    return this._evaluator;
  }
  // ==========================================================================
  // Handler Manifest Validation (RCG-03)
  // ==========================================================================
  /**
   * Validate that all effect types used in a schema have handlers registered.
   * Call this at runtime startup to catch missing handler setup immediately.
   *
   * @param usedEffectTypes - Effect operator names used in the loaded schemas
   * @param environment - Execution environment for context-aware error messages
   * @returns Array of missing handler errors (empty if all handlers are available)
   *
   * @example
   * ```ts
   * const missing = EffectExecutor.validateHandlers(
   *   ['persist', 'render-ui', 'fetch'],
   *   executor.getRegisteredHandlers(),
   *   'client'
   * );
   * if (missing.length > 0) {
   *   console.error('Missing handlers:', missing);
   * }
   * ```
   */
  static validateHandlers(usedEffectTypes, registeredHandlers, environment) {
    const errors = [];
    const expectedHandlers = environment ? HANDLER_MANIFEST[environment] : void 0;
    for (const effectType of usedEffectTypes) {
      if (!registeredHandlers.includes(effectType)) {
        let message = `Effect "${effectType}" is used in schema but no handler is registered.`;
        if (expectedHandlers && !expectedHandlers.includes(effectType)) {
          message += ` Effect "${effectType}" is not expected in "${environment}" environment.`;
        }
        errors.push(message);
      }
    }
    return errors;
  }
  /**
   * Get list of effect operators that have handlers registered.
   */
  getRegisteredHandlers() {
    const registered = [];
    const handlerMap = {
      "emit": this.handlers.emit,
      "persist": this.handlers.persist,
      "set": this.handlers.set,
      "call-service": this.handlers.callService,
      "fetch": this.handlers.fetch,
      "fetch-stream": this.handlers.fetchStream,
      "send-server": this.handlers.sendServer,
      "spawn": this.handlers.spawn,
      "despawn": this.handlers.despawn,
      "render-ui": this.handlers.renderUI,
      "render": this.handlers.renderUI,
      "navigate": this.handlers.navigate,
      "navigate-back": this.handlers.navigateBack,
      "log": this.handlers.log,
      "ref": this.handlers.ref,
      "deref": this.handlers.deref,
      "swap!": this.handlers.swap,
      "watch": this.handlers.watch,
      "atomic": this.handlers.atomic,
      "behavior/compose": this.handlers.composeBehaviors,
      "behavior/wire": this.handlers.applyEventWiring,
      "behavior/detect-layout": this.handlers.detectLayoutStrategy,
      "behavior/pipe": this.handlers.pipeBehaviors
    };
    for (const [name, handler] of Object.entries(handlerMap)) {
      if (handler) {
        registered.push(name);
      }
    }
    registered.push("do", "when", "let", "if");
    return registered;
  }
  /**
   * Resolve one effect's raw args before dispatch — shared by `execute()`
   * (and `executeAll()`, which loops it) and `executeWithResults()`
   * (RCG-04). Both must apply the SAME special-casing: compound operators
   * carry nested effects, not values to resolve; `set` with an
   * `@entity.<field>` path literal uses that first arg as a binding PATH;
   * fetch-like ops preserve their `filter` SExpression instead of
   * collapsing it against the outer trait's entity; render-ui on the
   * deferring path keeps `@entity`-dependent leaves as render-time
   * markers. `executeWithResults()` used to carry its own independent,
   * incomplete copy of this list (missing all three special cases) —
   * verified live: it silently corrupted `(set @entity.<field> value)`
   * (arg[0] resolved away to the field's CURRENT value before
   * `dispatch('set', ...)` could parse the path out of it) while still
   * reporting `status: 'executed'`. One shared method converges them so a
   * future special-case added here can't be missed on the other path.
   */
  async resolveEffectArgs(operator, args) {
    const isCompound = operator === "do" || operator === "when" || operator === "let" || operator === "if";
    const isSetPathForm = operator === "set" && args.length >= 2 && typeof args[0] === "string" && args[0].startsWith("@entity.");
    const isFetchLike = (operator === "fetch" || operator === "ref" || operator === "deref" || operator === "os/watch-collection" || operator === "os/watch") && args.length >= 2 && args[1] !== null && typeof args[1] === "object" && !Array.isArray(args[1]);
    const isDeferringRenderUI = (operator === "render-ui" || operator === "render") && this.deferRenderBindings;
    if (isCompound) {
      return args;
    }
    if (isDeferringRenderUI) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      return args.map(
        (arg, index) => index === 0 ? interpolateValue(arg, ctx) : deferEntityBindings(arg, ctx)
      );
    }
    if (isSetPathForm) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      const resolvedRest = [];
      for (const a of args.slice(1)) {
        if (Array.isArray(a) && a.length > 0 && typeof a[0] === "string") {
          let result = this.getEvaluator().evaluate(a, ctx);
          if (result instanceof Promise) {
            result = await result;
          }
          resolvedRest.push(result);
        } else {
          resolvedRest.push(interpolateValue(a, ctx));
        }
      }
      return [args[0], ...resolvedRest];
    }
    if (isFetchLike) {
      const ctx = createContextFromBindings(this.bindings, this.strictBindings, this.contextExtensions);
      const opts = args[1];
      const resolvedOpts = {
        ...opts.id !== void 0 && { id: interpolateValue(opts.id, ctx) },
        ...opts.filter !== void 0 && { filter: interpolateFilterTraitRefs(opts.filter, ctx) },
        ...opts.limit !== void 0 && { limit: interpolateValue(opts.limit, ctx) },
        ...opts.offset !== void 0 && { offset: interpolateValue(opts.offset, ctx) },
        ...opts.include !== void 0 && { include: interpolateValue(opts.include, ctx) },
        ...opts.emit !== void 0 && { emit: interpolateValue(opts.emit, ctx) }
      };
      return [
        interpolateValue(args[0], ctx),
        resolvedOpts,
        ...args.slice(2).map((a) => interpolateValue(a, ctx))
      ];
    }
    return resolveArgs(args, this.bindings, this.strictBindings, this.contextExtensions);
  }
  /**
   * Execute a single effect.
   */
  async execute(effect) {
    const parsed = parseEffect(effect);
    if (!parsed) {
      if (this.debug) {
        effectLog.warn("invalid-effect-format", () => ({ effectJson: JSON.stringify(effect ?? null) }));
      }
      return;
    }
    const { operator, args } = parsed;
    const resolvedArgs = await this.resolveEffectArgs(operator, args);
    effectLog.debug("execute", { operator, argCount: resolvedArgs.length, context: this.context.traitName });
    if (this.debug) {
      effectLog.debug("executing", () => ({ operator, argsJson: JSON.stringify(resolvedArgs) }));
    }
    try {
      await this.dispatch(operator, resolvedArgs);
      effectLog.debug("execute:result", { operator, success: true });
    } catch (error) {
      effectLog.error("execute:error", {
        operator,
        error: error instanceof Error ? error : String(error)
      });
      throw error;
    }
  }
  /**
   * Execute multiple effects in sequence.
   */
  async executeAll(effects) {
    for (const effect of effects) {
      await this.execute(effect);
    }
  }
  /**
   * Execute multiple effects in parallel.
   */
  async executeParallel(effects) {
    await Promise.all(effects.map((effect) => this.execute(effect)));
  }
  // ==========================================================================
  // Effect Execution with Results (RCG-04)
  // ==========================================================================
  /**
   * Execute effects and return detailed results for each.
   * Enables compensating transitions by reporting which effects failed.
   *
   * Unlike `executeAll`, this method does NOT throw on effect errors.
   * Instead, it captures errors in the returned `EffectResult[]` array.
   */
  async executeWithResults(effects) {
    const results = [];
    for (const effect of effects) {
      const parsed = parseEffect(effect);
      if (!parsed) {
        results.push({
          type: "unknown",
          args: [],
          status: "skipped",
          error: "Invalid effect format"
        });
        continue;
      }
      const start = Date.now();
      const { operator, args: rawArgs } = parsed;
      const resolvedArgs = await this.resolveEffectArgs(operator, rawArgs);
      try {
        const outcome = await this.dispatch(operator, resolvedArgs);
        if (outcome?.failed) {
          results.push({
            type: operator,
            args: resolvedArgs,
            status: "failed",
            error: outcome.error,
            durationMs: Date.now() - start
          });
        } else {
          results.push({
            type: operator,
            args: resolvedArgs,
            status: "executed",
            durationMs: Date.now() - start
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        results.push({
          type: operator,
          args: resolvedArgs,
          status: "failed",
          error: errorMessage,
          durationMs: Date.now() - start
        });
      }
    }
    return results;
  }
  // ==========================================================================
  // `emit:` config extraction — close-the-circuit on async/reactive ops.
  //
  // `fetch`, `persist`, `call-service`, `set`, `ref`, `os/watch-*` may carry
  // an `emit:` key in their options object. After the effect's work finishes,
  // the runtime fires the author-configured bus event so downstream state
  // machines can branch on success/failure without stitching async/sequence.
  //
  // See `docs/Almadar_Std_Gaps.md` §3.1 and the emit-config plan for the
  // semantics. The compiled-path shell does the same work in generated JS.
  // ==========================================================================
  extractEmitConfig(rawOpt) {
    if (!rawOpt || typeof rawOpt !== "object" || Array.isArray(rawOpt)) {
      return void 0;
    }
    const obj = rawOpt;
    const emitBlock = obj.emit;
    if (!emitBlock || typeof emitBlock !== "object" || Array.isArray(emitBlock)) {
      return void 0;
    }
    const block = emitBlock;
    const asStr = (v) => typeof v === "string" ? v : void 0;
    return {
      success: asStr(block.success),
      failure: asStr(block.failure),
      on_change: asStr(block.on_change) ?? asStr(block.onChange),
      on_message: asStr(block.on_message) ?? asStr(block.onMessage)
    };
  }
  /** Build the source metadata stamp for an emit fired from this trait. */
  sourceStamp() {
    return {
      orbital: this.context.orbitalName,
      trait: this.context.traitName,
      transition: this.context.transition
    };
  }
  emitSuccess(emit, key, payload, fromPersistSuccess) {
    const eventName = emit?.[key];
    if (eventName) {
      this.handlers.emit(eventName, payload, this.sourceStamp(), fromPersistSuccess);
    }
  }
  emitFailure(emit, err, extra) {
    if (!emit?.failure) return;
    this.handlers.emit(emit.failure, { ...extra, error: failureMessage(err) }, this.sourceStamp());
  }
  /**
   * Narrow the generic emit config into the `os/watch-*` handler surface
   * (only `on_message` + `failure` are meaningful for streaming ops).
   * Returns `undefined` when neither field is set so handlers can test
   * with a single `if (emit)` guard.
   */
  osEmit(emit) {
    if (!emit || !emit.on_message && !emit.failure) return void 0;
    return {
      on_message: emit.on_message,
      failure: emit.failure
    };
  }
  /**
   * Run a substrate (async) effect and dispatch the author's `emit.success`
   * / `emit.failure` bus event when configured. Mirrors the compiled-path
   * generic substrate dispatch (server.rs): uniform `{ result }` payload on
   * success, `{ error }` on failure.
   *
   * When no `emit` config is present (fire-and-forget call) the op still runs
   * but no event is dispatched and errors propagate (legacy behavior).
   */
  async runSubstrate(invoke, emitCfg) {
    if (!emitCfg) {
      await invoke();
      return;
    }
    try {
      const result = await invoke();
      this.emitSuccess(emitCfg, "success", { result: result ?? null });
    } catch (err) {
      this.emitFailure(emitCfg, err);
    }
  }
  /**
   * Separate a trailing `{ emit: {...} }` options object from positional
   * substrate-op args. Returns `[positionalArgs, emitConfig]`.
   */
  splitSubstrateEmit(args) {
    const emitCfg = args.length > 0 ? this.extractEmitConfig(args[args.length - 1]) : void 0;
    return emitCfg ? [args.slice(0, -1), emitCfg] : [args, void 0];
  }
  // ==========================================================================
  // Effect Dispatch
  // ==========================================================================
  /**
   * Dispatches one effect. Returns `void` for the ordinary case (the
   * caller only cares whether it threw). A `persist` that resolves
   * without throwing — the store DENIED the write, signalled by return
   * value rather than an exception, per the "close the circuit" doctrine
   * — reports that outcome here so `executeWithResults` can record it as
   * `status: 'failed'` instead of the default `'executed'`.
   */
  async dispatch(operator, args) {
    switch (operator) {
      // === Universal Effects ===
      case "emit": {
        const event = args[0];
        const payload = args[1];
        this.handlers.emit(event, payload, this.sourceStamp());
        break;
      }
      case "set": {
        const entity = this.bindings.entity;
        let entityId;
        let field;
        let value;
        let emitCfg;
        if (typeof args[0] === "string" && args[0].startsWith("@entity.")) {
          const path = args[0];
          field = path.slice("@entity.".length);
          value = args[1];
          emitCfg = this.extractEmitConfig(args[2]);
          entityId = typeof entity?.["id"] === "string" ? entity["id"] : void 0;
          if (!entityId) {
            const payload = this.bindings.payload;
            const payloadId = payload && typeof payload === "object" && "id" in payload ? payload.id : void 0;
            if (typeof payloadId === "string") {
              entityId = payloadId;
              if (!entity) {
                this.bindings.entity = { id: payloadId };
              } else {
                entity["id"] = payloadId;
              }
              effectLog.debug("set:auto-seed-entity-id", { path, id: payloadId });
            } else {
              effectLog.debug("set:in-memory-mirror-only", { path });
              if (!entity) {
                this.bindings.entity = {};
              }
              this.bindings.entity[field] = value;
              await this.handlers.set(entityId ?? "", field, value);
              this.emitSuccess(emitCfg, "success", value);
              break;
            }
          }
        } else {
          entityId = args[0];
          field = args[1];
          value = args[2];
          emitCfg = this.extractEmitConfig(args[3]);
        }
        await this.handlers.set(entityId, field, value);
        if (entity && (entity["id"] === entityId || !entity["id"])) {
          entity[field] = value;
        }
        this.emitSuccess(emitCfg, "success", value);
        break;
      }
      case "persist": {
        const action = args[0];
        const last = args[args.length - 1];
        const emitCfg = last && typeof last === "object" && !Array.isArray(last) && "emit" in last ? this.extractEmitConfig(last) : void 0;
        persistLog.debug("persist:dispatch", {
          action,
          argCount: args.length,
          argTypes: args.map((a) => Array.isArray(a) ? "array" : a === null ? "null" : typeof a).join(","),
          traitName: this.context.traitName,
          transition: this.context.transition
        });
        persistLog.debug("persist:emit-config", {
          action,
          hasEmitCfg: emitCfg !== void 0,
          success: emitCfg?.success,
          failure: emitCfg?.failure
        });
        if (this.handlers.persistDelegated === true) {
          persistLog.debug("persist:delegated", {
            action,
            entityType: action === "batch" ? "batch" : args[1],
            traitName: this.context.traitName,
            transition: this.context.transition
          });
          return;
        }
        try {
          if (action === "batch") {
            const requiredMissing = [];
            const rawOps = Array.isArray(args[1]) ? args[1] : [];
            const operations = rawOps.map((op) => {
              if (!isPersistBatchOperation(op)) return op;
              if (op[0] === "create") {
                const stripped = this.stripFrameFields(op[1], op[2]);
                const missing = this.missingRequiredFields(op[1], stripped);
                if (missing.length > 0) requiredMissing.push({ entityType: op[1], missing });
                return ["create", op[1], stripped];
              }
              if (op[0] === "update") {
                return ["update", op[1], op[2], this.stripFrameFields(op[1], op[3])];
              }
              return op;
            });
            if (requiredMissing.length > 0) {
              persistLog.error("persist:required-missing", { action, entityType: "batch", requiredMissing });
              const missingError = requiredMissing.map(({ entityType: t, missing }) => `persist create ${t}: required field(s) ${missing.join(", ")} missing`).join("; ");
              this.emitFailure(emitCfg, new Error(missingError));
              return { failed: true, error: missingError };
            }
            const batchSummary = await this.handlers.persist("batch", "", {
              operations
            });
            persistLog.debug("persist:success", {
              action,
              entityType: "batch",
              opCount: operations.length,
              willEmit: emitCfg?.success
            });
            this.emitSuccess(
              emitCfg,
              "success",
              batchSummary ?? { totalCount: operations.length },
              true
            );
            persistLog.debug("persist:emit-fired", { action, eventName: emitCfg?.success });
          } else {
            const entityType = args[1];
            const data = action === "create" || action === "update" ? this.stripFrameFields(entityType, args[2]) : args[2];
            if (action === "create") {
              const missing = this.missingRequiredFields(entityType, data);
              if (missing.length > 0) {
                persistLog.error("persist:required-missing", { entityType, missing });
                const missingError = `persist create ${entityType}: required field(s) ${missing.join(", ")} missing`;
                this.emitFailure(emitCfg, new Error(missingError), { entityType });
                return { failed: true, error: missingError };
              }
            }
            const persisted = await this.handlers.persist(action, entityType, data);
            if (persisted === void 0) {
              const attemptedId = typeof data === "string" ? data : data && typeof data === "object" ? data.id : void 0;
              persistLog.error("persist:denied", { action, entityType, attemptedId });
              const deniedError = `persist ${action} ${entityType} was denied or failed`;
              this.emitFailure(
                emitCfg,
                new Error(deniedError),
                { entityType, id: attemptedId }
              );
              return { failed: true, error: deniedError };
            } else {
              const dataId = typeof persisted === "string" ? persisted : persisted.id;
              persistLog.debug("persist:success", {
                action,
                entityType,
                dataId,
                willEmit: emitCfg?.success
              });
              if (action === "create" && this.bindings.entity && typeof dataId === "string") {
                const boundId = this.bindings.entity.id;
                const submittedId = data && typeof data === "object" ? data.id : void 0;
                if (boundId === void 0 || boundId === submittedId) {
                  this.bindings.entity.id = dataId;
                  persistLog.debug("persist:create-bound-id", {
                    entityType,
                    id: dataId
                  });
                }
              }
              this.emitSuccess(emitCfg, "success", persisted, true);
              persistLog.debug("persist:emit-fired", { action, eventName: emitCfg?.success });
            }
          }
        } catch (err) {
          persistLog.error("persist:error", {
            action,
            entityType: action === "batch" ? "batch" : args[1],
            error: err instanceof Error ? err.message : String(err)
          });
          this.emitFailure(emitCfg, err);
          throw err;
        }
        break;
      }
      case "call-service": {
        const service = args[0];
        const action = args[1];
        const params = args[2];
        const emitCfg = this.extractEmitConfig(args[3]);
        if (this.handlers.callServiceDelegated === true) {
          effectLog.debug("call-service:delegated", { service, action, traitName: this.context.traitName, transition: this.context.transition });
          break;
        }
        try {
          const result = await this.handlers.callService(service, action, params);
          this.emitSuccess(emitCfg, "success", result);
        } catch (err) {
          if (!emitCfg?.failure) throw err;
          this.emitFailure(emitCfg, err);
          return { failed: true, error: failureMessage(err) };
        }
        break;
      }
      case "fetch": {
        if (this.handlers.fetch) {
          const entityType = args[0];
          const rawOpt = args[1];
          const options = typeof rawOpt === "string" ? { id: rawOpt } : rawOpt;
          const emitCfg = this.extractEmitConfig(rawOpt);
          try {
            const result = await this.handlers.fetch(entityType, options);
            if (result === null) {
              if (options?.id !== void 0) {
                this.emitFailure(emitCfg, new Error(`${entityType} ${options.id} not found`));
              } else {
                this.emitSuccess(emitCfg, "success", { data: [], totalCount: 0 });
              }
            } else {
              const payload = { data: result.rows, totalCount: result.total };
              this.emitSuccess(emitCfg, "success", payload);
            }
          } catch (err) {
            this.emitFailure(emitCfg, err);
            throw err;
          }
        } else {
          this.logUnsupported("fetch");
        }
        break;
      }
      case "fetch-stream": {
        if (this.handlers.fetchStream) {
          const streamEntityType = args[0];
          const rawStreamOpt = args[1];
          const streamOptions = typeof rawStreamOpt === "object" && rawStreamOpt !== null ? rawStreamOpt : void 0;
          const streamEmitCfg = this.extractEmitConfig(rawStreamOpt);
          try {
            const result = await this.handlers.fetchStream(
              streamEntityType,
              streamOptions,
              (chunk) => {
                this.emitSuccess(streamEmitCfg, "on_message", { chunk });
              }
            );
            this.emitSuccess(streamEmitCfg, "success", { data: result });
          } catch (err) {
            this.emitFailure(streamEmitCfg, err);
            throw err;
          }
        } else {
          this.logUnsupported("fetch-stream");
        }
        break;
      }
      // === Resource Operators ===
      case "ref": {
        const refEntityType = args[0];
        const rawRefOpt = args[1];
        const refOptions = typeof rawRefOpt === "string" ? { id: rawRefOpt } : rawRefOpt;
        const refEmitCfg = this.extractEmitConfig(rawRefOpt);
        try {
          let result = null;
          if (this.handlers.ref) {
            result = await this.handlers.ref(refEntityType, refOptions);
          } else if (this.handlers.fetch) {
            result = await this.handlers.fetch(refEntityType, refOptions);
          } else {
            this.logUnsupported("ref");
          }
          const refPayload = result ? { data: result.rows, totalCount: result.total } : { data: null, totalCount: 0 };
          this.emitSuccess(refEmitCfg, "on_change", refPayload);
        } catch (err) {
          this.emitFailure(refEmitCfg, err);
          throw err;
        }
        break;
      }
      case "deref": {
        const derefEntityType = args[0];
        const rawDerefOpt = args[1];
        const derefOptions = typeof rawDerefOpt === "string" ? { id: rawDerefOpt } : rawDerefOpt;
        if (this.handlers.deref) {
          await this.handlers.deref(derefEntityType, derefOptions);
        } else if (this.handlers.fetch) {
          await this.handlers.fetch(derefEntityType, derefOptions);
        } else {
          this.logUnsupported("deref");
        }
        break;
      }
      case "swap!": {
        if (this.handlers.swap) {
          const swapEntityType = args[0];
          const swapEntityId = args[1];
          const swapTransform = args[2];
          await this.handlers.swap(swapEntityType, swapEntityId, swapTransform);
        } else {
          this.logUnsupported("swap!");
        }
        break;
      }
      case "watch": {
        if (this.handlers.watch) {
          const watchEntityType = args[0];
          const watchOptions = args[1];
          this.handlers.watch(watchEntityType, watchOptions);
        } else {
          if (this.debug) {
            effectLog.debug("watch:noop-server", { entityType: typeof args[0] === "string" ? args[0] : void 0 });
          }
        }
        break;
      }
      case "atomic": {
        if (this.handlers.atomic) {
          const atomicEffects = args;
          await this.handlers.atomic(atomicEffects);
        } else {
          const atomicEffects = args;
          for (const inner of atomicEffects) {
            await this.execute(inner);
          }
        }
        break;
      }
      case "send-server": {
        if (this.handlers.sendServer) {
          const sendEvent = args[0];
          const sendPayload = args[1];
          this.handlers.sendServer(sendEvent, sendPayload);
        } else {
          this.logUnsupported("send-server");
        }
        break;
      }
      case "spawn": {
        if (this.handlers.spawn) {
          const entityType = args[0];
          const props = args[1];
          this.handlers.spawn(entityType, props);
        } else {
          this.logUnsupported("spawn");
        }
        break;
      }
      case "despawn": {
        if (this.handlers.despawn) {
          const entityId = args[0];
          this.handlers.despawn(entityId);
        } else {
          this.logUnsupported("despawn");
        }
        break;
      }
      case "log": {
        if (this.handlers.log) {
          const message = args[0];
          const level = args[1];
          const data = args[2];
          this.handlers.log(message, level, data);
        } else {
          effectLog.debug("log:fallback", () => ({
            message: typeof args[0] === "string" ? args[0] : JSON.stringify(args[0] ?? null),
            extraJson: JSON.stringify(args.slice(1))
          }));
        }
        break;
      }
      // === Client-Only Effects ===
      case "render-ui":
      case "render": {
        if (this.handlers.renderUI) {
          const slot = args[0];
          const patternRaw = args[1];
          const pattern = patternRaw === null ? null : patternRaw;
          const props = args[2];
          const priority = args[3];
          await this.handlers.renderUI(slot, pattern, props, priority);
        } else {
          this.logUnsupported("render-ui");
        }
        break;
      }
      case "navigate": {
        if (this.handlers.navigate) {
          const path = args[0];
          if (typeof path !== "string" || path === "") {
            effectLog.warn("navigate-path-unresolved", { pathType: typeof path });
            break;
          }
          const params = args[1];
          const options = args[2];
          const crumb = options && typeof options.crumb === "string" && options.crumb !== "" ? options.crumb : void 0;
          this.handlers.navigate(path, params, crumb);
        } else {
          this.logUnsupported("navigate");
        }
        break;
      }
      case "navigate-back": {
        if (this.handlers.navigateBack) {
          this.handlers.navigateBack();
        } else {
          this.logUnsupported("navigate-back");
        }
        break;
      }
      case "notify": {
        this.logUnsupported("notify");
        break;
      }
      // === Compound Effects ===
      case "do": {
        const nestedEffects = args;
        for (const nested of nestedEffects) {
          await this.execute(nested);
        }
        break;
      }
      case "when": {
        const ctx = createContextFromBindings(this.bindings, false, this.contextExtensions);
        const condition = interpolateValue(args[0], ctx);
        const thenEffect = args[1];
        const elseEffect = args[2];
        if (condition) {
          await this.execute(thenEffect);
        } else if (elseEffect) {
          await this.execute(elseEffect);
        }
        break;
      }
      case "if": {
        const ctx = createContextFromBindings(this.bindings, false, this.contextExtensions);
        const cond = interpolateValue(args[0], ctx);
        const branch = cond ? args[1] : args[2];
        if (Array.isArray(branch)) {
          await this.execute(branch);
        }
        break;
      }
      case "let": {
        const rawBindings = args[0];
        const body = args[1];
        const locals = new Map(this.bindings.locals);
        const pairs = Array.isArray(rawBindings) ? rawBindings.flatMap(
          (b) => Array.isArray(b) && typeof b[0] === "string" ? [[b[0], b[1]]] : []
        ) : Object.entries(rawBindings);
        for (const [name, valueExpr] of pairs) {
          const ctx = createContextFromBindings(
            { ...this.bindings, locals },
            false,
            this.contextExtensions
          );
          locals.set(name, interpolateValue(valueExpr, ctx));
        }
        const bodyExecutor = new _EffectExecutor({
          handlers: this.handlers,
          bindings: { ...this.bindings, locals },
          context: this.context,
          debug: this.debug,
          strictBindings: this.strictBindings,
          contextExtensions: this.contextExtensions,
          deferRenderBindings: this.deferRenderBindings
        });
        await bodyExecutor.execute(body);
        break;
      }
      // OS trigger operators (server-side only)
      //
      // Each may carry `emit:` inside a trailing options object. The
      // executor extracts it and passes it to the handler so the
      // hardcoded fallback name (e.g. OS_CRON_FIRE) is replaced by the
      // author-configured event.
      case "os/watch-files": {
        if (this.handlers.osWatchFiles) {
          const glob = args[0];
          const rawOptions = args[1];
          const emitCfg = this.extractEmitConfig(rawOptions);
          const options = rawOptions ? { recursive: rawOptions.recursive, debounce: rawOptions.debounce } : {};
          this.handlers.osWatchFiles(glob, options, this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-files");
        }
        break;
      }
      case "os/watch-process": {
        if (this.handlers.osWatchProcess) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchProcess(
            args[0],
            args[1],
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-process");
        }
        break;
      }
      case "os/watch-port": {
        if (this.handlers.osWatchPort) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchPort(
            args[0],
            args[1] ?? "tcp",
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-port");
        }
        break;
      }
      case "os/watch-http": {
        if (this.handlers.osWatchHttp) {
          const emitCfg = this.extractEmitConfig(args[2]);
          this.handlers.osWatchHttp(
            args[0],
            args[1],
            this.osEmit(emitCfg)
          );
        } else {
          this.logUnsupported("os/watch-http");
        }
        break;
      }
      case "os/watch-cron": {
        if (this.handlers.osWatchCron) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchCron(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-cron");
        }
        break;
      }
      case "os/watch-signal": {
        if (this.handlers.osWatchSignal) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchSignal(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-signal");
        }
        break;
      }
      case "os/watch-env": {
        if (this.handlers.osWatchEnv) {
          const emitCfg = this.extractEmitConfig(args[1]);
          this.handlers.osWatchEnv(args[0], this.osEmit(emitCfg));
        } else {
          this.logUnsupported("os/watch-env");
        }
        break;
      }
      case "os/debounce": {
        if (this.handlers.osDebounce) {
          this.handlers.osDebounce(args[0], args[1]);
        } else {
          this.logUnsupported("os/debounce");
        }
        break;
      }
      // === Composition operators (compile-time, optional) ===
      case "behavior/compose": {
        if (this.handlers.composeBehaviors) {
          const config = args[0];
          await this.handlers.composeBehaviors(config);
        } else {
          this.logUnsupported("behavior/compose");
        }
        break;
      }
      case "behavior/wire": {
        if (this.handlers.applyEventWiring) {
          const wireOrbitals = args[0];
          const wireEntries = args[1];
          await this.handlers.applyEventWiring(wireOrbitals, wireEntries);
        } else {
          this.logUnsupported("behavior/wire");
        }
        break;
      }
      case "behavior/detect-layout": {
        if (this.handlers.detectLayoutStrategy) {
          const layoutOrbitals = args[0];
          const layoutWiring = args[1];
          await this.handlers.detectLayoutStrategy(layoutOrbitals, layoutWiring);
        } else {
          this.logUnsupported("behavior/detect-layout");
        }
        break;
      }
      case "behavior/pipe": {
        if (this.handlers.pipeBehaviors) {
          const [pipeSeed, ...pipeSteps] = args;
          await this.handlers.pipeBehaviors(
            pipeSeed,
            ...pipeSteps
          );
        } else {
          this.logUnsupported("behavior/pipe");
        }
        break;
      }
      // === Agent substrate operators (server-side, effect-position) ===
      //
      // Each honours a trailing `{ emit: { success, failure } }` options
      // object (mirrors fetch/persist/call-service): on success the
      // author's `emit.success` event fires with a uniform `{ result }`
      // payload so `?result` captures the return value; on failure the
      // `emit.failure` event fires with `{ error }`. Without an emit
      // config the call is fire-and-forget (legacy).
      case "compose/compose-all": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const config = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateComposeAll) {
            this.logUnsupported("compose/compose-all");
            return null;
          }
          return this.handlers.substrateComposeAll(config);
        }, emitCfg);
        break;
      }
      case "compose/compose-children": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const parentName = positional[0];
        const children = positional[1];
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateComposeChildren) {
            this.logUnsupported("compose/compose-children");
            return null;
          }
          return this.handlers.substrateComposeChildren(parentName, children);
        }, emitCfg);
        break;
      }
      case "behavior/instantiate": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const parentName = positional[0];
        const behavior = positional[1];
        const params = positional.length > 2 ? positional[2] : void 0;
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateInstantiate) {
            this.logUnsupported("behavior/instantiate");
            return null;
          }
          return this.handlers.substrateInstantiate(parentName, behavior, params);
        }, emitCfg);
        break;
      }
      case "behavior/call": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const behavior = positional[0];
        const method = positional[1];
        const params = positional.length > 2 ? positional[2] : void 0;
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateCall) {
            this.logUnsupported("behavior/call");
            return null;
          }
          return this.handlers.substrateCall(behavior, method, params);
        }, emitCfg);
        break;
      }
      case "validate/validate": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const orbitalName = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateValidate) {
            this.logUnsupported("validate/validate");
            return null;
          }
          return this.handlers.substrateValidate(orbitalName);
        }, emitCfg);
        break;
      }
      case "lolo/emit-body": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const orbitalName = positional[0];
        const loloSource = positional[1];
        await this.runSubstrate(async () => {
          if (!this.handlers.substrateEmitBody) {
            this.logUnsupported("lolo/emit-body");
            return null;
          }
          return this.handlers.substrateEmitBody(orbitalName, loloSource);
        }, emitCfg);
        break;
      }
      // === Browser device operators (client-side, user-gesture) ===
      // Async host APIs routed to dedicated handler methods. Uniform
      // `{ result }` / `{ error }` payload via runSubstrate. On the
      // server (or any host without the API) the handler is absent →
      // unsupported warning; a thrown error fires `emit.failure`.
      case "browser/open-file-picker": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const options = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserOpenFilePicker) {
            this.logUnsupported("browser/open-file-picker");
            return null;
          }
          return this.handlers.browserOpenFilePicker(options);
        }, emitCfg);
        break;
      }
      case "browser/clipboard-read": {
        const [, emitCfg] = this.splitSubstrateEmit(args);
        await this.runSubstrate(async () => {
          if (!this.handlers.browserClipboardRead) {
            this.logUnsupported("browser/clipboard-read");
            return null;
          }
          return this.handlers.browserClipboardRead();
        }, emitCfg);
        break;
      }
      case "browser/clipboard-write": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const text = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserClipboardWrite) {
            this.logUnsupported("browser/clipboard-write");
            return null;
          }
          return this.handlers.browserClipboardWrite(text);
        }, emitCfg);
        break;
      }
      case "browser/geolocation-current": {
        const [positional, emitCfg] = this.splitSubstrateEmit(args);
        const options = positional[0];
        await this.runSubstrate(async () => {
          if (!this.handlers.browserGeolocationCurrent) {
            this.logUnsupported("browser/geolocation-current");
            return null;
          }
          return this.handlers.browserGeolocationCurrent(options);
        }, emitCfg);
        break;
      }
      case "browser/push-subscribe": {
        const [, emitCfg] = this.splitSubstrateEmit(args);
        await this.runSubstrate(async () => {
          if (!this.handlers.browserPushSubscribe) {
            this.logUnsupported("browser/push-subscribe");
            return null;
          }
          return this.handlers.browserPushSubscribe();
        }, emitCfg);
        break;
      }
      default: {
        if (operator.includes("/")) {
          const [positional, emitCfg] = this.splitSubstrateEmit(args);
          await this.runSubstrate(async () => {
            const ctx = createContextFromBindings(
              this.bindings,
              this.strictBindings,
              this.contextExtensions
            );
            const result = this.getEvaluator().evaluate(
              [operator, ...positional],
              ctx
            );
            return result instanceof Promise ? await result : result;
          }, emitCfg);
        } else if (this.debug) {
          effectLog.warn("unknown-operator", { operator });
        }
      }
    }
  }
  logUnsupported(operator) {
    if (this.debug) {
      effectLog.warn("unsupported-on-platform", { operator });
    }
  }
};
function createTestExecutor(overrides = {}) {
  const noopAsync = async () => void 0;
  const noop = () => {
  };
  return new EffectExecutor({
    handlers: {
      emit: overrides.emit ?? noop,
      persist: overrides.persist ?? noopAsync,
      set: overrides.set ?? noop,
      callService: overrides.callService ?? (async () => ({})),
      ...overrides
    },
    bindings: {},
    context: {
      traitName: "TestTrait",
      state: "test",
      transition: "test->test"
    },
    debug: true
  });
}
function parseOrbitalTraits(orbital) {
  const configByTrait = /* @__PURE__ */ new Map();
  const unwrapped = (orbital.traits || []).map((t) => {
    if (t && typeof t === "object" && "ref" in t && "_resolved" in t) {
      const wrapper = t;
      const inner = wrapper._resolved;
      const normalizedConfig = normalizeCallSiteConfigToValues(wrapper.config);
      if (normalizedConfig && inner?.name) {
        configByTrait.set(inner.name, normalizedConfig);
      }
      return inner;
    }
    return t;
  });
  const inlineTraits = unwrapped.filter(isInlineTrait);
  const traits = inlineTraits.map((t) => {
    const sm = t.stateMachine;
    return {
      ...t.id !== void 0 ? { id: t.id } : {},
      name: t.name,
      states: sm?.states || [],
      transitions: sm?.transitions || [],
      listens: t.listens
    };
  });
  const entityRef = orbital.entity;
  let entity;
  if (typeof entityRef === "string") {
    entity = { name: entityRef, fields: [] };
  } else if (isEntityCall(entityRef)) {
    const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
    entity = {
      name: fallbackName,
      fields: entityRef.fields ?? [],
      ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
      ...entityRef.collection ? { collection: entityRef.collection } : {}
    };
  } else {
    entity = entityRef;
  }
  return { traits, inlineTraits, configByTrait, entity };
}
function findEntityAmongOrbitals(entities, name) {
  for (const entity of entities) {
    if (entity?.name === name) return entity;
  }
  return void 0;
}

// src/PayloadValidator.ts
function validateEventPayload(eventKey, payload, schema) {
  if (!schema || schema.length === 0) return [];
  const failures = [];
  for (const field of schema) {
    if (!field.required) continue;
    const value = payload?.[field.name];
    if (value === void 0) {
      failures.push({ event: eventKey, field: field.name, reason: "missing", expectedType: field.type });
      continue;
    }
    if (value === null) {
      failures.push({ event: eventKey, field: field.name, reason: "null", expectedType: field.type });
    }
  }
  return failures;
}
function formatPayloadValidationError(failures) {
  if (failures.length === 0) return "";
  const parts = failures.map(
    (f) => `${f.field} (${f.reason}${f.expectedType ? `, expected ${f.expectedType}` : ""})`
  );
  return `Payload validation failed for event '${failures[0].event}': ${parts.join("; ")}`;
}
function validatePayloadShapes(traits, emits) {
  const mismatches = [];
  const emitIndex = /* @__PURE__ */ new Map();
  for (const [traitName, declarations] of emits) {
    for (const decl of declarations) {
      const fields = decl.payloadSchema?.map((p) => p.name) ?? [];
      emitIndex.set(decl.event, { traitName, fields });
    }
  }
  for (const trait of traits) {
    if (!trait.listens) continue;
    for (const listener of trait.listens) {
      const emitter = emitIndex.get(listener.event);
      if (!emitter) continue;
      if (!listener.payloadMapping) continue;
      const payloadRefs = extractPayloadReferences(listener.payloadMapping);
      for (const ref of payloadRefs) {
        if (!emitter.fields.includes(ref)) {
          mismatches.push({
            listenerTrait: trait.name,
            emitterTrait: emitter.traitName,
            event: listener.event,
            referencedField: ref,
            availableFields: emitter.fields
          });
        }
      }
    }
  }
  return mismatches;
}
function extractPayloadReferences(mapping) {
  const refs = [];
  function collect(value) {
    if (typeof value === "string") {
      const match = value.match(/^@payload\.(\w+)$/);
      if (match) {
        refs.push(match[1]);
      }
    } else if (typeof value === "object" && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(collect);
      } else {
        Object.values(value).forEach(collect);
      }
    }
  }
  Object.values(mapping).forEach(collect);
  return [...new Set(refs)];
}
function buildEmitsFromTraits(traits, explicitEmits) {
  const result = new Map(explicitEmits ?? []);
  for (const trait of traits) {
    if (result.has(trait.name)) continue;
    const emitDecls = [];
    for (const transition of trait.transitions) {
      if (!transition.effects) continue;
      for (const effect of transition.effects) {
        if (!Array.isArray(effect)) continue;
        if (effect[0] === "emit" && typeof effect[1] === "string") {
          const event = effect[1];
          const payloadObj = effect[2];
          const payloadSchema = payloadObj ? Object.keys(payloadObj).map((name) => ({ name })) : void 0;
          if (!emitDecls.some((d) => d.event === event)) {
            emitDecls.push({ event, payloadSchema });
          }
        }
      }
    }
    if (emitDecls.length > 0) {
      result.set(trait.name, emitDecls);
    }
  }
  return result;
}
function collectDeclaredConfigDefaults(trait) {
  if (!trait) return void 0;
  const schema = trait.config;
  if (!schema || typeof schema !== "object") return void 0;
  const defaults = {};
  let hasAny = false;
  for (const [key, field] of Object.entries(schema)) {
    if (field && typeof field === "object" && !Array.isArray(field) && "default" in field) {
      const def = field.default;
      if (def !== void 0) {
        defaults[key] = def;
        hasAny = true;
      }
    }
  }
  return hasAny ? defaults : void 0;
}
function collectDeclaredEntityDefaults(entity) {
  if (!entity) return void 0;
  const defaults = {};
  let hasAny = false;
  for (const field of entity.fields) {
    if (field.name !== void 0 && "default" in field && field.default !== void 0) {
      defaults[field.name] = field.default;
      hasAny = true;
    }
  }
  return hasAny ? defaults : void 0;
}
var mockLog = createLogger("almadar:runtime:mock");
var DEFAULT_MOCK_SEED = 42;
var SEED_REFERENCE_TIMESTAMP = "2024-01-01T00:00:00.000Z";
var MS_PER_DAY = 864e5;
function isRelationField(field) {
  return field.type === "relation";
}
function stampedCellKey(entity, id, column) {
  return `${entity}\0${id}\0${column}`;
}
var MockPersistenceAdapter = class _MockPersistenceAdapter {
  stores = /* @__PURE__ */ new Map();
  schemas = /* @__PURE__ */ new Map();
  idCounters = /* @__PURE__ */ new Map();
  /** entityId -> store key, so relation lookups can prefer the id sibling over `relation.entity` name-matching. */
  storeNameById = /* @__PURE__ */ new Map();
  /** normalized entity name -> store key (the declared collection, else the name).
   *  Entities sharing a `persistent:` collection resolve to the same store. */
  storeKeyByEntity = /* @__PURE__ */ new Map();
  /** store key -> the first registrant's entity name, used as the minted-id label
   *  so rows in a shared collection carry one consistent id family. */
  idLabelByStoreKey = /* @__PURE__ */ new Map();
  config;
  /**
   * Every (entity, row id, column) cell `seed()` stamped with `config.ownerId`.
   * Seeding is eager and runs once at registration, before a dev host's
   * viewer is known to have changed — see `restampOwner`, which walks this
   * list to re-point the stamp when the default user changes later.
   */
  ownerStampedCells = [];
  constructor(config = {}) {
    this.config = {
      defaultSeedCount: 6,
      debug: false,
      ...config,
      // Apply default after spread so an undefined `seed` in the
      // input doesn't overwrite the default.
      seed: config.seed ?? DEFAULT_MOCK_SEED
    };
    seedRandom(this.config.seed);
    mockLog.debug("mock:adapter:init", { seed: this.config.seed });
  }
  /**
   * Add owner columns discovered after construction.
   *
   * The adapter is built before any schema is registered, so schema-DERIVED
   * owner columns (relation fields pointing at the `[identity]` entity) can
   * only arrive later.
   *
   * ⚠️ Seeding is EAGER — `registerEntity()` seeds immediately — so callers must
   * supply these columns BEFORE registering any orbital. Calling this afterwards
   * silently stamps nothing.
   */
  addOwnerFields(fields) {
    if (fields.length === 0) return;
    const merged = /* @__PURE__ */ new Set([...this.config.ownerFields ?? [], ...fields]);
    this.config.ownerFields = [...merged];
  }
  /**
   * Re-point every owner-stamped cell from the current `ownerId` to
   * `newOwnerId`, and remember `newOwnerId` for any future seed.
   *
   * Seeding is eager (`registerEntity()` seeds immediately, at construction
   * time), so the columns above are frozen to whichever id was the default
   * user THEN — a dev host switching viewers later (`setDefaultUser` /
   * `POST /persona`) left the stamp pointing at the old id forever, so an
   * ownership-scoped view for the NEW viewer stayed empty. This does not
   * re-seed (eager seeding is intentional, see the class doc); it only
   * re-labels the cells `seed()` already marked as viewer-owned.
   *
   * No-op when there is no new id, or it matches the current one — an
   * anonymous switch (`newOwnerId === undefined`) leaves existing stamps as
   * they are rather than clearing a non-nullable owner column.
   *
   * Both this re-labeling AND `seed()`'s original stamping consult the
   * `ownerGate` hook (see `setOwnerGate`) — a re-register reseeds identical
   * rows, so gating only the restamp path leaks the moment a client
   * connects and the deterministic reseed stamps with the updated ownerId.
   */
  restampOwner(newOwnerId) {
    const oldOwnerId = this.config.ownerId;
    this.config.ownerId = newOwnerId;
    if (!newOwnerId || newOwnerId === oldOwnerId) return;
    let skipped = 0;
    let fallbackStamps = 0;
    for (const cell of this.ownerStampedCells) {
      const row = this.stores.get(cell.entity)?.get(cell.id);
      if (!row) continue;
      if (this.ownerGate && !this.ownerGate(cell.entity, { ...row, [cell.column]: newOwnerId })) {
        const fallbackId = this.firstEligibleOwner(cell.entity, cell.column, row);
        if (fallbackId === void 0) {
          skipped++;
          continue;
        }
        row[cell.column] = fallbackId;
        fallbackStamps++;
        continue;
      }
      row[cell.column] = newOwnerId;
    }
    mockLog.debug("mock:owner-restamped", {
      from: oldOwnerId,
      to: newOwnerId,
      cells: this.ownerStampedCells.length,
      skipped,
      fallbackStamps
    });
  }
  /**
   * The host's answer to "may the current viewer OWN this row?" — evaluated
   * against the candidate row with the owner column already re-pointed,
   * `storeKey` being the collection key the stores are keyed by. The runtime
   * installs the registered schema's `@create` directive here (the single
   * authority on who authors; no role heuristics), so a read-only persona
   * never inherits authorship of drafts from the demo stamper — neither on a
   * persona switch (`restampOwner`) nor on a deterministic reseed (`seed`).
   * Absent hook = stamp unconditionally (pre-role-model behavior).
   */
  ownerGate;
  setOwnerGate(gate) {
    this.ownerGate = gate;
  }
  /**
   * The host's answer to "may THIS identity row own this candidate row?" —
   * evaluated per candidate rather than against the fixed default viewer, so
   * `linkRelationFields` and the eligible-owner fallback (see
   * `firstEligibleOwner`) can pick a policy-valid owner from the whole
   * identity roster instead of only ever checking the current default user.
   * Absent hook = no candidate filtering (pre-role-model behavior).
   */
  ownerCandidateGate;
  setOwnerCandidateGate(gate) {
    this.ownerCandidateGate = gate;
  }
  /**
   * The store an owner column's relation field targets, resolved the same
   * way `linkRelationFields` resolves any relation target (id sibling wins
   * over the `entity` name string) — but keyed by STORE, mirroring that
   * method's first-declarer-per-collection rule, so `seed()` and
   * `restampOwner()` can find it before a `linkRelationFields` pass has run
   * this registration. Undefined when no registered schema on `storeKey`
   * declares `column` as a relation field.
   */
  resolveOwnerColumnTargetStore(storeKey, column) {
    for (const [normalizedName, schema] of this.schemas) {
      if ((this.storeKeyByEntity.get(normalizedName) ?? normalizedName) !== storeKey) continue;
      const field = schema.fields.find((f) => f.name === column);
      if (field && isRelationField(field)) {
        return (field.relation.entityId && this.storeNameById.get(field.relation.entityId)) ?? this.resolveStoreKey(field.relation.entity);
      }
    }
    return void 0;
  }
  /**
   * The first identity-store row (seed order) whose persona is eligible, per
   * the installed `ownerCandidateGate`, to own `row`'s `column` in `storeKey`
   * — the deterministic replacement for "skip the stamp" when the CURRENT
   * viewer fails the `@create` policy (e.g. a reader-default persona against
   * an author|reviewer-only policy). Undefined when no gate is installed, the
   * column isn't a resolvable relation, or no identity row is eligible — the
   * caller keeps today's skip behavior in that case.
   */
  firstEligibleOwner(storeKey, column, row) {
    if (!this.ownerCandidateGate) return void 0;
    const identityStoreKey = this.resolveOwnerColumnTargetStore(storeKey, column);
    const identityStore = identityStoreKey ? this.stores.get(identityStoreKey) : void 0;
    if (!identityStore) return void 0;
    for (const identityRow of identityStore.values()) {
      const candidateId = identityRow["id"];
      if (this.ownerCandidateGate(storeKey, { ...row, [column]: candidateId }, identityRow)) {
        return candidateId;
      }
    }
    return void 0;
  }
  /** Re-anchor the PRNG to the configured seed. Called before every
   *  re-seed loop so identical reseed sequences produce identical rows
   *  (timestamps + generated fields). Without this, the first
   *  reseed produces row set A, the second produces row set B, and
   *  diff observers see all rows as "changed" between frames. */
  resetFakerSeed() {
    if (this.config.seed !== void 0) {
      seedRandom(this.config.seed);
    }
  }
  // ============================================================================
  // Store Management
  // ============================================================================
  /** Resolve an entity name to its store key: the declared collection when the
   *  entity is registered, the lowercased name otherwise (unregistered ad-hoc
   *  creates keep working). */
  resolveStoreKey(entityName) {
    const normalized = entityName.toLowerCase();
    return this.storeKeyByEntity.get(normalized) ?? normalized;
  }
  getStore(entityName) {
    const key = this.resolveStoreKey(entityName);
    if (!this.stores.has(key)) {
      this.stores.set(key, /* @__PURE__ */ new Map());
      this.idCounters.set(key, 0);
    }
    return this.stores.get(key);
  }
  nextId(entityName) {
    const key = this.resolveStoreKey(entityName);
    const counter = (this.idCounters.get(key) ?? 0) + 1;
    this.idCounters.set(key, counter);
    const label = this.idLabelByStoreKey.get(key) ?? entityName;
    return `${this.capitalizeFirst(label)} Id ${counter}`;
  }
  // ============================================================================
  // Schema & Seeding
  // ============================================================================
  /**
   * Register an entity schema and seed mock data.
   * If the schema has seedData, those instances are used directly.
   * Otherwise, random mock data is generated with the seeded PRNG.
   */
  registerEntity(schema, seedCount) {
    const normalized = schema.name.toLowerCase();
    const storeKey = (schema.collection ?? schema.name).toLowerCase();
    this.storeKeyByEntity.set(normalized, storeKey);
    if (!this.idLabelByStoreKey.has(storeKey)) {
      this.idLabelByStoreKey.set(storeKey, schema.name);
    }
    this.schemas.set(normalized, schema);
    if (schema.id) {
      this.storeNameById.set(schema.id, storeKey);
    }
    const alreadySeeded = (this.stores.get(storeKey)?.size ?? 0) > 0;
    if (schema.seedData && schema.seedData.length > 0) {
      this.seedFromInstances(schema.name, schema.seedData);
    } else if (alreadySeeded) {
      this.backfillFields(storeKey, schema);
    } else {
      const requested = seedCount ?? this.config.defaultSeedCount ?? 6;
      const count = sampleRowCount(
        { name: schema.name, persistence: schema.persistence, fields: schema.fields },
        requested
      );
      this.seed(schema.name, schema.fields, count, schema.persistence);
    }
    this.linkRelationFields();
  }
  /**
   * Walk every row of every registered entity and fill in `type: "relation"`
   * fields with real IDs from the target entity's store. Without this pass,
   * relation fields stay as placeholder `[]` / `""` and `populateRelations`
   * in OrbitalServerRuntime has nothing to hydrate — catalog/preview demos
   * of nested-tree atoms (e.g. std-thread-comments-linear with ThreadPost.
   * replies → [ThreadPost]) render empty reply cards.
   *
   * Cross-entity relations pick random IDs from the target store. EVERY
   * self-referential relation, any cardinality, is linked deterministically
   * instead via `@almadar/core/mock`'s `linkSelfRelationField`: row 0 stays a
   * root (never referenced), row *i* parents to row ⌊(i−1)/2⌋ — one shared
   * forest, real roots, no cycles. `one`/`many-to-one` self-relations (a
   * parent column like `Tag.parentId : Tag`) store the scalar parent id;
   * `many`/`one-to-many`/`many-to-many` self-relations (a children list like
   * `ChatMessage.replies : [ChatMessage]`) store the forest's children-id
   * list — no more random cross-linking, so the root row is never referenced
   * and stays deletable under `onDelete: restrict`. The runtime caps
   * recursion at depth=2 in `populateRelations`, so deep chains render two
   * levels then stop.
   *
   * Entities sharing a collection are linked once per STORE, over the union
   * of their declared relation fields (first declarer of a field name wins).
   */
  linkRelationFields() {
    const relationFieldsByStore = /* @__PURE__ */ new Map();
    for (const [normalizedName, schema] of this.schemas) {
      const storeKey = this.storeKeyByEntity.get(normalizedName) ?? normalizedName;
      let fieldMap = relationFieldsByStore.get(storeKey);
      if (!fieldMap) {
        fieldMap = /* @__PURE__ */ new Map();
        relationFieldsByStore.set(storeKey, fieldMap);
      }
      for (const f of schema.fields) {
        if (isRelationField(f) && !fieldMap.has(f.name)) {
          fieldMap.set(f.name, f);
        }
      }
    }
    const ownerColumnsByStore = /* @__PURE__ */ new Map();
    for (const { entity, field } of this.parsedOwnerFields()) {
      const storeKey = this.resolveStoreKey(entity);
      const set = ownerColumnsByStore.get(storeKey) ?? /* @__PURE__ */ new Set();
      set.add(field);
      ownerColumnsByStore.set(storeKey, set);
    }
    const stampedCellKeys = new Set(
      this.ownerStampedCells.map((cell) => stampedCellKey(cell.entity, cell.id, cell.column))
    );
    for (const [storeKey, fieldMap] of relationFieldsByStore) {
      const store = this.stores.get(storeKey);
      if (!store || fieldMap.size === 0) continue;
      const rows = [...store.values()];
      const ownerColumns = ownerColumnsByStore.get(storeKey);
      for (const field of fieldMap.values()) {
        const isOwnerColumn = ownerColumns?.has(field.name) ?? false;
        const targetKey = (field.relation.entityId && this.storeNameById.get(field.relation.entityId)) ?? this.resolveStoreKey(field.relation.entity);
        const targetStore = this.stores.get(targetKey);
        if (!targetStore || targetStore.size === 0) continue;
        const sameStore = targetStore === store;
        const cardinality = field.relation.cardinality ?? "many";
        if (sameStore) {
          if (isOwnerColumn) {
            for (const row of rows) {
              const selfId = row["id"];
              if (this.config.ownerId !== void 0 && row[field.name] === this.config.ownerId || stampedCellKeys.has(stampedCellKey(storeKey, selfId, field.name))) {
                continue;
              }
              row[field.name] = selfId;
            }
            continue;
          }
          linkSelfRelationField(
            rows,
            { name: field.name, cardinality },
            (row, i) => this.config.ownerId !== void 0 && row[field.name] === this.config.ownerId || stampedCellKeys.has(stampedCellKey(storeKey, row["id"], field.name))
          );
          continue;
        }
        for (const row of rows) {
          const selfId = row["id"];
          if (this.config.ownerId !== void 0 && row[field.name] === this.config.ownerId || stampedCellKeys.has(stampedCellKey(storeKey, selfId, field.name))) {
            continue;
          }
          let eligible = [...targetStore.keys()];
          if (isOwnerColumn && this.ownerCandidateGate) {
            const gate = this.ownerCandidateGate;
            eligible = eligible.filter(
              (id) => gate(storeKey, { ...row, [field.name]: id }, targetStore.get(id))
            );
          }
          if (eligible.length === 0) continue;
          if (cardinality === "one" || cardinality === "many-to-one") {
            row[field.name] = randomArrayElement(eligible);
          } else {
            const pickCount = Math.min(eligible.length, randomInt({ min: 2, max: 4 }));
            row[field.name] = shuffleArray(eligible.slice()).slice(0, pickCount);
          }
        }
      }
    }
  }
  /**
   * Fill fields a late-registering sibling schema declares that the shared
   * store's existing rows lack. The first registrant on a collection seeds
   * with ITS field list; a sibling declaring extra columns (e.g. `Tag.parentId`
   * arriving after `WikiTagRef {id,name}`) would otherwise read `undefined`
   * where its filters expect a value. Values come from the same canonical
   * sample policy as seeding; relation fields are then linked by the global
   * `linkRelationFields` pass that follows registration.
   */
  backfillFields(storeKey, schema) {
    const store = this.stores.get(storeKey);
    if (!store) return;
    const candidates = schema.fields.filter(
      (f) => f.name === void 0 || !RESERVED_FIELD_NAMES.has(f.name)
    );
    if (candidates.length === 0) return;
    let index = 0;
    for (const row of store.values()) {
      index += 1;
      const absent = candidates.filter((f) => row[f.name] === void 0);
      if (absent.length === 0) continue;
      const sample = sampleRow(
        { name: schema.name, persistence: schema.persistence, fields: absent },
        { index, strategy: "seeded", persistence: schema.persistence }
      );
      for (const f of absent) {
        if (sample[f.name] !== void 0) {
          row[f.name] = sample[f.name];
        }
      }
    }
    mockLog.debug("mock:backfill", {
      storeKey,
      entity: schema.name,
      fields: candidates.map((f) => f.name)
    });
  }
  /**
   * Seed an entity with pre-authored instance data.
   */
  seedFromInstances(entityName, instances) {
    const store = this.getStore(entityName);
    if (this.config.debug) {
      mockLog.debug("seeding-from-instances", { count: instances.length, entity: entityName });
    }
    for (const instance of instances) {
      const id = instance.id || this.nextId(entityName);
      const item = {
        ...instance,
        id,
        createdAt: instance.createdAt || SEED_REFERENCE_TIMESTAMP,
        updatedAt: SEED_REFERENCE_TIMESTAMP
      };
      store.set(id, item);
    }
  }
  /**
   * Seed an entity with mock data.
   */
  seed(entityName, fields, count, persistence) {
    const store = this.getStore(entityName);
    const normalized = entityName.toLowerCase();
    const storeKey = this.resolveStoreKey(entityName);
    if (this.config.debug) {
      mockLog.debug("seeding", { count, entity: entityName });
    }
    const ownerCols = this.parsedOwnerFields().filter(({ entity }) => entity.toLowerCase() === normalized).map(({ field }) => field);
    const ownerId = this.config.ownerId;
    const generated = [];
    let fallbackStamps = 0;
    for (let i = 0; i < count; i++) {
      const item = this.generateMockItem(entityName, fields, i + 1, persistence);
      if (ownerId && ownerCols.length > 0 && i % 2 === 0) {
        for (const col of ownerCols) {
          if (this.ownerGate && !this.ownerGate(storeKey, { ...item, [col]: ownerId })) {
            const fallbackId = this.firstEligibleOwner(storeKey, col, item);
            if (fallbackId === void 0) continue;
            item[col] = fallbackId;
            this.ownerStampedCells.push({ entity: storeKey, id: item.id, column: col });
            fallbackStamps++;
            continue;
          }
          item[col] = ownerId;
          this.ownerStampedCells.push({ entity: storeKey, id: item.id, column: col });
        }
      }
      store.set(item.id, item);
      generated.push({
        id: item.id,
        updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : ""
      });
    }
    mockLog.debug("mock:seed", () => ({
      entityName,
      count,
      idsAndTimestamps: JSON.stringify(generated),
      fallbackStamps
    }));
  }
  /** Parsed `config.ownerFields` pairs — every declared "Entity.field" owner
   *  column, split once so `seed()` and `linkRelationFields()` don't each
   *  re-parse the same strings. */
  parsedOwnerFields() {
    const out = [];
    for (const pair of this.config.ownerFields ?? []) {
      const [entity, field] = pair.split(".");
      if (entity && field) out.push({ entity, field });
    }
    return out;
  }
  /**
   * Generate a single mock item. Field values come from the canonical policy in
   * `@almadar/core/mock`; this method owns only the id and timestamp stamping.
   */
  generateMockItem(entityName, fields, index, persistence) {
    const id = this.nextId(entityName);
    const createdAt = randomPastDate({ years: 1 });
    const updatedAt = new Date(
      Math.min(createdAt.getTime() + randomInt({ min: 0, max: 30 }) * MS_PER_DAY, Date.now())
    );
    return {
      ...sampleRow(
        { name: entityName, persistence, fields },
        { index, strategy: "seeded", persistence }
      ),
      id,
      createdAt: createdAt.toISOString(),
      updatedAt: updatedAt.toISOString()
    };
  }
  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  // ============================================================================
  // PersistenceAdapter Implementation
  // ============================================================================
  async create(entityType, data) {
    const store = this.getStore(entityType);
    this.assertFileValuesWithinCeiling(entityType, data);
    const suppliedId = typeof data.id === "string" && data.id.length > 0 ? data.id : void 0;
    if (suppliedId && store.has(suppliedId)) {
      throw new Error(`Entity ${entityType} with id ${suppliedId} already exists`);
    }
    const id = suppliedId ?? this.nextId(entityType);
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const withDefaults = this.applyFieldDefaults(entityType, data);
    const item = {
      ...withDefaults,
      id,
      createdAt: now,
      updatedAt: now
    };
    store.set(id, item);
    return { id };
  }
  /**
   * Mock-mode file storage ceiling: a `file`-typed value whose `url` is a
   * `data:` URI keeps the whole payload in this in-memory/localStorage
   * store. Above the ceiling the write is REJECTED with a clear error —
   * never silently truncated or corrupted. Real deployments store bytes in
   * object storage and keep only the URL, so the ceiling is mock-only.
   */
  static FILE_DATA_URI_CEILING_BYTES = 2 * 1024 * 1024;
  assertFileValuesWithinCeiling(entityType, data) {
    for (const [fieldName, value] of Object.entries(data)) {
      if (value === null || typeof value !== "object" || Array.isArray(value)) continue;
      const candidate = value;
      if (typeof candidate.url !== "string" || !candidate.url.startsWith("data:")) continue;
      const bytes = typeof candidate.sizeBytes === "number" ? candidate.sizeBytes : candidate.url.length;
      if (bytes > _MockPersistenceAdapter.FILE_DATA_URI_CEILING_BYTES) {
        throw new Error(
          `${entityType}.${fieldName}: the playground mock store cannot persist files larger than ${_MockPersistenceAdapter.FILE_DATA_URI_CEILING_BYTES / (1024 * 1024)}MB (got ${(bytes / (1024 * 1024)).toFixed(1)}MB). Use a smaller file - production deployments store file bytes in object storage instead of the row.`
        );
      }
    }
  }
  /**
   * Fill in any entity-declared field defaults that the caller omitted.
   * SAVE payloads coming from form-section only carry the fields the user
   * edited; persisted rows should still honor `field.default` so downstream
   * row-content probes (VG11f) see a row whose every declared-default field
   * is non-empty. `@now` resolves to the current ISO timestamp.
   */
  applyFieldDefaults(entityType, data) {
    const schema = this.schemas.get(entityType.toLowerCase());
    if (!schema) return data;
    const result = { ...data };
    for (const field of schema.fields) {
      if (field.name !== void 0 && RESERVED_FIELD_NAMES.has(field.name)) continue;
      if (result[field.name] !== void 0) continue;
      if (field.default === void 0) continue;
      result[field.name] = field.default === "@now" ? (/* @__PURE__ */ new Date()).toISOString() : field.default;
    }
    return result;
  }
  async update(entityType, id, data) {
    const store = this.getStore(entityType);
    this.assertFileValuesWithinCeiling(entityType, data);
    const existing = store.get(id);
    if (!existing) {
      throw new Error(`Entity ${entityType} with id ${id} not found`);
    }
    const updated = {
      ...existing,
      ...data,
      id,
      // Preserve original ID
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    store.set(id, updated);
  }
  async delete(entityType, id) {
    const store = this.getStore(entityType);
    if (!store.has(id)) {
      throw new Error(`Entity ${entityType} with id ${id} not found`);
    }
    store.delete(id);
  }
  async getById(entityType, id) {
    const store = this.getStore(entityType);
    return store.get(id) ?? null;
  }
  async list(entityType) {
    const store = this.getStore(entityType);
    return Array.from(store.values());
  }
  // ============================================================================
  // Utilities
  // ============================================================================
  /**
   * Clear all data for an entity.
   */
  clear(entityName) {
    const key = this.resolveStoreKey(entityName);
    this.stores.delete(key);
    this.idCounters.delete(key);
  }
  /** Clear all data + re-anchor the PRNG so the next seed loop reproduces
   *  identical rows. Hermetic-frame mode calls this between every step
   *  via OrbitalServerRuntime.resetMockPersistence. */
  clearAll() {
    this.stores.clear();
    this.idCounters.clear();
    this.ownerStampedCells = [];
    this.storeKeyByEntity.clear();
    this.idLabelByStoreKey.clear();
    this.storeNameById.clear();
    this.resetFakerSeed();
    mockLog.debug("mock:adapter:clearAll", { reanchored: this.config.seed });
  }
  /**
   * Get count of items for an entity.
   */
  count(entityName) {
    const store = this.getStore(entityName);
    return store.size;
  }
};
function createMockPersistence(config) {
  return new MockPersistenceAdapter(config);
}

// src/loader/schema-loader.ts
function isElectron() {
  return typeof process !== "undefined" && !!process.versions?.electron;
}
function isBrowser() {
  return typeof window !== "undefined" && !isElectron();
}
function isNode() {
  return typeof process !== "undefined" && !isBrowser();
}
var HttpImportChain = class _HttpImportChain {
  chain = [];
  /**
   * Try to add a path to the chain.
   * @returns Error message if circular, null if OK
   */
  push(absolutePath) {
    if (this.chain.includes(absolutePath)) {
      const cycle = [
        ...this.chain.slice(this.chain.indexOf(absolutePath)),
        absolutePath
      ];
      return `Circular import detected: ${cycle.join(" -> ")}`;
    }
    this.chain.push(absolutePath);
    return null;
  }
  /**
   * Remove the last path from the chain.
   */
  pop() {
    this.chain.pop();
  }
  /**
   * Clone the chain for nested loading.
   */
  clone() {
    const newChain = new _HttpImportChain();
    newChain.chain = [...this.chain];
    return newChain;
  }
};
var HttpLoaderCache = class {
  cache = /* @__PURE__ */ new Map();
  get(url) {
    return this.cache.get(url);
  }
  set(url, schema) {
    this.cache.set(url, schema);
  }
  has(url) {
    return this.cache.has(url);
  }
  clear() {
    this.cache.clear();
  }
  get size() {
    return this.cache.size;
  }
};
var HttpLoader = class {
  options;
  cache;
  constructor(options) {
    this.options = {
      basePath: options.basePath,
      stdLibPath: options.stdLibPath ?? "",
      scopedPaths: options.scopedPaths ?? {},
      fetchOptions: options.fetchOptions,
      timeout: options.timeout ?? 3e4,
      credentials: options.credentials ?? "same-origin"
    };
    this.cache = new HttpLoaderCache();
  }
  /**
   * Load a schema from an import path.
   */
  async load(importPath, fromPath, chain) {
    const importChain = chain ?? new HttpImportChain();
    const resolveResult = this.resolvePath(importPath, fromPath);
    if (!resolveResult.success) {
      return resolveResult;
    }
    const absoluteUrl = resolveResult.data;
    const circularError = importChain.push(absoluteUrl);
    if (circularError) {
      return { success: false, error: circularError };
    }
    try {
      const cached = this.cache.get(absoluteUrl);
      if (cached) {
        return { success: true, data: cached };
      }
      const loadResult = await this.fetchSchema(absoluteUrl);
      if (!loadResult.success) {
        return loadResult;
      }
      const loaded = {
        schema: loadResult.data,
        sourcePath: absoluteUrl,
        importPath
      };
      this.cache.set(absoluteUrl, loaded);
      return { success: true, data: loaded };
    } finally {
      importChain.pop();
    }
  }
  /**
   * Load a specific orbital from a schema by name.
   */
  async loadOrbital(importPath, orbitalName, fromPath, chain) {
    const schemaResult = await this.load(importPath, fromPath, chain);
    if (!schemaResult.success) {
      return schemaResult;
    }
    const schema = schemaResult.data.schema;
    let orbital;
    if (orbitalName) {
      const found = schema.orbitals.find(
        (o) => o.name === orbitalName
      );
      if (!found) {
        return {
          success: false,
          error: `Orbital "${orbitalName}" not found in ${importPath}. Available: ${schema.orbitals.map((o) => o.name).join(", ")}`
        };
      }
      orbital = found;
    } else {
      if (schema.orbitals.length === 0) {
        return {
          success: false,
          error: `No orbitals found in ${importPath}`
        };
      }
      orbital = schema.orbitals[0];
    }
    return {
      success: true,
      data: {
        orbital,
        orbitals: schema.orbitals,
        ...schema.config ? { schemaConfig: schema.config } : {},
        sourcePath: schemaResult.data.sourcePath,
        importPath
      }
    };
  }
  /**
   * Resolve an import path to an absolute URL.
   */
  resolvePath(importPath, fromPath) {
    if (importPath.startsWith("http://") || importPath.startsWith("https://")) {
      return { success: true, data: importPath };
    }
    if (importPath.startsWith("std/")) {
      return this.resolveStdPath(importPath);
    }
    if (importPath.startsWith("@")) {
      return this.resolveScopedPath(importPath);
    }
    if (importPath.startsWith("./") || importPath.startsWith("../")) {
      return this.resolveRelativePath(importPath, fromPath);
    }
    return this.resolveRelativePath(`./${importPath}`, fromPath);
  }
  /**
   * Resolve a standard library path.
   */
  resolveStdPath(importPath) {
    if (!this.options.stdLibPath) {
      return {
        success: false,
        error: `Standard library URL not configured. Cannot load: ${importPath}`
      };
    }
    const relativePath = importPath.slice(4);
    let absoluteUrl = this.joinUrl(this.options.stdLibPath, relativePath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Resolve a scoped package path.
   */
  resolveScopedPath(importPath) {
    const match = importPath.match(/^(@[^/]+)/);
    if (!match) {
      return {
        success: false,
        error: `Invalid scoped package path: ${importPath}`
      };
    }
    const scope = match[1];
    const scopeRoot = this.options.scopedPaths[scope];
    if (!scopeRoot) {
      return {
        success: false,
        error: `Scoped package "${scope}" not configured. Available: ${Object.keys(this.options.scopedPaths).join(", ") || "none"}`
      };
    }
    const relativePath = importPath.slice(scope.length + 1);
    let absoluteUrl = this.joinUrl(scopeRoot, relativePath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Resolve a relative path.
   */
  resolveRelativePath(importPath, fromPath) {
    const baseUrl = fromPath ? this.getParentUrl(fromPath) : this.options.basePath;
    let absoluteUrl = this.joinUrl(baseUrl, importPath);
    if (!absoluteUrl.endsWith(".orb")) {
      absoluteUrl += ".orb";
    }
    return { success: true, data: absoluteUrl };
  }
  /**
   * Fetch and parse an OrbitalSchema from a URL.
   */
  async fetchSchema(url) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        this.options.timeout
      );
      try {
        const response = await fetch(url, {
          ...this.options.fetchOptions,
          credentials: this.options.credentials,
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            ...this.options.fetchOptions?.headers
          }
        });
        if (!response.ok) {
          return {
            success: false,
            error: `HTTP ${response.status}: ${response.statusText} for ${url}`
          };
        }
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          return {
            success: false,
            error: `Invalid JSON in ${url}: ${e instanceof Error ? e.message : String(e)}`
          };
        }
        const parseResult = OrbitalSchemaSchema.safeParse(data);
        if (!parseResult.success) {
          const errors = parseResult.error.errors.map((e) => `  - ${e.path.join(".")}: ${e.message}`).join("\n");
          return {
            success: false,
            error: `Invalid schema in ${url}:
${errors}`
          };
        }
        return { success: true, data: parseResult.data };
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") {
        return {
          success: false,
          error: `Request timeout for ${url} (${this.options.timeout}ms)`
        };
      }
      return {
        success: false,
        error: `Failed to fetch ${url}: ${e instanceof Error ? e.message : String(e)}`
      };
    }
  }
  /**
   * Join URL parts, handling relative paths and trailing slashes.
   */
  joinUrl(base, path) {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    if (typeof URL !== "undefined") {
      try {
        const baseUrl = base.endsWith("/") ? base : base + "/";
        return new URL(path, baseUrl).href;
      } catch {
      }
    }
    const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith("/") ? path : "/" + path;
    if (normalizedPath.startsWith("/./")) {
      return normalizedBase + normalizedPath.slice(2);
    }
    if (normalizedPath.startsWith("/../")) {
      const baseParts = normalizedBase.split("/");
      baseParts.pop();
      return baseParts.join("/") + normalizedPath.slice(3);
    }
    return normalizedBase + normalizedPath;
  }
  /**
   * Get parent URL (directory) from a URL.
   */
  getParentUrl(url) {
    if (typeof URL !== "undefined") {
      try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split("/");
        pathParts.pop();
        urlObj.pathname = pathParts.join("/");
        return urlObj.href;
      } catch {
      }
    }
    const lastSlash = url.lastIndexOf("/");
    if (lastSlash !== -1) {
      return url.slice(0, lastSlash);
    }
    return url;
  }
  /**
   * Clear the cache.
   */
  clearCache() {
    this.cache.clear();
  }
  /**
   * Get cache statistics.
   */
  getCacheStats() {
    return { size: this.cache.size };
  }
};

// src/loader/unified-loader.ts
var externalLoaderModule = null;
async function getExternalLoaderModule() {
  if (externalLoaderModule) {
    return externalLoaderModule;
  }
  if (isBrowser()) {
    return null;
  }
  try {
    externalLoaderModule = await import('./external-loader-CISUEINQ.js');
    return externalLoaderModule;
  } catch {
    return null;
  }
}
var UnifiedImportChain = class _UnifiedImportChain {
  chain = [];
  push(path) {
    const normalized = this.normalizePath(path);
    if (this.chain.includes(normalized)) {
      const cycle = [
        ...this.chain.slice(this.chain.indexOf(normalized)),
        normalized
      ];
      return `Circular import detected: ${cycle.join(" -> ")}`;
    }
    this.chain.push(normalized);
    return null;
  }
  pop() {
    this.chain.pop();
  }
  clone() {
    const newChain = new _UnifiedImportChain();
    newChain.chain = [...this.chain];
    return newChain;
  }
  normalizePath(path) {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return path.replace(/\\/g, "/");
  }
};
var UnifiedLoader = class {
  options;
  httpLoader = null;
  fsLoader = null;
  fsLoaderInitialized = false;
  cache = /* @__PURE__ */ new Map();
  constructor(options) {
    this.options = options;
    this.httpLoader = new HttpLoader({
      basePath: options.basePath,
      stdLibPath: options.stdLibPath,
      scopedPaths: options.scopedPaths,
      ...options.http
    });
  }
  /**
   * Initialize the filesystem loader if available.
   */
  async initFsLoader() {
    if (this.fsLoaderInitialized) {
      return;
    }
    this.fsLoaderInitialized = true;
    if (this.options.forceLoader === "http") {
      return;
    }
    const module = await getExternalLoaderModule();
    if (module) {
      this.fsLoader = new module.ExternalOrbitalLoader({
        basePath: this.options.basePath,
        stdLibPath: this.options.stdLibPath,
        scopedPaths: this.options.scopedPaths,
        ...this.options.fileSystem
      });
    }
  }
  /**
   * Determine which loader to use for an import path.
   */
  getLoaderForPath(importPath) {
    if (this.options.forceLoader) {
      return this.options.forceLoader;
    }
    if (importPath.startsWith("http://") || importPath.startsWith("https://")) {
      return "http";
    }
    if (importPath.startsWith("std/") && this.options.stdLibPath) {
      if (this.options.stdLibPath.startsWith("http://") || this.options.stdLibPath.startsWith("https://")) {
        return "http";
      }
    }
    if (importPath.startsWith("@") && this.options.scopedPaths) {
      const match = importPath.match(/^(@[^/]+)/);
      if (match) {
        const scopePath = this.options.scopedPaths[match[1]];
        if (scopePath && (scopePath.startsWith("http://") || scopePath.startsWith("https://"))) {
          return "http";
        }
      }
    }
    if (isBrowser()) {
      return "http";
    }
    return "filesystem";
  }
  /**
   * Load a schema from an import path.
   *
   * Note: We delegate chain management to the inner loader (HttpLoader or FsLoader).
   * The inner loader handles circular import detection, so we don't push/pop here.
   * We only use the unified cache to avoid duplicate loads across loaders.
   */
  async load(importPath, fromPath, chain) {
    await this.initFsLoader();
    const importChain = chain ?? new UnifiedImportChain();
    const loaderType = this.getLoaderForPath(importPath);
    const resolveResult = this.resolvePath(importPath, fromPath);
    if (!resolveResult.success) {
      return resolveResult;
    }
    const absolutePath = resolveResult.data;
    const cached = this.cache.get(absolutePath);
    if (cached) {
      return { success: true, data: cached };
    }
    let result;
    if (loaderType === "http") {
      if (!this.httpLoader) {
        return {
          success: false,
          error: "HTTP loader not available"
        };
      }
      result = await this.httpLoader.load(importPath, fromPath, importChain);
    } else {
      if (!this.fsLoader) {
        if (this.httpLoader) {
          result = await this.httpLoader.load(
            importPath,
            fromPath,
            importChain
          );
        } else {
          return {
            success: false,
            error: `Filesystem loader not available and import "${importPath}" cannot be loaded via HTTP. This typically happens when loading local files in a browser environment.`
          };
        }
      } else {
        result = await this.fsLoader.load(importPath, fromPath, importChain);
      }
    }
    if (result.success) {
      this.cache.set(absolutePath, result.data);
    }
    return result;
  }
  /**
   * Load a specific orbital from a schema by name.
   */
  async loadOrbital(importPath, orbitalName, fromPath, chain) {
    const schemaResult = await this.load(importPath, fromPath, chain);
    if (!schemaResult.success) {
      return schemaResult;
    }
    const schema = schemaResult.data.schema;
    if (orbitalName) {
      const found = schema.orbitals.find((o) => o.name === orbitalName);
      if (!found) {
        return {
          success: false,
          error: `Orbital "${orbitalName}" not found in ${importPath}. Available: ${schema.orbitals.map((o) => o.name).join(", ")}`
        };
      }
      return {
        success: true,
        data: {
          orbital: found,
          orbitals: schema.orbitals,
          ...schema.config ? { schemaConfig: schema.config } : {},
          sourcePath: schemaResult.data.sourcePath,
          importPath
        }
      };
    }
    if (schema.orbitals.length === 0) {
      return {
        success: false,
        error: `No orbitals found in ${importPath}`
      };
    }
    return {
      success: true,
      data: {
        orbital: schema.orbitals[0],
        orbitals: schema.orbitals,
        ...schema.config ? { schemaConfig: schema.config } : {},
        sourcePath: schemaResult.data.sourcePath,
        importPath
      }
    };
  }
  /**
   * Resolve an import path to an absolute path/URL.
   */
  resolvePath(importPath, fromPath) {
    const loaderType = this.getLoaderForPath(importPath);
    if (loaderType === "http" && this.httpLoader) {
      return this.httpLoader.resolvePath(importPath, fromPath);
    }
    if (this.fsLoader) {
      return this.fsLoader.resolvePath(importPath, fromPath);
    }
    if (this.httpLoader) {
      return this.httpLoader.resolvePath(importPath, fromPath);
    }
    return {
      success: false,
      error: "No loader available for path resolution"
    };
  }
  /**
   * Clear all caches.
   */
  clearCache() {
    this.cache.clear();
    this.httpLoader?.clearCache();
    this.fsLoader?.clearCache();
  }
  /**
   * Get combined cache statistics.
   */
  getCacheStats() {
    let size = this.cache.size;
    if (this.httpLoader) {
      size += this.httpLoader.getCacheStats().size;
    }
    if (this.fsLoader) {
      size += this.fsLoader.getCacheStats().size;
    }
    return { size };
  }
  /**
   * Check if filesystem loading is available.
   */
  async hasFilesystemAccess() {
    await this.initFsLoader();
    return this.fsLoader !== null;
  }
  /**
   * Get current environment info.
   */
  getEnvironmentInfo() {
    return {
      isElectron: isElectron(),
      isBrowser: isBrowser(),
      isNode: isNode(),
      hasFilesystem: this.fsLoader !== null
    };
  }
};
function createUnifiedLoader(options) {
  return new UnifiedLoader(options);
}
createLogger("almadar:runtime:studio-config");

// src/ui/splice-lambda-traits.ts
var TRAIT_BINDING_PREFIX = "@trait.";
var CONFIG_BINDING_PREFIX = "@config.";
var LambdaSpliceError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "LambdaSpliceError";
  }
  code;
};
function isRenderUiEffect(effect) {
  return Array.isArray(effect) && effect.length >= 1 && effect[0] === "render-ui";
}
function renderOnlyTemplate(trait) {
  if (trait.listens && trait.listens.length > 0) return null;
  if (trait.ticks && trait.ticks.length > 0) return null;
  const sm = trait.stateMachine;
  const states = sm?.states ?? [];
  if (states.length > 1) return null;
  let template = null;
  for (const transition of sm?.transitions ?? []) {
    const selfLoop = transition.from === transition.to;
    const guarded = transition.guard !== void 0 && transition.guard !== null;
    if (!selfLoop || guarded) return null;
    for (const effect of transition.effects ?? []) {
      if (!isRenderUiEffect(effect) || !Array.isArray(effect)) return null;
      const pattern = effect[2];
      if (pattern === void 0) return null;
      if (template !== null) return null;
      template = pattern;
    }
  }
  return template;
}
function resolveObjectPath(value, path) {
  let cur = value;
  for (const seg of path.split(".")) {
    if (cur === null || typeof cur !== "object") return void 0;
    cur = cur[seg];
    if (cur === void 0) return void 0;
  }
  return cur;
}
function substituteConfig(expr, subs) {
  if (typeof expr === "string") {
    if (!expr.startsWith(CONFIG_BINDING_PREFIX)) return expr;
    const rest = expr.slice(CONFIG_BINDING_PREFIX.length);
    const dot = rest.indexOf(".");
    const param = dot === -1 ? rest : rest.slice(0, dot);
    const trailing = dot === -1 ? void 0 : rest.slice(dot + 1);
    if (!(param in subs)) return expr;
    const value = subs[param];
    if (trailing === void 0) {
      if (typeof value === "string" && value === expr) return expr;
      return substituteConfig(value, subs);
    }
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const leaf = resolveObjectPath(value, trailing);
      if (leaf !== void 0) return substituteConfig(leaf, subs);
    }
    return expr;
  }
  if (Array.isArray(expr)) {
    return expr.map((item) => substituteConfig(item, subs));
  }
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const [k, v] of Object.entries(expr)) {
      out[k] = substituteConfig(v, subs);
    }
    return out;
  }
  return expr;
}
function buildTemplate(trait) {
  const raw = renderOnlyTemplate(trait);
  const emits = trait.emits ?? [];
  if (raw === null) return { template: null, emits, traitName: trait.name, id: trait.id, embedIds: trait.traitEmbedIds };
  const defaults = collectDeclaredConfigDefaults(trait);
  const template = defaults ? substituteConfig(raw, defaults) : raw;
  return { template, emits, traitName: trait.name, id: trait.id, embedIds: trait.traitEmbedIds };
}
function isLambdaForm(expr) {
  return expr.length === 3 && (expr[0] === "fn" || expr[0] === "lambda");
}
function spliceExpr(expr, inLambda, st) {
  if (typeof expr === "string") {
    if (!inLambda || !expr.startsWith(TRAIT_BINDING_PREFIX)) return expr;
    const name = expr.slice(TRAIT_BINDING_PREFIX.length);
    if (name.length === 0 || name.includes(".")) return expr;
    const embedId = st.embedIds?.[name];
    const entry = (embedId !== void 0 ? st.templatesById.get(embedId) : void 0) ?? st.templates.get(name);
    if (entry === void 0) return expr;
    if (entry.template === null) {
      throw new LambdaSpliceError(
        "LAMBDA_STATEFUL_TRAIT",
        `Trait "${name}" keeps its own state machine and cannot be expanded per list item \u2014 use the list pattern's item events (referenced inside a lambda in trait "${st.hostName}")`
      );
    }
    if (st.visiting.includes(name)) return expr;
    st.visiting.push(name);
    const prevEmbedIds = st.embedIds;
    st.embedIds = entry.embedIds;
    const expanded = spliceExpr(entry.template, true, st);
    st.embedIds = prevEmbedIds;
    st.visiting.pop();
    for (const emit of entry.emits) st.merged.push([entry.traitName, emit]);
    st.spliced.add(entry.traitName);
    st.changed = true;
    return expanded;
  }
  if (Array.isArray(expr)) {
    if (isLambdaForm(expr)) {
      return [expr[0], expr[1], spliceExpr(expr[2], true, st)];
    }
    return expr.map((item) => spliceExpr(item, inLambda, st));
  }
  if (expr !== null && typeof expr === "object") {
    const out = {};
    for (const [k, v] of Object.entries(expr)) {
      out[k] = spliceExpr(v, inLambda, st);
    }
    return out;
  }
  return expr;
}
function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value !== null && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      out[key] = canonical(value[key]);
    }
    return out;
  }
  return value;
}
function mergeWrapperEmits(host, incoming) {
  const emits = [...host.emits ?? []];
  for (const [wrapper, emit] of incoming) {
    const existing = emits.find((e) => e.event === emit.event);
    if (existing) {
      const same = JSON.stringify(canonical(existing)) === JSON.stringify(canonical(emit));
      if (!same) {
        throw new LambdaSpliceError(
          "EMIT_MERGE_CONFLICT",
          `Emit "${emit.event}" merged from spliced wrapper "${wrapper}" conflicts with an existing "${emit.event}" on host "${host.name}" (different payload shape)`
        );
      }
    } else {
      emits.push(emit);
    }
  }
  return emits;
}
function removeConsumedWrappers(traits, pages, spliced) {
  const idToName = /* @__PURE__ */ new Map();
  for (const { trait } of traits) {
    if (trait.id) idToName.set(trait.id, trait.name);
  }
  for (; ; ) {
    const stillReferenced = /* @__PURE__ */ new Set();
    for (const { trait } of traits) {
      const rawTokens = /* @__PURE__ */ new Set();
      for (const t of trait.stateMachine?.transitions ?? []) {
        if (t.guard !== void 0 && t.guard !== null) {
          collectTraitRefsFromValue(t.guard, rawTokens);
        }
        collectTraitRefsFromEffects(t.effects, rawTokens);
      }
      for (const tick of trait.ticks ?? []) {
        if (tick.guard !== void 0 && tick.guard !== null) {
          collectTraitRefsFromValue(tick.guard, rawTokens);
        }
        collectTraitRefsFromEffects(tick.effects, rawTokens);
      }
      if (trait.config) {
        for (const field of Object.values(trait.config)) {
          if (field && typeof field === "object" && "default" in field && field.default !== void 0) {
            collectTraitRefsFromValue(field.default, rawTokens);
          }
        }
      }
      for (const token of rawTokens) {
        const embedId = trait.traitEmbedIds?.[token];
        const resolvedName = (embedId !== void 0 ? idToName.get(embedId) : void 0) ?? token;
        stillReferenced.add(resolvedName);
      }
    }
    const removable = /* @__PURE__ */ new Set();
    for (const { trait } of traits) {
      if (spliced.has(trait.name) && !stillReferenced.has(trait.name)) {
        removable.add(trait.name);
      }
    }
    if (removable.size === 0) return;
    for (const { page } of pages) {
      const pageTraits = page.traits;
      if (!pageTraits) continue;
      const survivors2 = pageTraits.filter((pt) => !removable.has(pt.ref)).length;
      if (survivors2 === 0) {
        for (const pt of pageTraits) removable.delete(pt.ref);
      }
    }
    if (removable.size === 0) return;
    for (const resolvedPage of pages) {
      const pageTraits = resolvedPage.page.traits;
      if (pageTraits) {
        resolvedPage.page.traits = pageTraits.filter((pt) => !removable.has(pt.ref));
      }
    }
    const survivors = traits.filter(({ trait }) => !removable.has(trait.name));
    traits.length = 0;
    traits.push(...survivors);
  }
}
function spliceLambdaTraitRefs(traits, pages) {
  if (traits.length === 0) return;
  const templates = /* @__PURE__ */ new Map();
  const templatesById = /* @__PURE__ */ new Map();
  for (const { trait } of traits) {
    const entry = buildTemplate(trait);
    templates.set(trait.name, entry);
    if (trait.id) templatesById.set(trait.id, entry);
  }
  const spliced = /* @__PURE__ */ new Set();
  for (const resolved of traits) {
    const host = resolved.trait;
    const st = {
      templates,
      templatesById,
      embedIds: host.traitEmbedIds,
      // Seed with the host itself so a self-reference never self-splices.
      visiting: [host.name],
      merged: [],
      spliced,
      hostName: host.name,
      changed: false
    };
    const sm = host.stateMachine;
    const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
      ...t,
      effects: t.effects ? t.effects.map((e) => spliceExpr(e, false, st)) : t.effects
    })) : sm?.transitions;
    const nextTicks = host.ticks ? host.ticks.map((tick) => ({
      ...tick,
      effects: tick.effects.map((e) => spliceExpr(e, false, st))
    })) : host.ticks;
    if (!st.changed) {
      continue;
    }
    const nextEmits = st.merged.length > 0 ? mergeWrapperEmits(host, st.merged) : host.emits;
    resolved.trait = {
      ...host,
      ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
      ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
      ...nextEmits !== void 0 ? { emits: nextEmits } : {}
    };
  }
  if (spliced.size > 0) {
    removeConsumedWrappers(traits, pages, spliced);
  }
}

// src/resolver/sentinel-resolution.ts
var PRIMITIVE_TYPE_ARG_NAMES = /* @__PURE__ */ new Set([
  "string",
  "money",
  "any",
  "icon",
  "component",
  "email",
  "url",
  "phone",
  "uuid",
  "image",
  "node",
  "json",
  "render-ui",
  "relation",
  "int",
  "float",
  "number",
  "duration",
  "bool",
  "boolean",
  "date",
  "datetime",
  "timestamp",
  "object",
  "sexpr",
  "scalar"
]);
var BARE_PAYLOAD_CARRIER = "value";
function resolveTypeArgValue(value, entitiesByName, typesByName) {
  if (PRIMITIVE_TYPE_ARG_NAMES.has(value)) {
    return { kind: "scalar", primitive: value };
  }
  const entity = entitiesByName.get(value);
  if (entity) {
    return { kind: "entity", entity };
  }
  const shape = typesByName[value];
  if (shape) {
    return { kind: "struct", shape };
  }
  return void 0;
}
function entityFieldToPayloadField(f) {
  if (f.type === "object" && f.items) {
    const valueType = fieldTypeTagOf(f.items);
    return {
      name: f.name ?? "",
      type: `Map<string,${valueType}>`,
      ...f.required !== void 0 ? { required: f.required } : {}
    };
  }
  let properties;
  if (f.type === "object" && f.properties) {
    properties = Object.values(f.properties).map(entityFieldToPayloadField);
  } else if (f.type === "union" && f.properties) {
    const ordered = f.values ?? Object.keys(f.properties);
    properties = ordered.map((k) => f.properties?.[k]).filter((v) => v !== void 0).map(entityFieldToPayloadField);
  } else if (f.type === "array" && f.items) {
    properties = entityFieldToPayloadField(f.items).properties;
  }
  const isArrayOfStruct = f.type === "array" && properties !== void 0;
  return {
    name: f.name ?? "",
    type: isArrayOfStruct ? "[object]" : fieldTypeTagOf(f),
    ...f.required !== void 0 ? { required: f.required } : {},
    ...properties && properties.length > 0 ? { properties } : {}
  };
}
function fieldTypeTagOf(f) {
  return f.type;
}
function entityFieldsToPayloadFields(entity) {
  return entity.fields.map(entityFieldToPayloadField);
}
function flattenBareEntityPayload(fields, entityDef, subs) {
  if (fields.length !== 1) return false;
  const field = fields[0];
  if (field.name !== BARE_PAYLOAD_CARRIER) return false;
  let entity;
  if (field.type === "@entity") {
    entity = entityDef;
  } else if (field.type === "object" && field.properties) {
    entity = entityDef;
  } else if (field.type.startsWith("$")) {
    const sub = subs.get(field.type.slice(1));
    entity = sub?.kind === "entity" ? sub.entity : void 0;
  }
  if (!entity) return false;
  fields.length = 0;
  fields.push(...entityFieldsToPayloadFields(entity));
  return true;
}
function resolveSentinelFields(fields, entityDef, subs, declaredNames) {
  let changed = false;
  for (const field of fields) {
    const isSelfRef = field.type === "@entity" || field.type === "[@entity]";
    if (isSelfRef) {
      if (entityDef) {
        const isArray = field.type.startsWith("[");
        field.type = isArray ? "[object]" : "object";
        field.properties = entityFieldsToPayloadFields(entityDef);
        field.entity = entityDef.name;
        changed = true;
      }
      continue;
    }
    const isArraySentinel = field.type.startsWith("[$") && field.type.endsWith("]");
    const isBareSentinel = !isArraySentinel && field.type.startsWith("$");
    if (isBareSentinel || isArraySentinel) {
      const paramName = isArraySentinel ? field.type.slice(2, -1) : field.type.slice(1);
      const sub = subs.get(paramName);
      if (sub) {
        switch (sub.kind) {
          case "scalar":
            field.type = isArraySentinel ? `[${sub.primitive}]` : sub.primitive;
            field.properties = void 0;
            break;
          case "entity":
            field.type = isArraySentinel ? "[object]" : "object";
            field.properties = entityFieldsToPayloadFields(sub.entity);
            field.entity = sub.entity.name;
            break;
          case "struct":
            field.type = isArraySentinel ? "[object]" : "object";
            field.properties = [...sub.shape];
            break;
        }
        changed = true;
        continue;
      }
      if (declaredNames.has(paramName)) {
        continue;
      }
      if (entityDef) {
        field.type = isArraySentinel ? "[object]" : "object";
        field.properties = entityFieldsToPayloadFields(entityDef);
        field.entity = entityDef.name;
        changed = true;
      }
      continue;
    }
    if (field.properties) {
      const mutableProps = field.properties;
      changed = resolveSentinelFields(mutableProps, entityDef, subs, declaredNames) || changed;
    }
  }
  return changed;
}
function resolvePayloadEntitySchema(payloadEntity, setPayloadSchema, entitiesByName) {
  if (!payloadEntity) return false;
  const entity = entitiesByName.get(payloadEntity);
  if (!entity) return false;
  setPayloadSchema(entityFieldsToPayloadFields(entity));
  return true;
}
function resolveTraitTypeParamSentinels(trait, entityDef, entitiesByName, typesByName, typeArgs) {
  const declared = trait.typeParams ?? [];
  const subs = /* @__PURE__ */ new Map();
  const declaredNames = /* @__PURE__ */ new Set();
  for (const p of declared) {
    declaredNames.add(p.name);
    const argValue = typeArgs?.[p.name];
    let sub;
    if (argValue !== void 0) {
      sub = resolveTypeArgValue(argValue, entitiesByName, typesByName);
    } else if (p.kind === "Entity" && entityDef) {
      sub = { kind: "entity", entity: entityDef };
    } else if (p.default) {
      sub = resolveTypeArgValue(p.default, entitiesByName, typesByName);
    }
    if (sub) subs.set(p.name, sub);
  }
  let changed = false;
  for (const emit of trait.emits ?? []) {
    if (!emit.payloadSchema) continue;
    if (resolvePayloadEntitySchema(
      emit.payloadEntity,
      (schema2) => {
        emit.payloadSchema = schema2;
      },
      entitiesByName
    )) {
      changed = true;
      continue;
    }
    const schema = emit.payloadSchema;
    if (flattenBareEntityPayload(schema, entityDef, subs)) {
      changed = true;
    } else {
      changed = resolveSentinelFields(schema, entityDef, subs, declaredNames) || changed;
    }
  }
  for (const ev of trait.stateMachine?.events ?? []) {
    if (!ev.payloadSchema) continue;
    if (resolvePayloadEntitySchema(
      ev.payloadEntity,
      (schema2) => {
        ev.payloadSchema = schema2;
      },
      entitiesByName
    )) {
      changed = true;
      continue;
    }
    const schema = ev.payloadSchema;
    if (flattenBareEntityPayload(schema, entityDef, subs)) {
      changed = true;
    } else {
      changed = resolveSentinelFields(schema, entityDef, subs, declaredNames) || changed;
    }
  }
  return changed;
}
function resolveOrbitalTypeParamSentinels(traits, primaryEntity, auxiliaryEntities, orbitalTypes) {
  const entitiesByName = /* @__PURE__ */ new Map();
  entitiesByName.set(primaryEntity.name, primaryEntity);
  for (const e of auxiliaryEntities) entitiesByName.set(e.name, e);
  const typesByName = orbitalTypes ?? {};
  for (const rt of traits) {
    const entityDef = rt.trait.linkedEntity ? entitiesByName.get(rt.trait.linkedEntity) : void 0;
    resolveTraitTypeParamSentinels(rt.trait, entityDef, entitiesByName, typesByName, rt.typeArgs);
  }
}

// src/resolver/reference-resolver.ts
var refResolverLog = createLogger("almadar:runtime:ref-resolver");
function indexOrbitalNodes(orbital, idIndex) {
  for (const traitRef of orbital.traits ?? []) {
    if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
      const trait = traitRef;
      if (trait.id) {
        idIndex.set(trait.id, { kind: "trait", node: trait });
      }
      for (const ev of trait.stateMachine?.events ?? []) {
        if (ev.id) {
          idIndex.set(ev.id, { kind: "event", node: ev });
        }
      }
    }
  }
  const entityRef = orbital.entity;
  if (entityRef && typeof entityRef !== "string" && !("extends" in entityRef) && entityRef.id) {
    const entity = entityRef;
    idIndex.set(entity.id, { kind: "entity", node: entity });
  }
  for (const pageRef of orbital.pages ?? []) {
    if (typeof pageRef !== "string" && !("ref" in pageRef)) {
      const page = pageRef;
      if (page.id) {
        idIndex.set(page.id, { kind: "page", node: page });
      }
    }
  }
}
function buildIdIndex(orbital, orbitals) {
  const idIndex = /* @__PURE__ */ new Map();
  indexOrbitalNodes(orbital, idIndex);
  for (const imported of orbitals.values()) {
    for (const o of importedOrbitals(imported)) indexOrbitalNodes(o, idIndex);
  }
  return idIndex;
}
function importedOrbitals(imported) {
  return imported.orbitals?.length ? imported.orbitals : [imported.orbital];
}
function renameEventsInRenderUiConfig(node, rename) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    return node.map((item) => renameEventsInRenderUiConfig(item, rename));
  }
  if (typeof node !== "object") return node;
  const obj = node;
  const next = { ...obj };
  const rawType = obj.type;
  const patternType = typeof rawType === "string" ? rawType : void 0;
  for (const [key, value] of Object.entries(obj)) {
    if (key === "action" && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    if (/^on[A-Z]/.test(key) && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    if (key.endsWith("Event") && typeof value === "string" && !value.startsWith("@")) {
      next[key] = rename(value) ?? value;
      continue;
    }
    const eventField = patternType !== void 0 ? eventListPropsOf(patternType).get(key) : void 0;
    if (eventField !== void 0 && Array.isArray(value)) {
      const rewrittenArray = value.map((entry) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return entry;
        const action = entry;
        const current = action[eventField];
        if (typeof current === "string" && !current.startsWith("@")) {
          return { ...action, [eventField]: rename(current) ?? current };
        }
        return action;
      });
      next[key] = rewrittenArray;
      continue;
    }
    next[key] = renameEventsInRenderUiConfig(value, rename);
  }
  return next;
}
function renameEventTypedMembers(value, items, rename) {
  if (items === void 0 || value === null || value === void 0) return value;
  if (Array.isArray(value)) {
    return value.map((entry2) => renameEventTypedMembers(entry2, items, rename));
  }
  if (typeof value !== "object") return value;
  const properties = items.properties;
  if (properties === void 0) return value;
  const entry = value;
  const next = { ...entry };
  let touched = false;
  for (const [member, decl] of Object.entries(properties)) {
    const current = entry[member];
    if (decl.type === "event") {
      if (typeof current === "string" && current !== "" && !current.startsWith("@")) {
        const renamed = rename(current);
        if (renamed !== void 0 && renamed !== current) {
          next[member] = renamed;
          touched = true;
        }
      }
      continue;
    }
    if (decl.items !== void 0 && current !== void 0) {
      const nested = renameEventTypedMembers(current, decl.items, rename);
      if (nested !== current) {
        next[member] = nested;
        touched = true;
      }
    }
  }
  return touched ? next : value;
}
function renameEventsInDeclaredConfig(config, rename) {
  if (config === void 0) return config;
  let touched = false;
  const next = {};
  for (const [knob, field] of Object.entries(config)) {
    if (field.default === void 0) {
      next[knob] = field;
      continue;
    }
    const renamedDefault = field.type === "event" ? typeof field.default === "string" && field.default !== "" && !field.default.startsWith("@") ? rename(field.default) ?? field.default : field.default : renameEventTypedMembers(field.default, field.items, rename);
    if (renamedDefault !== field.default) {
      next[knob] = { ...field, default: renamedDefault };
      touched = true;
    } else {
      next[knob] = field;
    }
  }
  return touched ? next : config;
}
function renameEventsInEffects(effects, rename) {
  return effects.map((effect) => renameEventRefsInNode(effect, rename));
}
var EMIT_OPTION_SLOTS = /* @__PURE__ */ new Set(["success", "failure", "on_message", "on_change"]);
function renameEventRefsInNode(node, rename) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    if (node[0] === "render-ui" && node.length >= 3) {
      const nextConfig = renameEventsInRenderUiConfig(node[2], rename);
      return [
        node[0],
        node[1],
        nextConfig,
        ...node.slice(3).map((x) => renameEventRefsInNode(x, rename))
      ];
    }
    const out = node.map((x) => renameEventRefsInNode(x, rename));
    if (node[0] === "emit" && typeof node[1] === "string") {
      out[1] = rename(node[1]) ?? node[1];
    }
    return out;
  }
  if (typeof node === "object") {
    const obj = node;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === "emit") {
        if (typeof v === "string") {
          next[k] = rename(v) ?? v;
        } else if (v !== null && typeof v === "object" && !Array.isArray(v)) {
          const slots = {};
          for (const [slot, slotV] of Object.entries(v)) {
            slots[slot] = EMIT_OPTION_SLOTS.has(slot) && typeof slotV === "string" ? rename(slotV) ?? slotV : renameEventRefsInNode(slotV, rename);
          }
          next[k] = slots;
        } else {
          next[k] = renameEventRefsInNode(v, rename);
        }
        continue;
      }
      next[k] = renameEventRefsInNode(v, rename);
    }
    return next;
  }
  return node;
}
var REBIND_ENTITY_PROPS = /* @__PURE__ */ new Set(["entity"]);
var ID_ENTITY_PROPS = /* @__PURE__ */ new Set(["entity", "entityType", "source"]);
function renameEntityInRenderUiConfig(node, rename, props) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    return node.map((item) => renameEntityInRenderUiConfig(item, rename, props));
  }
  if (typeof node !== "object") return node;
  const obj = node;
  const next = { ...obj };
  for (const [key, value] of Object.entries(obj)) {
    if (props.has(key) && typeof value === "string") {
      const replaced = rename(value);
      if (replaced !== void 0) {
        next[key] = replaced;
        continue;
      }
    }
    next[key] = renameEntityInRenderUiConfig(value, rename, props);
  }
  return next;
}
function renameEntityInEffects(effects, rename, props) {
  return effects.map((effect) => renameEntityInEffect(effect, rename, props));
}
var ENTITY_AT_POS_1 = /* @__PURE__ */ new Set(["fetch", "ref", "deref", "spawn"]);
var ALL_ARGS_ARE_EFFECTS = /* @__PURE__ */ new Set([
  "do",
  "atomic",
  "async/race",
  "async/all",
  "async/sequence"
]);
var ARGS_FROM_POS_2_ARE_EFFECTS = /* @__PURE__ */ new Set([
  "if",
  "when",
  "let",
  "async/delay",
  "async/debounce",
  "async/throttle",
  "async/interval"
]);
function renameEntityInEffect(effect, rename, props) {
  if (!Array.isArray(effect) || effect.length === 0) return effect;
  const op = effect[0];
  if (typeof op !== "string") return effect;
  if (op === "render-ui" && effect.length >= 3) {
    const [, slot, config, ...rest] = effect;
    const nextConfig = renameEntityInRenderUiConfig(config, rename, props);
    return [op, slot, nextConfig, ...rest];
  }
  if (op === "persist" && effect.length >= 3 && typeof effect[2] === "string") {
    const replaced = rename(effect[2]);
    if (replaced !== void 0) return [op, effect[1], replaced, ...effect.slice(3)];
  }
  if (ENTITY_AT_POS_1.has(op) && typeof effect[1] === "string") {
    const replaced = rename(effect[1]);
    if (replaced !== void 0) return [op, replaced, ...effect.slice(2)];
  }
  const skipFirstNonEffectArg = ARGS_FROM_POS_2_ARE_EFFECTS.has(op);
  const recurseAll = ALL_ARGS_ARE_EFFECTS.has(op);
  if (recurseAll || skipFirstNonEffectArg) {
    const startIndex = skipFirstNonEffectArg ? 2 : 1;
    return effect.map((arg, i) => {
      if (i < startIndex) return arg;
      if (Array.isArray(arg)) {
        return renameEntityInEffect(arg, rename, props);
      }
      return arg;
    });
  }
  return effect;
}
var TRAIT_EMBED_PREFIX = "@trait.";
function renameTraitEmbedsInValue(node, subs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    if (!node.startsWith(TRAIT_EMBED_PREFIX)) return node;
    const rest = node.slice(TRAIT_EMBED_PREFIX.length);
    const dot = rest.search(/[.[]/);
    const name = dot === -1 ? rest : rest.slice(0, dot);
    const suffix = dot === -1 ? "" : rest.slice(dot);
    const to = subs.get(name);
    return to === void 0 ? node : `${TRAIT_EMBED_PREFIX}${to}${suffix}`;
  }
  if (Array.isArray(node)) return node.map((item) => renameTraitEmbedsInValue(item, subs));
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = renameTraitEmbedsInValue(value, subs);
  }
  return next;
}
function renameEntityNameTokensInValue(node, entitySubs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    if (!node.startsWith("@")) return node;
    const rest = node.slice(1);
    const dot = rest.search(/[.[]/);
    const name = dot === -1 ? rest : rest.slice(0, dot);
    const suffix = dot === -1 ? "" : rest.slice(dot);
    const to = entitySubs.get(name);
    return to === void 0 ? node : `@${to}${suffix}`;
  }
  if (Array.isArray(node)) return node.map((item) => renameEntityNameTokensInValue(item, entitySubs));
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = renameEntityNameTokensInValue(value, entitySubs);
  }
  return next;
}
function renameTraitEmbeds(trait, subs) {
  if (subs.size === 0) return trait;
  const sm = trait.stateMachine;
  const next = { ...trait };
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map(
        (t) => t.effects ? { ...t, effects: renameTraitEmbedsInValue(t.effects, subs) } : t
      )
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map(
      (tick) => tick.effects ? { ...tick, effects: renameTraitEmbedsInValue(tick.effects, subs) } : tick
    );
  }
  if (trait.config) {
    const nextConfig = {};
    for (const [key, field] of Object.entries(trait.config)) {
      nextConfig[key] = field.default === void 0 ? field : {
        ...field,
        default: renameTraitEmbedsInValue(
          field.default,
          subs
        )
      };
    }
    next.config = nextConfig;
  }
  return next;
}
function findTraitEntryInOrbital(orbital, localName) {
  for (const traitRef of orbital.traits ?? []) {
    if (typeof traitRef === "string") continue;
    if ("stateMachine" in traitRef) {
      if (traitRef.name === localName) return traitRef;
      continue;
    }
    if (!("ref" in traitRef)) continue;
    const refObj = traitRef;
    const declared = refObj.name ?? parseImportedTraitRef(refObj.ref)?.traitName;
    if (declared === localName) return traitRef;
  }
  return null;
}
function homeOrbitalConfigForTrait(imported, traitName) {
  for (const o of importedOrbitals(imported)) {
    if (findTraitEntryInOrbital(o, traitName)) return o.config;
  }
  return void 0;
}
function findPageEntryInOrbital(orbital, localName) {
  for (const pageRef of orbital.pages ?? []) {
    if (typeof pageRef === "string") continue;
    if (!("ref" in pageRef)) {
      if (pageRef.name === localName) return pageRef;
      continue;
    }
    const refObj = pageRef;
    if (parsePageRef(refObj.ref)?.pageName === localName) return refObj;
  }
  return null;
}
function toPageTraitRef(traitRef) {
  if (typeof traitRef === "string") return { ref: traitRef };
  if ("stateMachine" in traitRef) return { ref: traitRef.name };
  const refObj = traitRef;
  return {
    ref: refObj.name ?? refObj.ref,
    ...refObj.refId !== void 0 ? { refId: refObj.refId } : {},
    ...refObj.linkedEntity !== void 0 ? { linkedEntity: refObj.linkedEntity } : {},
    ...refObj.linkedEntityId !== void 0 ? { linkedEntityId: refObj.linkedEntityId } : {},
    ...refObj.config !== void 0 ? { config: refObj.config } : {}
  };
}
function traitEmbedNamesOf(trait) {
  const found = /* @__PURE__ */ new Set();
  const walk = (node) => {
    if (node === null || node === void 0) return;
    if (typeof node === "string") {
      if (!node.startsWith(TRAIT_EMBED_PREFIX)) return;
      const rest = node.slice(TRAIT_EMBED_PREFIX.length);
      const dot = rest.search(/[.[]/);
      const name = dot === -1 ? rest : rest.slice(0, dot);
      if (name.length > 0) found.add(name);
      return;
    }
    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }
    if (typeof node !== "object") return;
    for (const value of Object.values(node)) walk(value);
  };
  for (const t of trait.stateMachine?.transitions ?? []) {
    walk(t.guard);
    walk(t.effects);
  }
  for (const tick of trait.ticks ?? []) {
    walk(tick.guard);
    walk(tick.effects);
  }
  for (const field of Object.values(trait.config ?? {})) walk(field.default);
  return [...found].sort();
}
function resolveForwardedSiblingConfigFrom(trait, parentChain, orbitalConfig, schemaConfig, schemaOnlyKeys) {
  const declared = trait.config;
  if (!declared) return trait;
  const next = resolveConfigForwards(
    declared,
    parentChain,
    orbitalConfig,
    void 0,
    void 0,
    schemaConfig,
    schemaOnlyKeys
  );
  return next ? { ...trait, config: next } : trait;
}
function callSiteRungValue(rungConfig, knob) {
  const entry = rungConfig[knob];
  if (entry === void 0) return void 0;
  return isCallSiteConfigDeclaration(entry) ? entry.default : entry;
}
function walkConfigForwardChain(forward, parentChain, orbitalConfig, upstreamOrbitalConfig, upstreamSchemaConfig, schemaConfig) {
  const unresolved = { value: void 0, reachedSchemaConfigOnly: false };
  let knob = forward.slice("@config.".length);
  if (knob.length === 0 || knob.includes(".")) return unresolved;
  let value;
  let currentForward = forward;
  for (const rungConfig of parentChain) {
    const rungValue = callSiteRungValue(rungConfig, knob);
    if (rungValue === void 0 || rungValue === currentForward) continue;
    if (typeof rungValue === "string" && rungValue.startsWith("@config.")) {
      const nextKnob = rungValue.slice("@config.".length);
      if (nextKnob.length === 0 || nextKnob.includes(".") || nextKnob === knob) continue;
      knob = nextKnob;
      currentForward = rungValue;
      continue;
    }
    value = rungValue;
    break;
  }
  if (value === void 0 || value === currentForward) value = orbitalConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) value = upstreamOrbitalConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) value = upstreamSchemaConfig?.[knob]?.default;
  const reachesSchemaConfigRung = value === void 0 || value === currentForward;
  if (reachesSchemaConfigRung) value = schemaConfig?.[knob]?.default;
  if (value === void 0 || value === currentForward) return unresolved;
  return { value, reachedSchemaConfigOnly: reachesSchemaConfigRung };
}
function resolveConfigForwards(declared, parentChain, orbitalConfig, upstreamOrbitalConfig, upstreamSchemaConfig, schemaConfig, schemaOnlyKeys) {
  let next;
  for (const [key, field] of Object.entries(declared)) {
    const forward = field.default;
    if (typeof forward !== "string" || !forward.startsWith("@config.")) continue;
    const { value, reachedSchemaConfigOnly } = walkConfigForwardChain(
      forward,
      parentChain,
      orbitalConfig,
      upstreamOrbitalConfig,
      upstreamSchemaConfig,
      schemaConfig
    );
    if (value === void 0) continue;
    if (reachedSchemaConfigOnly) schemaOnlyKeys?.add(key);
    next ??= { ...declared };
    next[key] = { ...field, default: value, forwardedFrom: forward };
  }
  return next;
}
function resolveCallSiteConfigForwards(config, parentChain, orbitalConfig, schemaConfig) {
  let next;
  for (const [key, entry] of Object.entries(config)) {
    const forward = isCallSiteConfigDeclaration(entry) ? entry.default : entry;
    if (typeof forward !== "string" || !forward.startsWith("@config.")) continue;
    const { value } = walkConfigForwardChain(forward, parentChain, orbitalConfig, void 0, void 0, schemaConfig);
    if (value === void 0) continue;
    next ??= { ...config };
    next[key] = isCallSiteConfigDeclaration(entry) ? { ...entry, default: value, forwardedFrom: forward } : value;
  }
  return next;
}
function traitRefEmbedNamesOf(traitRef) {
  if (typeof traitRef === "string") return [];
  return traitEmbedNamesOf(traitRef);
}
function traitRefLocalName(traitRef) {
  if (typeof traitRef === "string") {
    return parseImportedTraitRef(traitRef)?.traitName ?? traitRef;
  }
  if ("stateMachine" in traitRef) return traitRef.name;
  const refObj = traitRef;
  return refObj.name ?? parseImportedTraitRef(refObj.ref)?.traitName ?? refObj.ref;
}
function traitRefConfigOf(traitRef) {
  if (typeof traitRef === "string") return void 0;
  return traitRef.config;
}
function buildEmbedGraph(traitRefs) {
  const out = /* @__PURE__ */ new Map();
  for (const traitRef of traitRefs) {
    const referrerName = traitRefLocalName(traitRef);
    if (!referrerName) continue;
    for (const child of traitRefEmbedNamesOf(traitRef)) {
      if (child === referrerName) continue;
      if (!out.has(child)) out.set(child, referrerName);
    }
  }
  return out;
}
function embedderChain(localName, traitRefs, graph) {
  const chain = [];
  const visited = /* @__PURE__ */ new Set([localName]);
  let cur = graph.get(localName);
  let hops = 0;
  while (cur !== void 0 && !visited.has(cur) && hops < traitRefs.length) {
    visited.add(cur);
    hops++;
    const entry = traitRefs.find((t) => traitRefLocalName(t) === cur);
    const entryConfig = entry ? traitRefConfigOf(entry) : void 0;
    if (entryConfig) chain.push(entryConfig);
    cur = graph.get(cur);
  }
  return chain;
}
function resolveEmbedderConfigForward(config, localName, embedCtx, schemaConfig) {
  const chain = embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph);
  const next = resolveCallSiteConfigForwards(config, chain, embedCtx.orbitalConfig, schemaConfig);
  return next ? next : config;
}
function consumerKnownEntityRebindSubs(trait, entityIds) {
  const out = /* @__PURE__ */ new Map();
  const refs = trait.entityRefIds;
  if (!refs) return out;
  const idToConsumerName = /* @__PURE__ */ new Map();
  for (const [name, id] of entityIds) {
    if (!idToConsumerName.has(id)) idToConsumerName.set(id, name);
  }
  for (const [tokenName, entityId] of Object.entries(refs)) {
    const consumerName = idToConsumerName.get(entityId);
    if (consumerName !== void 0 && consumerName !== tokenName) {
      out.set(tokenName, consumerName);
    }
  }
  return out;
}
function staleEntityRebindSubs(trait, newEntity) {
  const out = /* @__PURE__ */ new Map();
  const boundId = trait.linkedEntityId;
  const refs = trait.entityRefIds;
  if (boundId && refs) {
    for (const [tokenName, entityId] of Object.entries(refs)) {
      if (entityId === boundId) out.set(tokenName, newEntity);
    }
  }
  return out;
}
function applyLinkedEntityRename(trait, linkedEntity, entityIds) {
  const atomLinked = trait.linkedEntity;
  const entitySubs = consumerKnownEntityRebindSubs(trait, entityIds);
  if (!linkedEntity || !atomLinked || linkedEntity === atomLinked) {
    return rewriteEntityTypedConfigValues(rewriteEntityRefIds(trait, entitySubs, entityIds), entitySubs, entityIds);
  }
  for (const [from, to] of staleEntityRebindSubs(trait, linkedEntity)) {
    entitySubs.set(from, to);
  }
  entitySubs.set(atomLinked, linkedEntity);
  const rename = (name) => name === atomLinked ? linkedEntity : void 0;
  const sm = trait.stateMachine;
  const rebound = {
    ...trait,
    linkedEntity,
    linkedEntityId: entityIds.get(linkedEntity)
  };
  if (!sm) {
    return rewriteEntityTypedConfigValues(
      renamePayloadEntityMarkers(rewriteEntityRefIds(rebound, entitySubs, entityIds), rename),
      entitySubs,
      entityIds
    );
  }
  const nextTransitions = (sm.transitions ?? []).map((t) => {
    const nextEffects = t.effects ? renameEntityInEffects(
      t.effects,
      rename,
      REBIND_ENTITY_PROPS
    ) : t.effects;
    return { ...t, effects: nextEffects };
  });
  refResolverLog.info("linkedEntity:rename", {
    trait: trait.name,
    from: atomLinked,
    to: linkedEntity,
    transitionCount: nextTransitions.length
  });
  return rewriteEntityTypedConfigValues(
    renamePayloadEntityMarkers(
      rewriteEntityRefIds(
        { ...rebound, stateMachine: { ...sm, transitions: nextTransitions } },
        entitySubs,
        entityIds
      ),
      rename
    ),
    entitySubs,
    entityIds
  );
}
function resolveEntityTokensById(trait, idIndex) {
  const map = trait.entityRefIds;
  if (!map) return trait;
  const rewrites = /* @__PURE__ */ new Map();
  for (const [tokenName, entityId] of Object.entries(map)) {
    const entry = idIndex.get(entityId);
    if (entry && entry.kind === "entity") {
      const currentName = entry.node.name;
      if (currentName && currentName !== tokenName) {
        rewrites.set(tokenName, currentName);
      }
    }
  }
  if (rewrites.size === 0) return trait;
  const rename = (name) => rewrites.get(name);
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    effects: t.effects ? renameEntityInEffects(
      t.effects,
      rename,
      ID_ENTITY_PROPS
    ) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    effects: renameEntityInEffects(
      tick.effects,
      rename,
      ID_ENTITY_PROPS
    )
  })) : trait.ticks;
  const nextInitial = trait.initialEffects ? renameEntityInEffects(
    trait.initialEffects,
    rename,
    ID_ENTITY_PROPS
  ) : trait.initialEffects;
  const nextLinked = trait.linkedEntity !== void 0 ? rewrites.get(trait.linkedEntity) ?? trait.linkedEntity : trait.linkedEntity;
  refResolverLog.info("entity-ref:id-resolve", {
    trait: trait.name,
    rewrites: Object.fromEntries(rewrites)
  });
  return {
    ...trait,
    linkedEntity: nextLinked,
    ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
    ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
    ...nextInitial !== void 0 ? { initialEffects: nextInitial } : {}
  };
}
var REFERENCE_CONFIG_TYPE_TO_ID_KIND = {
  entity: "entity",
  trait: "trait",
  event: "event"
};
function resolveConfigRefsById(trait, idIndex) {
  const schema = trait.config;
  if (!schema) return trait;
  let nextSchema;
  const rewrites = [];
  for (const [key, field] of Object.entries(schema)) {
    if (!field.refId || !isReferenceConfigType(field.type)) continue;
    const expectedKind = REFERENCE_CONFIG_TYPE_TO_ID_KIND[field.type];
    if (!expectedKind) continue;
    const entry = idIndex.get(field.refId);
    if (!entry || entry.kind !== expectedKind) continue;
    const currentName = entry.kind === "entity" ? entry.node.name : entry.kind === "event" ? entry.node.key : entry.node.name;
    if (!currentName) continue;
    const isTraitBinding = entry.kind === "trait" && typeof field.default === "string" && field.default.startsWith("@trait.");
    const nextDefault = isTraitBinding ? `@trait.${currentName}` : currentName;
    if (nextDefault === field.default) continue;
    nextSchema ??= { ...schema };
    nextSchema[key] = { ...field, default: nextDefault };
    rewrites.push({ key, from: field.default, to: nextDefault });
  }
  if (!nextSchema) return trait;
  refResolverLog.info("config-ref:id-resolve", {
    trait: trait.name,
    rewrites
  });
  return { ...trait, config: nextSchema };
}
function applyEventRenames(trait, renames, keptFinalNames) {
  if (!renames || Object.keys(renames).length === 0) return trait;
  const rename = (k) => k !== void 0 && k in renames ? renames[k] : k;
  const sm = trait.stateMachine;
  const nextTransitions = (sm?.transitions ?? []).map((t) => {
    const nextEvent = rename(t.event) ?? t.event;
    const nextEffects = t.effects ? renameEventsInEffects(t.effects, rename) : t.effects;
    return { ...t, event: nextEvent, effects: nextEffects };
  });
  const nextEvents = (sm?.events ?? []).map((e) => {
    const newKey = rename(e.key);
    if (newKey === e.key) return e;
    return { ...e, key: newKey ?? e.key };
  });
  const nextEmits = (trait.emits ?? []).map((em) => {
    if (typeof em === "string") return rename(em) ?? em;
    const newEvent = rename(em.event);
    return newEvent === em.event ? em : { ...em, event: newEvent ?? em.event };
  });
  const nextTicks = (trait.ticks ?? []).map(
    (tk) => tk.effects ? {
      ...tk,
      effects: renameEventsInEffects(
        tk.effects,
        rename
      )
    } : tk
  );
  const nextListens = (trait.listens ?? []).map((l) => {
    const sourced = l.source !== void 0 && l.source.kind !== "any";
    const sourceTraitName = l.source && l.source.kind !== "any" ? l.source.trait : void 0;
    const fromThisImport = sourceTraitName !== void 0 && keptFinalNames !== void 0 && keptFinalNames.has(sourceTraitName);
    const nextEvent = sourced && !fromThisImport ? l.event : rename(l.event) ?? l.event;
    const nextTriggers = rename(l.triggers) ?? l.triggers;
    return nextEvent === l.event && nextTriggers === l.triggers ? l : { ...l, event: nextEvent, triggers: nextTriggers };
  });
  const nextConfig = renameEventsInDeclaredConfig(trait.config, rename);
  return {
    ...trait,
    stateMachine: sm ? {
      ...sm,
      transitions: nextTransitions,
      events: nextEvents
    } : sm,
    ...trait.ticks ? { ticks: nextTicks } : {},
    ...trait.listens ? { listens: nextListens } : {},
    ...nextConfig !== void 0 ? { config: nextConfig } : {},
    emits: nextEmits
  };
}
function applyEventRenamesToCallSiteConfig(config, declared, renames) {
  if (config === void 0 || declared === void 0) return config;
  if (!renames || Object.keys(renames).length === 0) return config;
  const rename = (k) => k !== void 0 && k in renames ? renames[k] : k;
  let touched = false;
  const next = { ...config };
  for (const [knob, value] of Object.entries(config)) {
    const field = declared[knob];
    if (field === void 0) continue;
    const renamed = field.type === "event" ? typeof value === "string" && value !== "" && !value.startsWith("@") ? rename(value) ?? value : value : renameEventTypedMembers(value, field.items, rename);
    if (renamed !== value) {
      next[knob] = renamed;
      touched = true;
    }
  }
  return touched ? next : config;
}
function resolvedKnobValues(before, after, knobs) {
  const out = /* @__PURE__ */ new Map();
  if (knobs.length === 0) return out;
  const knobSet = new Set(knobs);
  const beforeEmits = before.emits ?? [];
  const afterEmits = after.emits ?? [];
  for (let i = 0; i < beforeEmits.length; i++) {
    const knob = configRefEventKnob(beforeEmits[i].event);
    if (knob !== void 0 && knobSet.has(knob)) out.set(knob, afterEmits[i].event);
  }
  return out;
}
function foldEventRenameOntoDeclaredKnobs(trait, resolved, eventRenames) {
  if (!eventRenames || resolved.size === 0 || !trait.config) return trait;
  let nextConfig;
  for (const [knob, resolvedValue] of resolved) {
    const renamed = eventRenames[resolvedValue];
    if (renamed === void 0 || renamed === resolvedValue) continue;
    const field = trait.config[knob];
    if (!field) continue;
    nextConfig ??= { ...trait.config };
    nextConfig[knob] = { ...field, default: renamed };
  }
  return nextConfig ? { ...trait, config: nextConfig } : trait;
}
function resolveConfigRefEmitNames(trait, callSiteConfig) {
  const emits = trait.emits ?? [];
  const hasRef = emits.some((em) => configRefEventKnob(em.event) !== void 0);
  if (!hasRef) return { trait, errors: [], resolvedKnobs: [] };
  const effectiveConfig = {
    ...normalizeCallSiteConfigToValues(trait.config) ?? {},
    ...normalizeCallSiteConfigToValues(callSiteConfig) ?? {}
  };
  const errors = [];
  const resolvedTriples = [];
  const nextEmits = emits.map((em) => {
    const knob = configRefEventKnob(em.event);
    if (knob === void 0) return em;
    const result = resolveConfigRefEventName(em.event, trait.config, effectiveConfig);
    if (!result.ok) {
      errors.push(
        `Trait "${trait.name}" emits \`${em.event}\` but the reference is invalid (${result.error}): the knob must be a declared string-typed config field with a default.`
      );
      return em;
    }
    refResolverLog.debug("emit-config-ref:resolved", {
      trait: trait.name,
      ref: em.event,
      resolved: result.value
    });
    resolvedTriples.push({ knob, from: em.event, to: result.value });
    return { ...em, event: result.value };
  });
  const sm = trait.stateMachine;
  const nextEvents = resolvedTriples.length > 0 && sm ? sm.events.map((ev) => {
    const hit = resolvedTriples.find((t) => t.from === ev.key);
    return hit ? { ...ev, key: hit.to } : ev;
  }) : sm?.events;
  return {
    trait: {
      ...trait,
      emits: nextEmits,
      ...sm && nextEvents !== sm.events ? { stateMachine: { ...sm, events: nextEvents } } : {}
    },
    errors,
    resolvedKnobs: resolvedTriples.map((t) => t.knob)
  };
}
function foldCallSiteConfigOntoTrait(trait, callSiteConfig) {
  if (!callSiteConfig) return trait;
  const declared = trait.config;
  const overrideValues = {};
  const overriddenKeys = /* @__PURE__ */ new Set();
  const newDeclarations = {};
  for (const [key, entry] of Object.entries(callSiteConfig)) {
    const isDecl = isCallSiteConfigDeclaration(entry);
    const value = isDecl ? entry.default : entry;
    if (declared && key in declared) {
      if (value !== void 0) {
        overrideValues[key] = value;
        overriddenKeys.add(key);
      }
    } else if (isDecl) {
      newDeclarations[key] = entry;
    }
  }
  if (overriddenKeys.size === 0 && Object.keys(newDeclarations).length === 0) {
    return trait;
  }
  let nextConfig = overrideDeclaredKnobs(declared ?? {}, overrideValues);
  if (overriddenKeys.size > 0) {
    const patched = { ...nextConfig };
    for (const key of overriddenKeys) {
      const field = patched[key];
      if (!field) continue;
      const entry = callSiteConfig[key];
      const entryDecl = isCallSiteConfigDeclaration(entry) ? entry : void 0;
      patched[key] = {
        ...field,
        forwardedFrom: entryDecl?.forwardedFrom,
        // The atom's OWN `refId` (a reference-config field's declared
        // default names a SPECIFIC node by id) describes the ATOM's OWN
        // `default`, not whatever this override just replaced it with —
        // carrying it forward unchanged left a STALE id bound to the NEW
        // literal string, so the id-index's "current name" correction
        // (`resolveConfigRefsById`) silently rewrote the override BACK to
        // the atom's own default target — found via std-notes'
        // `NoteGlobalSearch`: `idleContent`'s override says `@trait.
        // NoteCatalog` but the atom's OWN stale `refId` (still pointing at
        // `AppSearchIdleHint`, the atom's default) rewrote the sibling-pull
        // target back to it (rung 2: an override with no id of its own
        // names no id, so it must clear the atom's). An override that
        // carries ITS OWN `refId` (an annotated, id-aware override) wins
        // instead — same precedence a `default` override already has.
        refId: entryDecl?.refId,
        ...entryDecl?.label !== void 0 ? { label: entryDecl.label } : {},
        ...entryDecl?.description !== void 0 ? { description: entryDecl.description } : {},
        ...entryDecl?.synonyms !== void 0 ? { synonyms: entryDecl.synonyms } : {},
        ...entryDecl?.tier !== void 0 ? { tier: entryDecl.tier } : {}
      };
    }
    nextConfig = patched;
  }
  if (Object.keys(newDeclarations).length > 0) {
    nextConfig = { ...nextConfig, ...newDeclarations };
  }
  return { ...trait, config: nextConfig };
}
var ROW_ALIAS_TOKEN_PREFIXES = ["@entity.", "@item.", "@row."];
var ROW_ALIAS_ROOTS = /* @__PURE__ */ new Set(["@entity", "@item", "@row"]);
var FIELD_NAME_MEMBER_KEYS = ["key", "name", "field"];
function rewriteEntityFieldTokensInValue(node, subs) {
  if (node === null || node === void 0) return node;
  if (typeof node === "string") {
    for (const prefix of ROW_ALIAS_TOKEN_PREFIXES) {
      if (!node.startsWith(prefix)) continue;
      const rest = node.slice(prefix.length);
      const dot = rest.search(/[.[]/);
      const name = dot === -1 ? rest : rest.slice(0, dot);
      const suffix = dot === -1 ? "" : rest.slice(dot);
      const to = subs.get(name);
      return to === void 0 ? node : `${prefix}${to}${suffix}`;
    }
    return node;
  }
  if (Array.isArray(node)) {
    if (node.length >= 3 && node[0] === "object/get" && typeof node[1] === "string" && ROW_ALIAS_ROOTS.has(node[1]) && typeof node[2] === "string") {
      const to = subs.get(node[2]);
      if (to !== void 0) {
        const rewritten = node.map((item) => rewriteEntityFieldTokensInValue(item, subs));
        rewritten[2] = to;
        return rewritten;
      }
    }
    return node.map((item) => rewriteEntityFieldTokensInValue(item, subs));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    if (FIELD_NAME_MEMBER_KEYS.includes(key) && typeof value === "string") {
      const to = subs.get(value);
      next[key] = to === void 0 ? value : to;
      continue;
    }
    next[key] = rewriteEntityFieldTokensInValue(value, subs);
  }
  return next;
}
function rewriteBareFieldNameConfigDefault(value, fieldSubs) {
  if (typeof value === "string") {
    if (value === "" || value.startsWith("@")) return value;
    return fieldSubs.get(value) ?? value;
  }
  if (Array.isArray(value) && value.every((v) => typeof v === "string")) {
    return value.map((v) => v === "" || v.startsWith("@") ? v : fieldSubs.get(v) ?? v);
  }
  return value;
}
function rewriteEntityFieldsInConfig(config, fieldSubs) {
  if (fieldSubs.size === 0 || !config) return config;
  const rewrite = (v) => rewriteEntityFieldTokensInValue(v, fieldSubs);
  const nextConfig = {};
  for (const [key, field] of Object.entries(config)) {
    if (field.default === void 0) {
      nextConfig[key] = field;
      continue;
    }
    const afterSigils = rewrite(field.default);
    nextConfig[key] = {
      ...field,
      default: rewriteBareFieldNameConfigDefault(afterSigils, fieldSubs)
    };
  }
  return nextConfig;
}
function rewriteEntityFieldsInTrait(trait, fieldSubs) {
  if (fieldSubs.size === 0) return trait;
  const rewrite = (v) => rewriteEntityFieldTokensInValue(v, fieldSubs);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map((t) => ({
        ...t,
        ...t.guard !== void 0 ? { guard: rewrite(t.guard) } : {},
        ...t.effects ? { effects: rewrite(t.effects) } : {}
      }))
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({
      ...tick,
      ...tick.guard !== void 0 ? { guard: rewrite(tick.guard) } : {},
      effects: rewrite(tick.effects)
    }));
  }
  if (trait.listens) {
    next.listens = trait.listens.map((l) => ({
      ...l,
      ...l.guard !== void 0 ? { guard: rewrite(l.guard) } : {},
      ...l.payloadMapping ? { payloadMapping: rewrite(l.payloadMapping) } : {}
    }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  if (trait.config) {
    next.config = rewriteEntityFieldsInConfig(trait.config, fieldSubs);
  }
  return next;
}
function rewriteEntityFieldsInEntity(entity, fieldSubs) {
  if (fieldSubs.size === 0) return entity;
  const rewrite = (v) => rewriteEntityFieldTokensInValue(v, fieldSubs);
  return {
    ...entity,
    fields: entity.fields.map((f) => f.name && fieldSubs.has(f.name) ? { ...f, name: fieldSubs.get(f.name) } : f),
    ...entity.read_policy !== void 0 ? { read_policy: rewrite(entity.read_policy) } : {},
    ...entity.create_policy !== void 0 ? { create_policy: rewrite(entity.create_policy) } : {},
    ...entity.update_policy !== void 0 ? { update_policy: rewrite(entity.update_policy) } : {},
    ...entity.delete_policy !== void 0 ? { delete_policy: rewrite(entity.delete_policy) } : {}
  };
}
var USER_FIELD_PREFIX = "@user.";
function directUserField(node) {
  if (typeof node !== "string" || !node.startsWith(USER_FIELD_PREFIX)) return void 0;
  const rest = node.slice(USER_FIELD_PREFIX.length);
  return rest.length > 0 && !/[.[]/.test(rest) ? rest : void 0;
}
var EQUALITY_OPS = /* @__PURE__ */ new Set(["=", "==", "!=", "!==", "eq", "neq"]);
var NEGATED_EQUALITY_OPS = /* @__PURE__ */ new Set(["!=", "!==", "neq"]);
function literalHaystackMembers(items) {
  const isListCall = items[0] === "list";
  const members = isListCall ? items.slice(1) : items;
  return members.every((v) => typeof v === "string") ? [...members] : void 0;
}
function rewriteRoleLiteralsInValue(node, roleFields, roles) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    const head = node[0];
    if (typeof head === "string" && EQUALITY_OPS.has(head) && node.length === 3) {
      const [, left, right] = node;
      const leftField = directUserField(left);
      const rightField = directUserField(right);
      const userField = leftField !== void 0 && rightField === void 0 ? leftField : rightField !== void 0 && leftField === void 0 ? rightField : void 0;
      if (userField !== void 0 && roleFields.has(userField)) {
        const literalSide = leftField !== void 0 ? right : left;
        if (typeof literalSide === "string" && literalSide in roles) {
          const targets = roles[literalSide];
          if (targets.length === 1) {
            return leftField !== void 0 ? [head, left, targets[0]] : [head, targets[0], right];
          }
          const includesExpr = ["array/includes", [...targets], leftField !== void 0 ? left : right];
          return NEGATED_EQUALITY_OPS.has(head) ? ["not", includesExpr] : includesExpr;
        }
      }
    }
    if (head === "array/includes" && node.length === 3 && Array.isArray(node[1])) {
      const needleField = directUserField(node[2]);
      if (needleField !== void 0 && roleFields.has(needleField)) {
        const members = literalHaystackMembers(node[1]);
        if (members !== void 0) {
          const expanded = [];
          for (const literal of members) {
            const mapped = roles[literal];
            if (mapped) {
              for (const target of mapped) if (!expanded.includes(target)) expanded.push(target);
            } else if (!expanded.includes(literal)) {
              expanded.push(literal);
            }
          }
          const isListCall = node[1][0] === "list";
          return ["array/includes", isListCall ? ["list", ...expanded] : expanded, node[2]];
        }
      }
    }
    return node.map((item) => rewriteRoleLiteralsInValue(item, roleFields, roles));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = rewriteRoleLiteralsInValue(value, roleFields, roles);
  }
  return next;
}
function rewriteRoleLiteralsInTrait(trait, roleFields, roles) {
  if (roleFields.size === 0 || Object.keys(roles).length === 0) return trait;
  const rewrite = (v) => rewriteRoleLiteralsInValue(v, roleFields, roles);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map((t) => ({
        ...t,
        ...t.guard !== void 0 ? { guard: rewrite(t.guard) } : {},
        ...t.effects ? { effects: rewrite(t.effects) } : {}
      }))
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({
      ...tick,
      ...tick.guard !== void 0 ? { guard: rewrite(tick.guard) } : {},
      effects: rewrite(tick.effects)
    }));
  }
  if (trait.listens) {
    next.listens = trait.listens.map((l) => ({
      ...l,
      ...l.guard !== void 0 ? { guard: rewrite(l.guard) } : {},
      ...l.payloadMapping ? { payloadMapping: rewrite(l.payloadMapping) } : {}
    }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  if (trait.config) {
    const nextConfig = {};
    for (const [key, field] of Object.entries(trait.config)) {
      nextConfig[key] = field.default === void 0 ? field : { ...field, default: rewrite(field.default) };
    }
    next.config = nextConfig;
  }
  if (trait.sourceEntityDefinition) {
    next.sourceEntityDefinition = rewriteRoleLiteralsInEntity(trait.sourceEntityDefinition, roleFields, roles);
  }
  return next;
}
function rewriteRoleLiteralsInEntity(entity, roleFields, roles) {
  if (roleFields.size === 0 || Object.keys(roles).length === 0) return entity;
  const rewrite = (v) => rewriteRoleLiteralsInValue(v, roleFields, roles);
  return {
    ...entity,
    ...entity.read_policy !== void 0 ? { read_policy: rewrite(entity.read_policy) } : {},
    ...entity.create_policy !== void 0 ? { create_policy: rewrite(entity.create_policy) } : {},
    ...entity.update_policy !== void 0 ? { update_policy: rewrite(entity.update_policy) } : {},
    ...entity.delete_policy !== void 0 ? { delete_policy: rewrite(entity.delete_policy) } : {}
  };
}
function renameRelationTargetsInField(field, entitySubs, entityIds) {
  let next = field;
  if (next.type === "relation") {
    const to = entitySubs.get(next.relation.entity);
    if (to !== void 0) {
      next = { ...next, relation: { ...next.relation, entity: to, entityId: entityIds.get(to) } };
    }
  }
  if ((next.type === "array" || next.type === "object") && next.items) {
    next = { ...next, items: renameRelationTargetsInField(next.items, entitySubs, entityIds) };
  }
  if (next.properties) {
    const nextProperties = {};
    for (const [key, value] of Object.entries(next.properties)) {
      nextProperties[key] = renameRelationTargetsInField(value, entitySubs, entityIds);
    }
    next = { ...next, properties: nextProperties };
  }
  return next;
}
function renameEntityRelationTargets(entity, entitySubs, entityIds) {
  if (entitySubs.size === 0) return entity;
  return { ...entity, fields: entity.fields.map((f) => renameRelationTargetsInField(f, entitySubs, entityIds)) };
}
function renameSourceEntityDefinition(trait, subs, entityIds) {
  const src = trait.sourceEntityDefinition;
  if (!src || subs.size === 0) return trait;
  const renamedName = subs.get(src.name) ?? src.name;
  const nextSrc = renameEntityRelationTargets({ ...src, name: renamedName }, subs, entityIds);
  return { ...trait, sourceEntityDefinition: nextSrc };
}
function renamePayloadEntity(fields, rename) {
  return fields.map((field) => {
    const renamedEntity = field.entity !== void 0 ? rename(field.entity) : void 0;
    const renamedProps = field.properties ? renamePayloadEntity(field.properties, rename) : field.properties;
    return {
      ...field,
      ...renamedEntity !== void 0 ? { entity: renamedEntity } : {},
      ...renamedProps !== void 0 ? { properties: renamedProps } : {}
    };
  });
}
function renamePayloadEntityMarkers(trait, rename) {
  const sm = trait.stateMachine;
  const nextEvents = sm?.events ? sm.events.map((ev) => {
    const renamedEntity = ev.payloadEntity !== void 0 ? rename(ev.payloadEntity) : void 0;
    if (!ev.payloadSchema && renamedEntity === void 0) return ev;
    return {
      ...ev,
      ...ev.payloadSchema ? { payloadSchema: renamePayloadEntity(ev.payloadSchema, rename) } : {},
      ...renamedEntity !== void 0 ? { payloadEntity: renamedEntity } : {}
    };
  }) : void 0;
  const nextEmits = trait.emits ? trait.emits.map((em) => {
    const renamedEntity = em.payloadEntity !== void 0 ? rename(em.payloadEntity) : void 0;
    if (!em.payloadSchema && renamedEntity === void 0) return em;
    return {
      ...em,
      ...em.payloadSchema ? { payloadSchema: renamePayloadEntity(em.payloadSchema, rename) } : {},
      ...renamedEntity !== void 0 ? { payloadEntity: renamedEntity } : {}
    };
  }) : void 0;
  return {
    ...trait,
    ...sm && nextEvents ? { stateMachine: { ...sm, events: nextEvents } } : {},
    ...nextEmits ? { emits: nextEmits } : {}
  };
}
function walkEntityInPayloadMapping(payloadMapping, rename, props) {
  const next = {};
  for (const [key, value] of Object.entries(payloadMapping)) {
    next[key] = renameEntityInEffect(value, rename, props);
  }
  return next;
}
function renameEntitiesInTrait(trait, entitySubs, entityIds) {
  if (entitySubs.size === 0) return trait;
  const rename = (name) => entitySubs.get(name);
  const withTokens = (value) => renameEntityNameTokensInValue(value, entitySubs);
  const nextLinked = trait.linkedEntity !== void 0 ? entitySubs.get(trait.linkedEntity) ?? trait.linkedEntity : trait.linkedEntity;
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    ...t.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(t.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: t.effects ? withTokens(renameEntityInEffects(t.effects, rename, REBIND_ENTITY_PROPS)) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    ...tick.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(tick.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: withTokens(renameEntityInEffects(
      tick.effects,
      rename,
      REBIND_ENTITY_PROPS
    ))
  })) : trait.ticks;
  const nextListens = trait.listens ? trait.listens.map((l) => ({
    ...l,
    ...l.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(l.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    ...l.payloadMapping ? { payloadMapping: withTokens(walkEntityInPayloadMapping(l.payloadMapping, rename, REBIND_ENTITY_PROPS)) } : {}
  })) : trait.listens;
  const nextInitial = trait.initialEffects ? withTokens(renameEntityInEffects(
    trait.initialEffects,
    rename,
    REBIND_ENTITY_PROPS
  )) : trait.initialEffects;
  return rewriteEntityTypedConfigValues(
    renamePayloadEntityMarkers(
      {
        ...trait,
        linkedEntity: nextLinked,
        ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
        ...nextTicks !== void 0 ? { ticks: nextTicks } : {},
        ...nextListens !== void 0 ? { listens: nextListens } : {},
        ...nextInitial !== void 0 ? { initialEffects: nextInitial } : {}
      },
      rename
    ),
    entitySubs,
    entityIds
  );
}
function rewriteEntityRefIds(trait, entitySubs, entityIds) {
  const refs = trait.entityRefIds;
  if (!refs) return trait;
  const out = {};
  for (const [from, oldId] of Object.entries(refs)) {
    const name = entitySubs.get(from) ?? from;
    out[name] = entityIds.get(name) ?? oldId;
  }
  return { ...trait, entityRefIds: out };
}
function rewriteEntityTypedConfigValues(trait, entitySubs, entityIds) {
  if (entitySubs.size === 0 || !trait.config) return trait;
  let changed = false;
  const nextConfig = { ...trait.config };
  for (const [key, field] of Object.entries(trait.config)) {
    if (field.type !== "entity" || typeof field.default !== "string") continue;
    const to = entitySubs.get(field.default);
    if (to === void 0) continue;
    changed = true;
    nextConfig[key] = { ...field, default: to, refId: entityIds.get(to) };
  }
  return changed ? { ...trait, config: nextConfig } : trait;
}
function applyAliasEntityRenames(trait, subs, entityIds) {
  if (subs.size === 0) return trait;
  const rename = (name) => subs.get(name);
  const withTokens = (value) => renameEntityNameTokensInValue(value, subs);
  const sm = trait.stateMachine;
  const nextTransitions = sm?.transitions ? sm.transitions.map((t) => ({
    ...t,
    ...t.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(t.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: t.effects ? withTokens(renameEntityInEffects(t.effects, rename, REBIND_ENTITY_PROPS)) : t.effects
  })) : sm?.transitions;
  const nextTicks = trait.ticks ? trait.ticks.map((tick) => ({
    ...tick,
    ...tick.guard !== void 0 ? { guard: withTokens(renameEntityInEffect(tick.guard, rename, REBIND_ENTITY_PROPS)) } : {},
    effects: withTokens(
      renameEntityInEffects(tick.effects, rename, REBIND_ENTITY_PROPS)
    )
  })) : trait.ticks;
  const next = {
    ...trait,
    ...sm ? { stateMachine: { ...sm, transitions: nextTransitions ?? [] } } : {},
    ...nextTicks !== void 0 ? { ticks: nextTicks } : {}
  };
  return rewriteEntityRefIds(next, subs, entityIds);
}
function collectEntityNamesInTrait(trait, candidates) {
  const collected = /* @__PURE__ */ new Set();
  const collect = (name) => {
    if (candidates.has(name)) collected.add(name);
    return void 0;
  };
  const sm = trait.stateMachine;
  for (const t of sm?.transitions ?? []) {
    if (t.guard !== void 0) renameEntityInEffect(t.guard, collect, REBIND_ENTITY_PROPS);
    if (t.effects) renameEntityInEffects(t.effects, collect, REBIND_ENTITY_PROPS);
  }
  for (const tick of trait.ticks ?? []) {
    if (tick.guard !== void 0) renameEntityInEffect(tick.guard, collect, REBIND_ENTITY_PROPS);
    renameEntityInEffects(tick.effects, collect, REBIND_ENTITY_PROPS);
  }
  for (const l of trait.listens ?? []) {
    if (l.guard !== void 0) renameEntityInEffect(l.guard, collect, REBIND_ENTITY_PROPS);
    if (l.payloadMapping) walkEntityInPayloadMapping(l.payloadMapping, collect, REBIND_ENTITY_PROPS);
  }
  if (trait.initialEffects) {
    renameEntityInEffects(trait.initialEffects, collect, REBIND_ENTITY_PROPS);
  }
  return collected;
}
function collectRelationTargets(field, candidates, out) {
  if (field.type === "relation" && candidates.has(field.relation.entity)) out.add(field.relation.entity);
  if ((field.type === "array" || field.type === "object") && field.items) {
    collectRelationTargets(field.items, candidates, out);
  }
  if (field.properties) {
    for (const value of Object.values(field.properties)) collectRelationTargets(value, candidates, out);
  }
}
function relationTargetsOfEntity(entity, candidates) {
  const out = /* @__PURE__ */ new Set();
  for (const field of entity.fields) collectRelationTargets(field, candidates, out);
  return out;
}
function rewriteListenSources(trait, subs, idByName, orbitalRename, alias, listenSourceTargets) {
  const listens = trait.listens;
  if (!listens || listens.length === 0) return trait;
  let changed = false;
  const nextListens = listens.map((listen) => {
    const source = listen.source;
    if (!source) return listen;
    if (source.kind === "trait") {
      const target = subs.get(source.trait);
      if (target === void 0 || target === source.trait) return listen;
      changed = true;
      const traitId = idByName.get(target);
      return {
        ...listen,
        source: { kind: "trait", trait: target, ...traitId ? { traitId } : {} }
      };
    }
    if (source.kind === "orbital") {
      if (orbitalRename && source.orbital === orbitalRename.from) {
        const targetTrait = subs.get(source.trait) ?? source.trait;
        changed = true;
        const traitId = idByName.get(targetTrait);
        return {
          ...listen,
          source: {
            kind: "orbital",
            orbital: orbitalRename.to,
            trait: targetTrait,
            ...traitId ? { traitId } : {}
          }
        };
      }
      const info = alias && listenSourceTargets ? listenSourceTargets.get(siblingImportKey(alias, source.orbital)) : void 0;
      if (info) {
        const target = info.traitSubs.get(source.trait);
        changed = true;
        return {
          ...listen,
          source: {
            kind: "orbital",
            orbital: info.localName,
            trait: target?.finalName ?? source.trait,
            ...target?.finalId ? { traitId: target.finalId } : {}
          }
        };
      }
    }
    return listen;
  });
  return changed ? { ...trait, listens: nextListens } : trait;
}
function isInlineEntity(ref) {
  return !isEntityReference(ref) && !isEntityCall(ref);
}
function inlineEntityRefsOf(orbital) {
  return [orbital.entity, ...orbital.auxiliaryEntities ?? []].filter(isInlineEntity);
}
function entityByNameForAlias(imports, alias, entityName) {
  const imported = imports.orbitals.get(alias);
  if (!imported) return void 0;
  for (const o of importedOrbitals(imported)) {
    const found = inlineEntityRefsOf(o).find((e) => e.name === entityName);
    if (found) return found;
  }
  return void 0;
}
function entityByNameAnyAlias(imports, entityName) {
  const aliases = Array.from(imports.orbitals.keys()).sort();
  for (const alias of aliases) {
    const found = entityByNameForAlias(imports, alias, entityName);
    if (found) return found;
  }
  return void 0;
}
function ownedTraitEntryFor(imports, alias, traitName) {
  const imported = imports.orbitals.get(alias);
  if (!imported) return void 0;
  const owner = importedOrbitals(imported).find(
    (o) => findTraitEntryInOrbital(o, traitName) !== null
  );
  if (!owner || !isInlineEntity(owner.entity)) return void 0;
  return { owner, ownerEntity: owner.entity, entry: findTraitEntryInOrbital(owner, traitName) };
}
function boundTraitOrbitalBinding(imports, alias, traitName) {
  const imported = imports.orbitals.get(alias);
  if (imported?.resolvedOrbitals) {
    for (const ro of imported.resolvedOrbitals) {
      const entry = findTraitEntryInOrbital(ro.original, traitName);
      if (!entry) continue;
      const bound2 = entry.linkedEntity;
      const resolvedEntities = [ro.entity, ...ro.auxiliaryEntities ?? []];
      const boundEntity2 = bound2 !== void 0 ? resolvedEntities.find((e) => e.name === bound2) : void 0;
      const requiredData2 = new Set(
        ro.traits.find((rt) => rt.trait.name === traitName)?.trait.entityContract?.requires ?? []
      );
      return { owner: ro.original, ownerEntity: ro.entity, entity: boundEntity2 ?? ro.entity, requiredData: requiredData2 };
    }
  }
  const owned = ownedTraitEntryFor(imports, alias, traitName);
  if (!owned) return void 0;
  const ownerEntities = inlineEntityRefsOf(owned.owner);
  const bound = owned.entry?.linkedEntity;
  const boundEntity = bound !== void 0 ? ownerEntities.find((e) => e.name === bound) : void 0;
  const requiredData = new Set(
    owned.entry && "stateMachine" in owned.entry ? owned.entry.entityContract?.requires ?? [] : []
  );
  return { owner: owned.owner, ownerEntity: owned.ownerEntity, entity: boundEntity ?? owned.ownerEntity, requiredData };
}
function auxEntitiesFromUnrebindTraitRefs(orbitalTraits, imports) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (e) => {
    if (!seen.has(e.name)) {
      seen.add(e.name);
      out.push(e);
    }
  };
  for (const tr of orbitalTraits) {
    let ref;
    let linkedEntity;
    if (typeof tr === "string") {
      ref = tr;
    } else if ("ref" in tr) {
      ref = tr.ref;
      linkedEntity = tr.linkedEntity;
    } else {
      continue;
    }
    const parsed = parseImportedTraitRef(ref);
    if (!parsed) continue;
    const binding = boundTraitOrbitalBinding(imports, parsed.alias, parsed.traitName);
    if (!binding) continue;
    if (linkedEntity === void 0) {
      push(binding.entity);
    }
    for (const e of inlineEntityRefsOf(binding.owner)) {
      if (e.name !== binding.ownerEntity.name) push(e);
    }
  }
  return out;
}
function rewriteSelfRelationTarget(field, oldName, newName, newId) {
  if (field.type === "relation" && field.relation.entity === oldName) {
    field.relation = { ...field.relation, entity: newName, entityId: newId };
  }
  if (field.type === "array" && field.items) {
    rewriteSelfRelationTarget(field.items, oldName, newName, newId);
  }
  if (field.type === "object" && field.items) {
    rewriteSelfRelationTarget(field.items, oldName, newName, newId);
  }
  if (field.properties) {
    for (const key of Object.keys(field.properties)) {
      rewriteSelfRelationTarget(field.properties[key], oldName, newName, newId);
    }
  }
}
function mergeImportedEntityFields(callerEntity, importedEntity, intrinsicOnly, requiredData) {
  const existingNames = new Set(
    callerEntity.fields.map((f) => f.name).filter((n) => n !== void 0)
  );
  const oldName = importedEntity.name;
  const newName = callerEntity.name;
  const newId = callerEntity.id;
  for (const field of importedEntity.fields) {
    if (intrinsicOnly && field.intrinsic !== true) continue;
    if (field.name === void 0) continue;
    if (requiredData.has(field.name) && field.intrinsic !== true) continue;
    if (existingNames.has(field.name)) continue;
    const cloned = structuredClone(field);
    rewriteSelfRelationTarget(cloned, oldName, newName, newId);
    cloned.mergedFrom = oldName;
    callerEntity.fields.push(cloned);
  }
}
function mergeImportedEntityFieldsIntoOrbital(orbitalTraits, entity, declaredAuxEntities, imports) {
  const mergedAliases = /* @__PURE__ */ new Set();
  const pending = [];
  for (const tr of orbitalTraits) {
    let ref;
    let linkedEntity;
    if (typeof tr === "string") {
      ref = tr;
    } else if ("ref" in tr) {
      ref = tr.ref;
      linkedEntity = tr.linkedEntity;
    } else {
      continue;
    }
    const parsed = parseImportedTraitRef(ref);
    if (!parsed) continue;
    const binding = boundTraitOrbitalBinding(imports, parsed.alias, parsed.traitName);
    if (!binding) continue;
    const carriesFields = linkedEntity !== void 0 ? true : binding.entity.name === entity.name;
    if (!carriesFields) continue;
    const key = `${parsed.alias}\0${parsed.traitName}`;
    if (mergedAliases.has(key)) continue;
    mergedAliases.add(key);
    pending.push({
      target: linkedEntity,
      imported: binding.entity,
      intrinsicOnly: linkedEntity === void 0,
      requiredData: binding.requiredData
    });
  }
  for (const pm of pending) {
    const auxTarget = pm.target !== void 0 && pm.target !== entity.name ? declaredAuxEntities.find((e) => e.name === pm.target) : void 0;
    mergeImportedEntityFields(auxTarget ?? entity, pm.imported, pm.intrinsicOnly, pm.requiredData);
  }
}
function consumerEntityIdsOf(orbitals) {
  const out = /* @__PURE__ */ new Map();
  for (const orbital of orbitals) {
    for (const entity of inlineEntityRefsOf(orbital)) {
      if (entity.id) out.set(entity.name, entity.id);
    }
  }
  return out;
}
function materializedPrimaryEntity(upstreamEntity, upstreamOrbitalId, ref, localName) {
  const name = ref.entity ?? `${localName}${upstreamEntity.name}`;
  const id = asEntityId(
    deriveMaterializedId(upstreamEntity.id, upstreamOrbitalId, upstreamEntity.name, localName, "entity")
  );
  return { name, id };
}
function materializedTraitSubs(upstreamId, traits, ref, localName) {
  const onlySet = ref.only ? new Set(ref.only) : void 0;
  const omitSet = new Set(ref.omit ?? []);
  const out = /* @__PURE__ */ new Map();
  for (const rt of traits) {
    const name = rt.trait.name;
    if (!name) continue;
    const keep = onlySet ? onlySet.has(name) : !omitSet.has(name);
    if (!keep) continue;
    out.set(name, {
      finalName: `${localName}${name}`,
      finalId: asTraitId(deriveMaterializedId(rt.trait.id, upstreamId, name, localName, "trait"))
    });
  }
  return out;
}
function siblingImportKey(alias, upstreamOrbitalName) {
  return `${alias}\0${upstreamOrbitalName}`;
}
function applyExtendFields(entity, extend, fieldSubs, consumerEntityIds, localName) {
  if (!extend || extend.length === 0) return { entity, errors: [] };
  const errors = [];
  const taken = new Set(entity.fields.map((f) => f.name).filter((n) => Boolean(n)));
  for (const target of fieldSubs.values()) taken.add(target);
  const added = [];
  for (const field of extend) {
    const name = field.name;
    if (!name) continue;
    if (taken.has(name)) {
      errors.push(
        `Orbital "${localName}" declares extend field "${name}" but the upstream primary entity already has a field of that name after "fields {}" renames (ORB_O_EXTEND_FIELD_COLLISION)`
      );
      continue;
    }
    taken.add(name);
    if (field.type === "relation" && !field.relation.entityId) {
      const targetId = consumerEntityIds.get(field.relation.entity);
      added.push(targetId ? { ...field, relation: { ...field.relation, entityId: targetId } } : field);
      continue;
    }
    added.push(field);
  }
  if (added.length === 0) return { entity, errors };
  return { entity: { ...entity, fields: [...entity.fields, ...added] }, errors };
}
function deriveMaterializedId(sourceId, upstreamOrbitalId, sourceName, localName, kind) {
  const parent = sourceId ?? `${idPrefix(kind)}orbital-import-seed:${upstreamOrbitalId ?? "no-orbital-id"}:${sourceName}`;
  return deriveId(parent, localName);
}
function navigatePathSegments(path) {
  return path.split("/").filter((segment) => segment.length > 0);
}
function navigateParamStrippedPrefix(path) {
  const segments = navigatePathSegments(path);
  const last = segments[segments.length - 1];
  if (last === void 0 || !last.startsWith(":")) return void 0;
  return `/${segments.slice(0, -1).join("/")}/`;
}
function rewriteConcatPrefix(literalPrefix, pathMap) {
  const normalized = literalPrefix.endsWith("/") ? literalPrefix : `${literalPrefix}/`;
  for (const [upstreamPath, finalPath] of pathMap) {
    const stripped = navigateParamStrippedPrefix(upstreamPath);
    if (stripped === void 0 || stripped !== normalized) continue;
    const finalStripped = navigateParamStrippedPrefix(finalPath);
    if (finalStripped !== void 0) return finalStripped;
  }
  return void 0;
}
function rewriteNavigateTargets(node, pathMap) {
  if (node === null || node === void 0) return node;
  if (Array.isArray(node)) {
    if (node[0] === "navigate" && node.length >= 2) {
      const target = node[1];
      if (typeof target === "string" && pathMap.has(target)) {
        return [node[0], pathMap.get(target), ...node.slice(2).map((v) => rewriteNavigateTargets(v, pathMap))];
      }
      if (Array.isArray(target) && target[0] === "str/concat" && typeof target[1] === "string" && target[1].startsWith("/")) {
        const rewrittenPrefix = rewriteConcatPrefix(target[1], pathMap);
        const nextTarget = rewrittenPrefix !== void 0 ? [target[0], rewrittenPrefix, ...target.slice(2)] : target;
        return [node[0], nextTarget, ...node.slice(2).map((v) => rewriteNavigateTargets(v, pathMap))];
      }
    }
    return node.map((item) => rewriteNavigateTargets(item, pathMap));
  }
  if (typeof node !== "object") return node;
  const next = {};
  for (const [key, value] of Object.entries(node)) {
    next[key] = rewriteNavigateTargets(value, pathMap);
  }
  return next;
}
function rewriteTraitNavigateTargets(trait, pathMap) {
  if (pathMap.size === 0) return trait;
  const rewrite = (v) => rewriteNavigateTargets(v, pathMap);
  const next = { ...trait };
  const sm = trait.stateMachine;
  if (sm) {
    next.stateMachine = {
      ...sm,
      transitions: (sm.transitions ?? []).map(
        (t) => t.effects ? { ...t, effects: rewrite(t.effects) } : t
      )
    };
  }
  if (trait.ticks) {
    next.ticks = trait.ticks.map((tick) => ({ ...tick, effects: rewrite(tick.effects) }));
  }
  if (trait.initialEffects) {
    next.initialEffects = rewrite(trait.initialEffects);
  }
  return next;
}
function rewriteConfigPagePaths(node, pathMap) {
  if (typeof node === "string") return pathMap.get(node) ?? node;
  if (Array.isArray(node)) return node.map((item) => rewriteConfigPagePaths(item, pathMap));
  if (node !== null && typeof node === "object") {
    const next = {};
    for (const [key, value] of Object.entries(node)) {
      next[key] = rewriteConfigPagePaths(value, pathMap);
    }
    return next;
  }
  return node;
}
function rewriteTraitConfigPagePaths(trait, pathMap, schemaOnlyKeys) {
  if (pathMap.size === 0 || !trait.config) return trait;
  const nextConfig = {};
  for (const [key, field] of Object.entries(trait.config)) {
    nextConfig[key] = field.default === void 0 || schemaOnlyKeys.has(key) ? field : { ...field, default: rewriteConfigPagePaths(field.default, pathMap) };
  }
  return { ...trait, config: nextConfig };
}
function foldUseConfigOverride(declared, override, context) {
  if (!override) return declared;
  const values = {};
  for (const [key, field] of Object.entries(override)) {
    if (!(key in declared)) {
      refResolverLog.warn("use-config:unknown-knob", { ...context, key });
      continue;
    }
    if (field.default !== void 0) values[key] = field.default;
  }
  return Object.keys(values).length > 0 ? overrideDeclaredKnobs(declared, values) : declared;
}
function forwardConsumerAppKnobs(folded, consumer) {
  if (!consumer) return folded;
  const forwards = {};
  for (const [key, field] of Object.entries(folded)) {
    const token = field.default;
    if (typeof token !== "string" || !token.startsWith("@config.")) continue;
    const source = token.slice("@config.".length);
    if (source.includes(".") || !(source in consumer)) continue;
    if (consumer[source].default === void 0) continue;
    forwards[key] = source;
  }
  if (Object.keys(forwards).length === 0) return folded;
  const next = { ...folded };
  for (const [key, source] of Object.entries(forwards)) {
    next[key] = { ...folded[key], default: consumer[source].default, forwardedFrom: `@config.${source}` };
  }
  return next;
}
var ReferenceResolver = class _ReferenceResolver {
  loader;
  options;
  localTraits;
  /** id-keyed mirror of `localTraits`, populated wherever the trait carries an `id`. */
  localTraitsById = /* @__PURE__ */ new Map();
  /** Import scope of each loaded source orbital, keyed by its source path. */
  sourceImportsCache = /* @__PURE__ */ new Map();
  /**
   * {@link preResolveImportFile}'s cache, keyed by absolute source path —
   * `null` for a file whose own pre-resolution failed (best-effort, cached
   * too, so a repeat import doesn't retry a doomed resolve). Distinct from
   * {@link sourceImportsCache} (raw import SCOPE, no trait resolution) —
   * this holds the file's fully RESOLVED orbitals.
   */
  importFileResolveCache = /* @__PURE__ */ new Map();
  loaderInitialized = false;
  /** The schema's declared `config {}` — outermost rung of the forwarded-config chain (§4.5). */
  schemaConfig;
  /**
   * Schema-wide CONSUMER entity NAME → id, growing as each of THIS
   * schema's own orbitals finishes resolving. JS twin of the compiled
   * path's `consumer_entity_ids` (`orbital-compiler/src/phases/inline/
   * mod.rs`), extended the same way: once per orbital, right after it
   * resolves. Extended ONLY by {@link noteResolvedEntityIds} — called by
   * the free function {@link resolveSchema} after each of ITS OWN orbitals
   * finishes — never automatically inside `resolve()` itself, so the
   * nested `this.resolve()` call {@link materializeOrbitalRef} makes for an
   * external upstream orbital (a DIFFERENT file, never this consumer
   * schema) cannot pollute it.
   */
  schemaEntityIds = /* @__PURE__ */ new Map();
  /**
   * Entity NAME → id visible to the orbital CURRENTLY being resolved:
   * {@link schemaEntityIds} (every name an EARLIER orbital in this schema
   * already established) plus the current orbital's own declared primary +
   * auxiliary entities — JS twin of the compiled path's per-orbital `entity_
   * ids = consumer_entity_ids.clone(); entity_ids.extend(collect_orbital_
   * entity_ids(orbital))`, timed the same way (before this orbital's own
   * trait composition runs). Read by {@link applyLinkedEntityRename}'s call
   * sites so a call-site `-> Entity` rebind can REFRESH (never merely drop)
   * the trait's `entityRefIds` side-map when the rebind target's id is
   * already known — the gap `std-api-gateway` exposed: `RouteOrbital`'s
   * no-rebind `Audit.traits.AuditCaptureListener` pull establishes
   * `AuditEntry`'s id; `GatewayUserOrbital`, resolved LATER in the same
   * schema, rebinds `Browse.traits.BrowseItemBrowse -> AuditEntry` and must
   * see it. `resolve()` re-sets this fresh at its own entry (no restore
   * needed once it returns): nothing reads it once `resolve()` has
   * returned, and no `resolve()` call is ever active while another is on
   * this instance — the only recursive `this.resolve()` call
   * (`materializeOrbitalRef`'s upstream re-resolve) runs strictly BEFORE
   * `resolveSchema`'s own per-orbital loop begins, never nested inside it.
   */
  entityIdsInScope = /* @__PURE__ */ new Map();
  /**
   * Per-orbital name → the FINAL trait names {@link pullSiblingTraits}
   * auto-pulled for it (`ResolvedOrbital.name` keyed, set once per `resolve()`
   * call). JS twin of Rust's `pulled_by_orbital` (`inline/mod.rs`) — read by
   * {@link uniquifyCrossOrbitalPulledSiblings}, run once by the free
   * function {@link resolveSchema} AFTER every orbital in the schema has
   * resolved, because the rename decision (does this pulled name collide
   * with ANOTHER orbital's own declared trait name?) needs the WHOLE
   * schema's roster, not just this orbital's — exactly why Rust's own
   * `uniquify_cross_orbital_pulled_siblings` runs once, after its per-orbital
   * inline loop, not inside `inline_orbital` itself.
   */
  pulledTraitNamesByOrbital = /* @__PURE__ */ new Map();
  /**
   * Seed {@link schemaEntityIds} with EVERY orbital's PRIMARY (+ any
   * already-declared auxiliary) entity name → id, from the RAW pre-resolve
   * schema — JS twin of the compiled path's `consumer_entity_ids` INITIAL
   * build (`orbital-compiler/src/phases/inline/mod.rs`, `schema.orbitals
   * .iter().flat_map(...)`), which scans ALL orbitals' primaries up front,
   * before ANY of them inlines — not just the ones resolved so far. Without
   * this, an EARLY-resolved orbital's own `entityRefIds` secondary
   * references to a LATER orbital's primary entity (`RouteOrbital`'s
   * `entityRefIds["RateBucket"]`, `RateBucket` being a DIFFERENT, not-yet-
   * resolved orbital's own primary in `std-api-gateway`) would see nothing
   * in {@link entityIdsInScope} and {@link consumerKnownEntityRebindSubs}
   * would never refresh the stale atom-baked id. Called by {@link
   * resolveSchema} ONCE, before its per-orbital loop begins; {@link
   * noteResolvedEntityIds} then keeps widening past this seed with each
   * orbital's post-resolve auxiliary discoveries (Gap #22, sibling pulls),
   * same as before.
   */
  seedSchemaEntityIds(orbitals) {
    const next = new Map(this.schemaEntityIds);
    for (const orbital of orbitals) {
      for (const e of inlineEntityRefsOf(orbital)) {
        if (e.id) next.set(e.name, e.id);
      }
    }
    this.schemaEntityIds = next;
  }
  /**
   * Extend {@link schemaEntityIds} with a just-resolved orbital's FINAL
   * entity set — primary + every auxiliary entity resolution carried in
   * (including a no-rebind sibling pull's own bound entity,
   * `ResolvedOrbital.auxiliaryEntities`). Mirrors the compiled path's
   * `consumer_entity_ids.extend(collect_orbital_entity_ids(orbital))`,
   * called the same way: once per orbital, immediately after it resolves.
   * Called by {@link resolveSchema}'s own per-orbital loop only.
   */
  noteResolvedEntityIds(resolved) {
    const next = new Map(this.schemaEntityIds);
    if (resolved.entity.id) next.set(resolved.entity.name, resolved.entity.id);
    for (const aux of resolved.auxiliaryEntities ?? []) {
      if (aux.id) next.set(aux.name, aux.id);
    }
    this.schemaEntityIds = next;
  }
  /**
   * Make auto-pulled sibling traits app-unique across orbitals — JS twin of
   * Rust's `uniquify_cross_orbital_pulled_siblings` (`inline/mod.rs`), called
   * by the free function {@link resolveSchema} once every orbital in the
   * schema has resolved (mirroring Rust's own post-per-orbital-loop timing:
   * the rename decision needs the WHOLE schema's trait-name roster, built
   * fresh here rather than grown incrementally like {@link schemaEntityIds}).
   * An auto-pulled sibling keeps its source name within its OWN orbital, so a
   * `@trait.<Sibling>` reference inside that orbital still resolves — but
   * when TWO orbitals in the same schema each declare (pulled OR explicit) a
   * trait of the SAME name, that name collides app-wide. Only renames a
   * PULLED copy (never an explicitly-authored trait ref —
   * {@link pulledTraitNamesByOrbital} is the eligibility set) whose name is
   * ALSO declared by at least one OTHER orbital, prefixing it with the
   * owning orbital's own name; the prefixed name must itself be free
   * schema-wide or the rename is skipped (matches Rust: a collision on the
   * PREFIXED name is left for the validator, never silently double-renamed).
   * Mutates `orbitals`' traits in place, same mutation style as
   * {@link pullSiblingTraits}.
   */
  uniquifyCrossOrbitalPulledSiblings(orbitals) {
    if (this.pulledTraitNamesByOrbital.size === 0) return;
    const nameOrbitalCount = /* @__PURE__ */ new Map();
    for (const orbital of orbitals) {
      const local = new Set(
        orbital.traits.map((rt) => rt.trait.name).filter((n) => Boolean(n))
      );
      for (const name of local) {
        nameOrbitalCount.set(name, (nameOrbitalCount.get(name) ?? 0) + 1);
      }
    }
    for (const orbital of orbitals) {
      const pulledNames = this.pulledTraitNamesByOrbital.get(orbital.name);
      if (!pulledNames || pulledNames.size === 0) continue;
      const renames = /* @__PURE__ */ new Map();
      for (const oldName of pulledNames) {
        if ((nameOrbitalCount.get(oldName) ?? 0) <= 1) continue;
        const newName = `${orbital.name}${oldName}`;
        if (nameOrbitalCount.has(newName)) continue;
        renames.set(oldName, newName);
      }
      if (renames.size === 0) continue;
      for (const rt of orbital.traits) {
        if (rt.trait.name && renames.has(rt.trait.name)) {
          rt.trait = { ...rt.trait, name: renames.get(rt.trait.name) };
        }
      }
      const idByName = /* @__PURE__ */ new Map();
      for (const rt of orbital.traits) {
        if (rt.trait.name) idByName.set(rt.trait.name, rt.trait.id);
      }
      for (const rt of orbital.traits) {
        rt.trait = renameTraitEmbeds(rt.trait, renames);
        rt.trait = rewriteListenSources(rt.trait, renames, idByName);
      }
    }
  }
  constructor(options) {
    this.options = options;
    this.loader = options.loader;
    this.schemaConfig = options.schemaConfig;
    this.localTraits = options.localTraits ?? /* @__PURE__ */ new Map();
    for (const trait of this.localTraits.values()) {
      if (trait.id) {
        this.localTraitsById.set(trait.id, trait);
      }
    }
  }
  async ensureLoader() {
    if (this.loader || this.loaderInitialized) return;
    this.loaderInitialized = true;
    try {
      const { ExternalOrbitalLoader } = await import('./external-loader-CISUEINQ.js');
      this.loader = new ExternalOrbitalLoader(this.options);
    } catch {
    }
  }
  /**
   * Resolve all references in an orbital.
   */
  async resolve(orbital, sourcePath, chain, opts) {
    const errors = [];
    const warnings = [];
    const importChain = chain ?? { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    this.entityIdsInScope = new Map([...this.schemaEntityIds, ...inlineEntityRefsOf(orbital).flatMap((e) => e.id ? [[e.name, e.id]] : [])]);
    const traitsList = orbital.traits ?? [];
    const alreadyResolved = traitsList.length > 0 && traitsList.every((t) => isInlineTrait(t));
    const importsResult = alreadyResolved ? { success: true, data: { orbitals: /* @__PURE__ */ new Map(), idIndex: /* @__PURE__ */ new Map() }} : await this.resolveImports(orbital.uses ?? [], sourcePath, importChain);
    if (!importsResult.success) {
      return { success: false, errors: importsResult.errors };
    }
    const imports = importsResult.data;
    imports.idIndex = buildIdIndex(orbital, imports.orbitals);
    const gap22AuxEntities = auxEntitiesFromUnrebindTraitRefs(orbital.traits ?? [], imports);
    if (gap22AuxEntities.length > 0) {
      const next = new Map(this.entityIdsInScope);
      for (const e of gap22AuxEntities) {
        if (e.id && !next.has(e.name)) next.set(e.name, e.id);
      }
      this.entityIdsInScope = next;
    }
    if (isInlineEntity(orbital.entity)) {
      mergeImportedEntityFieldsIntoOrbital(
        orbital.traits ?? [],
        orbital.entity,
        (orbital.auxiliaryEntities ?? []).filter(isInlineEntity),
        imports
      );
    }
    const entityResult = this.resolveEntity(orbital.entity, imports);
    if (!entityResult.success) {
      errors.push(...entityResult.errors);
    }
    const traitsResult = await this.resolveTraits(orbital.traits, imports, importChain, orbital.config);
    if (!traitsResult.success) {
      errors.push(...traitsResult.errors);
    }
    const pagesResult = await this.resolvePages(orbital.pages, imports, importChain);
    if (!pagesResult.success) {
      errors.push(...pagesResult.errors);
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    if (!entityResult.success || !traitsResult.success || !pagesResult.success) {
      return { success: false, errors: ["Internal error: unexpected failure state"] };
    }
    const { errors: pullErrors, auxEntities: pulledAuxEntities, pulledNames } = await this.pullSiblingTraits(
      traitsResult.data,
      imports,
      importChain,
      orbital.config
    );
    if (pullErrors.length > 0) {
      return { success: false, errors: pullErrors };
    }
    this.pulledTraitNamesByOrbital.set(orbital.name, pulledNames);
    const known = /* @__PURE__ */ new Set([entityResult.data.entity.name]);
    const declaredAux = (orbital.auxiliaryEntities ?? []).filter(isInlineEntity);
    for (const e of declaredAux) known.add(e.name);
    const combined = [...declaredAux];
    for (const e of [...gap22AuxEntities, ...pulledAuxEntities]) {
      if (!known.has(e.name)) {
        known.add(e.name);
        combined.push(e);
      }
    }
    const resolvedAuxEntities = combined.length > 0 ? combined : void 0;
    for (const resolvedTrait of traitsResult.data) {
      resolvedTrait.trait = resolveEntityTokensById(resolvedTrait.trait, imports.idIndex);
      resolvedTrait.trait = resolveConfigRefsById(resolvedTrait.trait, imports.idIndex);
    }
    try {
      spliceLambdaTraitRefs(traitsResult.data, pagesResult.data);
    } catch (e) {
      if (e instanceof LambdaSpliceError) {
        return { success: false, errors: [e.message] };
      }
      throw e;
    }
    if (!opts?.skipTypeParamSentinels) {
      resolveOrbitalTypeParamSentinels(
        traitsResult.data,
        entityResult.data.entity,
        resolvedAuxEntities ?? [],
        orbital.types
      );
    }
    return {
      success: true,
      data: {
        name: orbital.name,
        entity: entityResult.data.entity,
        entitySource: entityResult.data.source,
        traits: traitsResult.data,
        ...resolvedAuxEntities ? { auxiliaryEntities: resolvedAuxEntities } : {},
        pages: pagesResult.data,
        imports,
        original: orbital
      },
      warnings
    };
  }
  /**
   * Pre-resolve one imported FILE's own orbitals to completion — its own
   * `uses` (recursively, through THIS same method), its own trait refs,
   * its own sibling pulls, its own entity-ref-id rebinds — BEFORE the
   * consumer that names this alias ever folds or pulls from it. JS twin
   * of Rust's per-`uses` recursive `inline_orbital` call (`orbital-
   * compiler/src/phases/inline/mod.rs:1001-1013`): every `uses` alias's
   * OWN orbitals are inlined to completion, ahead of `ctx.add_alias_multi`
   * registering the alias for the consumer's own lookups. Without this, a
   * sibling-pull collision that is a genuine top-level PEER collision
   * INSIDE the imported file (`std-approval-request.orb`'s
   * `InlineBrowseItemBrowse6`/`…10`, both `ref: Dense.traits.
   * BrowseItemBrowse`, each embedding `@trait.DataGrid1`) never becomes
   * visible as a peer collision to the CONSUMER's own {@link
   * pullSiblingTraits} call — it arrives already flattened one level
   * deeper, sharing one JS-visible owner (see that method's own
   * drain-order doc for the full trace this ports).
   *
   * Cached per absolute `sourcePath` ({@link importFileResolveCache}) —
   * "a file imported twice resolves once" — so the cache must not bake in
   * any ONE call site's `uses { config }` override: the child resolver
   * below is seeded with this FILE's own raw, un-folded `config {}`
   * (`fileSchemaConfig`), never a folded override. A genuinely
   * call-site-configured explicit ref (`Alias.traits.X { config: {…} }`)
   * still resolves through the pre-existing lazy path
   * ({@link resolveTraitEntry} / {@link resolveTraitRefString}), which
   * this cache does not touch.
   *
   * Runs on a FRESH child `ReferenceResolver`, never `this` — `resolve()`'s
   * own `entityIdsInScope` / `schemaEntityIds` / `pulledTraitNamesByOrbital`
   * are single mutable instance state scoped to ONE active `resolve()` call
   * tree (see {@link entityIdsInScope}'s own doc: "no `resolve()` call is
   * ever active while another is on this instance"); reusing `this` here
   * would nest a `resolve()` call inside the CONSUMER's own still-active
   * `resolve()` → `resolveImports` call and corrupt that state once this
   * nested call returns. The child reuses `this.loader` (and therefore its
   * underlying `LoaderCache`) so raw parsing still dedupes exactly as
   * before. `chain` is threaded through unchanged (not cloned, matching
   * every other cross-call `chain` hand-off in this file) so genuine `uses`
   * cycles are still caught by the loader.
   *
   * Mirrors {@link resolveSchema}'s own orchestration (flatten orbital
   * references → collect local traits → seed schema entity ids →
   * per-orbital `resolve()` → `uniquifyCrossOrbitalPulledSiblings`) rather
   * than calling that free function directly — it accepts no `chain`
   * parameter, so a direct call would silently drop cycle protection one
   * level in.
   *
   * Best-effort: returns `null` (never throws) on any failure — a caller
   * falls back to the pre-existing raw-ref walk, matching this file's
   * other best-effort pre-passes (e.g. {@link precomputeReferenceFormPrimaryIds}).
   */
  async preResolveImportFile(fileOrbitals, sourcePath, fileSchemaConfig, chain) {
    const cached = this.importFileResolveCache.get(sourcePath);
    if (cached !== void 0) return cached;
    let result;
    try {
      result = await this.preResolveImportFileUncached(fileOrbitals, sourcePath, fileSchemaConfig, chain);
    } catch {
      result = null;
    }
    this.importFileResolveCache.set(sourcePath, result);
    return result;
  }
  async preResolveImportFileUncached(fileOrbitals, sourcePath, fileSchemaConfig, chain) {
    const child = new _ReferenceResolver({
      ...this.options,
      loader: this.loader,
      schemaConfig: fileSchemaConfig
    });
    const fileSchema = {
      name: sourcePath,
      orbitals: fileOrbitals,
      ...fileSchemaConfig ? { config: fileSchemaConfig } : {}
    };
    const flattenResult = await child.resolveOrbitalImports(fileSchema, { skipTypeParamSentinels: true });
    if (!flattenResult.success) return null;
    const orbitals = flattenResult.data;
    const inlineTraits = orbitals.flatMap(
      (o) => o.traits.filter((t) => typeof t !== "string" && "stateMachine" in t)
    );
    child.addLocalTraits(inlineTraits);
    child.seedSchemaEntityIds(orbitals);
    const resolved = [];
    for (const orbital of orbitals) {
      const result = await child.resolve(orbital, sourcePath, chain, { skipTypeParamSentinels: true });
      if (!result.success) return null;
      resolved.push(result.data);
      child.noteResolvedEntityIds(result.data);
    }
    return resolved;
  }
  /**
   * Resolve `uses` declarations to loaded orbitals.
   */
  async resolveImports(uses, sourcePath, chain) {
    const errors = [];
    const orbitals = /* @__PURE__ */ new Map();
    if (this.options.skipExternalLoading) {
      return {
        success: true,
        data: { orbitals, idIndex: /* @__PURE__ */ new Map() },
        warnings: ["External loading skipped"]
      };
    }
    const effectiveChain = chain ?? { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    for (const use of uses) {
      if (orbitals.has(use.as)) {
        errors.push(`Duplicate import alias: ${use.as}`);
        continue;
      }
      await this.ensureLoader();
      if (!this.loader) {
        errors.push(`No loader available to resolve import: ${use.from}`);
        continue;
      }
      const loadResult = await this.loader.loadOrbital(
        use.from,
        void 0,
        sourcePath,
        effectiveChain
      );
      if (!loadResult.success) {
        errors.push(`Failed to load "${use.from}" as "${use.as}": ${loadResult.error}`);
        continue;
      }
      const fileOrbitals = loadResult.data.orbitals ?? [loadResult.data.orbital];
      const resolvedOrbitals = await this.preResolveImportFile(
        fileOrbitals,
        loadResult.data.sourcePath,
        loadResult.data.schemaConfig,
        effectiveChain
      );
      orbitals.set(use.as, {
        alias: use.as,
        from: use.from,
        orbital: loadResult.data.orbital,
        orbitals: fileOrbitals,
        sourcePath: loadResult.data.sourcePath,
        ...resolvedOrbitals ? { resolvedOrbitals } : {},
        ...loadResult.data.schemaConfig ? {
          schemaConfig: forwardConsumerAppKnobs(
            foldUseConfigOverride(loadResult.data.schemaConfig, use.config, use),
            this.schemaConfig
          )
        } : {}
      });
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: { orbitals, idIndex: /* @__PURE__ */ new Map() }, warnings: [] };
  }
  /**
   * Resolve entity reference.
   */
  resolveEntity(entityRef, imports) {
    if (isEntityCall(entityRef)) {
      const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
      return {
        success: true,
        data: {
          entity: {
            name: fallbackName,
            fields: entityRef.fields ?? [],
            ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
            ...entityRef.collection ? { collection: entityRef.collection } : {}
          }
        },
        warnings: []
      };
    }
    if (!isEntityReference(entityRef)) {
      return {
        success: true,
        data: { entity: entityRef },
        warnings: []
      };
    }
    const parsed = parseEntityRef(entityRef);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid entity reference format: ${entityRef}. Expected "Alias.entity"`]
      };
    }
    const imported = imports.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in entity reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    const importedEntity = this.getEntityFromOrbital(imported.orbital);
    if (!importedEntity) {
      return {
        success: false,
        errors: [
          `Imported orbital "${parsed.alias}" does not have an inline entity. Entity references cannot be chained.`
        ]
      };
    }
    const persistence = importedEntity.persistence ?? "persistent";
    return {
      success: true,
      data: {
        entity: importedEntity,
        source: {
          alias: parsed.alias,
          persistence
        }
      },
      warnings: []
    };
  }
  /**
   * Get the entity from an orbital (handling EntityRef).
   */
  getEntityFromOrbital(orbital) {
    const entityRef = orbital.entity;
    if (typeof entityRef === "string") {
      return null;
    }
    if (isEntityCall(entityRef)) {
      const fallbackName = entityRef.name ?? entityRef.extends.replace(/\.entity$/, "");
      return {
        name: fallbackName,
        fields: entityRef.fields ?? [],
        ...entityRef.persistence ? { persistence: entityRef.persistence } : {},
        ...entityRef.collection ? { collection: entityRef.collection } : {}
      };
    }
    return entityRef;
  }
  /**
   * The ATOM's OWN trait `alias.traits.traitName` resolves to — no call-site
   * overrides applied (`callSiteConfig` omitted) — so its DECLARED
   * `linkedEntity` can drive {@link buildAliasEntityRenames}. Same lookup
   * `resolveTraitRefString`'s imported branch performs to find the base
   * `trait` before any rebind, factored out so the pre-pass and the main
   * resolve share one lookup rather than two. `null` when the alias or
   * trait cannot be found — the pre-pass simply skips that entry, same as a
   * lookup failure anywhere else in this best-effort pass.
   */
  async atomOwnTrait(alias, traitName, imports, chain) {
    const imported = imports.orbitals.get(alias);
    if (!imported) return null;
    for (const o of importedOrbitals(imported)) {
      const entry = findTraitEntryInOrbital(o, traitName);
      if (!entry) continue;
      const entryResolved = await this.resolveTraitEntry(imported, entry, traitName, chain, imports);
      return entryResolved.success ? entryResolved.data.trait : null;
    }
    return null;
  }
  /**
   * Alias-scoped cross-trait entity-rebind propagation — JS twin of Rust's
   * `alias_entity_renames` pre-pass (`inline/trait.rs::inline_traits`). One
   * explicit pull's `linkedEntity` override (`Button.traits.ButtonRender ->
   * Route`) establishes a rebind (the atom's OWN declared `linkedEntity` →
   * the override) that a SIBLING pull from the SAME alias with NO rebind of
   * its own (`Button.traits.ButtonRender`, no `->`) never sees through
   * {@link applyLinkedEntityRename} — that call only ever knows ITS OWN
   * delta. But the no-rebind pull's `entityRefIds` side-map can still carry
   * a SECONDARY, positional reference to that same atom-local name (a
   * "self" entry keyed by the atom's own bound entity), which nothing else
   * rewrites — `std-api-gateway`'s `RouteOrbital.InlineButtonRender3`
   * (`Button.traits.ButtonRender`, no rebind) recovers `entityRefIds`'
   * `ButtonItem` key to `Route` only because `InlineButtonRender9` (`Button.
   * traits.ButtonRender -> Route`) established the rebind earlier in the
   * SAME orbital's `traits` list. Scanned once per orbital, over every
   * EXPLICIT `Alias.traits.X -> Y` entry in `traitRefs`: `(alias, atom's
   * declared linkedEntity) -> override`. First rebind for a given `(alias,
   * atomLocal)` pair wins (`Map.set` only on a fresh key), matching Rust's
   * `.or_insert_with`.
   */
  async buildAliasEntityRenames(traitRefs, imports, chain) {
    const out = /* @__PURE__ */ new Map();
    for (const traitRef of traitRefs) {
      if (typeof traitRef === "string" || "stateMachine" in traitRef || !("ref" in traitRef)) continue;
      const refObj = traitRef;
      if (!refObj.linkedEntity) continue;
      const parsed = parseImportedTraitRef(refObj.ref);
      if (!parsed) continue;
      const atomTrait = await this.atomOwnTrait(parsed.alias, parsed.traitName, imports, chain);
      const atomLocal = atomTrait?.linkedEntity;
      if (!atomLocal || atomLocal === refObj.linkedEntity) continue;
      const forAlias = out.get(parsed.alias) ?? /* @__PURE__ */ new Map();
      if (!forAlias.has(atomLocal)) forAlias.set(atomLocal, refObj.linkedEntity);
      out.set(parsed.alias, forAlias);
    }
    return out;
  }
  /**
   * Resolve trait references.
   */
  async resolveTraits(traitRefs, imports, chain, orbitalConfig) {
    const errors = [];
    const resolved = [];
    const embedCtx = {
      traitRefs,
      embedGraph: buildEmbedGraph(traitRefs),
      orbitalConfig
    };
    const aliasEntityRenames = await this.buildAliasEntityRenames(traitRefs, imports, chain);
    for (const traitRef of traitRefs) {
      const result = await this.resolveTraitRef(traitRef, imports, chain, embedCtx, aliasEntityRenames);
      if (!result.success) {
        errors.push(...result.errors);
      } else {
        resolved.push(result.data);
      }
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: resolved, warnings: [] };
  }
  /**
   * The import scope of an already-loaded orbital, memoised per source path.
   * A sibling that is itself a ref names its target through the SOURCE atom's
   * aliases, so it can only be resolved against those.
   */
  async importsOfSource(imported, chain) {
    const key = imported.sourcePath ?? `${imported.alias}:${imported.from}`;
    const cached = this.sourceImportsCache.get(key);
    if (cached) return cached;
    const result = await this.resolveImports(
      imported.orbital.uses ?? [],
      imported.sourcePath,
      chain
    );
    if (!result.success) return null;
    result.data.idIndex = buildIdIndex(imported.orbital, result.data.orbitals);
    this.sourceImportsCache.set(key, result.data);
    return result.data;
  }
  /**
   * The concrete base `Trait` a trait entry (found under `localName` in
   * `imported`) resolves to — an inline entry resolves its own
   * `@config.<knob>` emit refs against `callSiteConfig` (the ref hop that
   * led here, e.g. `std-approval-gate`'s `CloseButton = Button.traits.
   * ButtonRender { config: { action: CLOSE } }` resolving `ButtonRender`
   * itself); a ref-shaped entry (an atom's sub-view declared `trait X =
   * Y.traits.Z {…}` INSIDE the source atom, e.g. `std-note`'s
   * `NoteSubpages = RecordDetail.traits.RecordItemDetail`) recurses through
   * that source's own import scope via {@link importsOfSource} — the
   * entry's own declared overrides are its call site. Single owner for
   * `pullSiblingTraits`' sibling pull and `resolveTraitRefString`'s
   * imported branch, both of which used to reach `findTraitInOrbital`'s
   * `stateMachine`-gated lookup (its ref arm did nothing), silently failing
   * to resolve a nested ref (ledger (i)).
   *
   * `callSiteConfig` MUST be threaded by any caller that goes on to fold a
   * call-site config of its own onto the returned trait
   * (`resolveTraitRefString`'s ref-imported branch, ledger (o)) — the
   * `@config.<knob>` emit-name substitution is destructive
   * (`resolveConfigRefEmitNames` rewrites the marker to a concrete literal
   * in place), so resolving it here with the WRONG effective config
   * (the atom's own bare default, absent the override) permanently loses
   * the override: a second `resolveConfigRefEmitNames` call downstream sees
   * no more `@config.<knob>` marker to substitute and silently no-ops.
   * `pullSiblingTraits` (no further fold of its own) omits it, matching its
   * prior no-callSiteConfig behavior exactly.
   */
  async resolveTraitEntry(imported, entry, localName, chain, homeImports, callSiteConfig) {
    if ("stateMachine" in entry) {
      const { trait, errors } = resolveConfigRefEmitNames(entry, callSiteConfig);
      if (errors.length > 0) return { success: false, errors };
      return {
        success: true,
        data: { trait, embedScope: { imports: homeImports, alias: imported.alias } },
        warnings: []
      };
    }
    const refObj = entry;
    const srcImports = await this.importsOfSource(imported, chain);
    if (!srcImports) {
      return {
        success: false,
        errors: [
          `Trait "${localName}" in imported behavior could not resolve its own import scope to follow ref "${refObj.ref}"`
        ]
      };
    }
    const nested = await this.resolveTraitRefString(
      refObj.ref,
      srcImports,
      chain,
      refObj.config,
      refObj.linkedEntity,
      refObj.name ?? localName,
      refObj.events,
      refObj.listens,
      refObj.refId,
      refObj.typeArgs
    );
    if (!nested.success) return nested;
    return {
      success: true,
      data: {
        trait: nested.data.trait,
        embedScope: nested.data.embedScope ?? { imports: srcImports, alias: imported.alias },
        ...nested.data.typeArgs !== void 0 ? { typeArgs: nested.data.typeArgs } : {}
      },
      warnings: nested.warnings
    };
  }
  /**
   * The concrete `Page` a page entry (found under `localName` in `imported`)
   * resolves to — an inline `Page` passes through; a `PageRefObject` (a
   * re-exported page declared inside the SOURCE orbital's own `pages {}`,
   * {@link findPageEntryInOrbital}'s ref arm) recurses through that source's
   * own import scope via {@link importsOfSource} and
   * {@link resolvePageRefObject} — the page twin of {@link resolveTraitEntry}
   * (nested refs, ledger (i)).
   */
  async resolvePageEntry(imported, entry, chain) {
    if (!("ref" in entry)) return { success: true, data: { page: entry }, warnings: [] };
    const srcImports = await this.importsOfSource(imported, chain);
    if (!srcImports) {
      return {
        success: false,
        errors: [`Page in imported behavior could not resolve its own import scope to follow ref "${entry.ref}"`]
      };
    }
    const nested = await this.resolvePageRefObject(entry, srcImports, chain);
    if (!nested.success) return nested;
    return { success: true, data: { page: nested.data.page }, warnings: nested.warnings };
  }
  /**
   * Sibling-trait auto-pull — the JS twin of the compiler's
   * `phases/inline/trait.rs` pass, appending to `resolved` in place.
   *
   * An atom's main trait embeds its own sub-views as `@trait.<Sibling>` string
   * literals (`std-browse`'s `bodyContent: {children: [@trait.DataGrid1]}`).
   * A consumer that imports only the main trait never instantiates those
   * siblings, so the token dangles: no state machine, no fetch, an empty list
   * where the grid should be. The compiled path materialises them; without this
   * pass the interpreted path did not, so a raw `.orb` handed straight to
   * `OrbitalServerRuntime` rendered a different app than the same `.orb` run
   * through `orbital resolve`.
   *
   * Pulls are keyed per EMBEDDER (`owner` = the top-level consumer trait that
   * started the chain), not per atom. Two rebinds of one atom in one orbital
   * (`ChannelRail -> Channel` and `ChatThread -> ChatMessage`, both std-browse)
   * carry different entity rebinds and different hosts to listen to, so one
   * shared copy can only ever serve one of them. The first owner keeps the
   * source name; later owners pull under `<Owner><Sibling>` — unique by
   * construction, since owner names are unique within the orbital.
   */
  async pullSiblingTraits(resolved, imports, chain, orbitalConfig) {
    const auxEntities = [];
    const work = [];
    const ownerSubs = /* @__PURE__ */ new Map();
    const subsFor = (owner) => {
      let m = ownerSubs.get(owner);
      if (!m) {
        m = /* @__PURE__ */ new Map();
        ownerSubs.set(owner, m);
      }
      return m;
    };
    for (const rt of resolved) {
      if (rt.source.type !== "imported" || !rt.trait.name) continue;
      const owner = rt.trait.name;
      subsFor(owner).set(rt.source.traitName, owner);
      for (const sibling of traitEmbedNamesOf(rt.trait)) {
        work.push({
          alias: rt.source.alias,
          fallback: rt.embedScope,
          sibling,
          linkedEntity: rt.linkedEntity,
          parent: owner,
          owner,
          // Top-level SEED — always starts with the raw-walk-first gate
          // (see {@link PullItem.preResolvedScope}'s own doc).
          preResolvedScope: void 0
        });
      }
    }
    if (work.length === 0) return { errors: [], auxEntities: [], pulledNames: /* @__PURE__ */ new Set() };
    const errors = [];
    const consumerDeclared = new Set(resolved.map((r) => r.trait.name).filter(Boolean));
    const consumerDeclaredProvenance = /* @__PURE__ */ new Map();
    for (const rt of resolved) {
      if (rt.source.type === "imported" && rt.trait.name) {
        consumerDeclaredProvenance.set(rt.trait.name, { alias: rt.source.alias, traitName: rt.source.traitName });
      }
    }
    const seen = new Set(consumerDeclared);
    const visited = /* @__PURE__ */ new Set();
    const pulledAs = /* @__PURE__ */ new Map();
    const ownerOf = /* @__PURE__ */ new Map();
    const parentOf = /* @__PURE__ */ new Map();
    const parentRewrites = /* @__PURE__ */ new Map();
    const pulled = [];
    const embedderChain2 = (finalName) => {
      const chain2 = [];
      const seenNames = /* @__PURE__ */ new Set();
      let cur = finalName;
      while (cur !== void 0 && !seenNames.has(cur)) {
        seenNames.add(cur);
        const t = resolved.find((r) => r.trait.name === cur)?.trait ?? pulled.find((r) => r.trait.name === cur)?.trait;
        if (!t) break;
        if (t.config) chain2.push(t.config);
        cur = parentOf.get(cur);
      }
      return chain2;
    };
    const noteRewrite = (parent, from, to) => {
      let m = parentRewrites.get(parent);
      if (!m) {
        m = /* @__PURE__ */ new Map();
        parentRewrites.set(parent, m);
      }
      m.set(from, to);
    };
    for (let item = work.pop(); item !== void 0; item = work.pop()) {
      const { alias, fallback, sibling, linkedEntity, parent, owner, preResolvedScope } = item;
      const pullKey = `${alias} ${sibling} ${owner} ${parent}`;
      const existing = pulledAs.get(pullKey);
      if (existing !== void 0) {
        if (existing !== sibling) noteRewrite(parent, sibling, existing);
        continue;
      }
      let finalName = sibling;
      if (seen.has(sibling)) {
        const provenance = consumerDeclaredProvenance.get(sibling);
        if (consumerDeclared.has(sibling) && provenance?.alias === alias && provenance.traitName === sibling) {
          continue;
        }
        finalName = `${parent}${sibling}`;
        if (seen.has(finalName)) continue;
      }
      if (visited.has(pullKey)) continue;
      visited.add(pullKey);
      let searchImports = imports;
      let searchAlias = alias;
      let atomTrait;
      let childScope;
      let foundTypeArgs;
      let childPreResolvedScope;
      if (preResolvedScope) {
        const found = preResolvedScope.traits.find((rt) => rt.trait.name === sibling);
        if (found) {
          atomTrait = found.trait;
          childScope = { imports: searchImports, alias: searchAlias };
          foundTypeArgs = found.typeArgs;
          childPreResolvedScope = preResolvedScope;
        }
      }
      if (!atomTrait) {
        let imported = imports.orbitals.get(alias);
        let atomEntry = null;
        let atomOrbitalIndex = -1;
        if (imported) {
          const list = importedOrbitals(imported);
          for (let i = 0; i < list.length; i++) {
            atomEntry = findTraitEntryInOrbital(list[i], sibling);
            if (atomEntry) {
              atomOrbitalIndex = i;
              break;
            }
          }
        }
        if (!atomEntry && fallback) {
          const fbImported = fallback.imports.orbitals.get(fallback.alias);
          if (fbImported) {
            const list = importedOrbitals(fbImported);
            for (let i = 0; i < list.length; i++) {
              atomEntry = findTraitEntryInOrbital(list[i], sibling);
              if (atomEntry) {
                imported = fbImported;
                searchImports = fallback.imports;
                searchAlias = fallback.alias;
                atomOrbitalIndex = i;
                break;
              }
            }
          }
        }
        if (!imported || !atomEntry) continue;
        const preResolvedOrbital = imported.resolvedOrbitals?.[atomOrbitalIndex];
        const preResolved = preResolvedOrbital?.traits.find((rt) => rt.trait.name === sibling);
        if (preResolved) {
          atomTrait = preResolved.trait;
          childScope = { imports: searchImports, alias: searchAlias };
          foundTypeArgs = preResolved.typeArgs;
          childPreResolvedScope = preResolvedOrbital;
        } else {
          const entryResolved = await this.resolveTraitEntry(imported, atomEntry, sibling, chain, searchImports);
          if (!entryResolved.success) {
            errors.push(...entryResolved.errors);
            continue;
          }
          atomTrait = entryResolved.data.trait;
          childScope = entryResolved.data.embedScope;
          foundTypeArgs = entryResolved.data.typeArgs;
        }
      }
      if (!atomTrait || !childScope) continue;
      const nextFallback = childScope.alias === searchAlias && childScope.imports === searchImports ? void 0 : childScope;
      let copy = resolveForwardedSiblingConfigFrom(
        applyLinkedEntityRename(atomTrait, linkedEntity, this.entityIdsInScope),
        embedderChain2(parent),
        orbitalConfig,
        this.schemaConfig
      );
      if (finalName !== sibling) {
        copy = { ...copy, name: finalName };
        noteRewrite(parent, sibling, finalName);
      }
      const childParent = childPreResolvedScope ? parent : finalName;
      for (const next of traitEmbedNamesOf(copy)) {
        work.push({
          alias,
          fallback: nextFallback,
          sibling: next,
          linkedEntity,
          parent: childParent,
          owner,
          preResolvedScope: childPreResolvedScope
        });
      }
      pulledAs.set(pullKey, finalName);
      subsFor(owner).set(sibling, finalName);
      ownerOf.set(finalName, owner);
      parentOf.set(finalName, parent);
      seen.add(finalName);
      pulled.push({
        trait: copy,
        source: { type: "imported", alias, traitName: sibling },
        ...linkedEntity !== void 0 ? { linkedEntity } : {},
        ...foundTypeArgs !== void 0 ? { typeArgs: foundTypeArgs } : {}
      });
      if (copy.linkedEntity !== void 0) {
        const aux = entityByNameForAlias(imports, alias, copy.linkedEntity) ?? entityByNameAnyAlias(imports, copy.linkedEntity);
        if (aux) auxEntities.push(aux);
      }
    }
    if (pulled.length === 0 && parentRewrites.size === 0) {
      return { errors, auxEntities, pulledNames: /* @__PURE__ */ new Set() };
    }
    const applyRewrites = (rt) => {
      const subs = rt.trait.name ? parentRewrites.get(rt.trait.name) : void 0;
      if (subs) rt.trait = renameTraitEmbeds(rt.trait, subs);
    };
    for (const rt of resolved) applyRewrites(rt);
    for (const rt of pulled) applyRewrites(rt);
    const idByName = /* @__PURE__ */ new Map();
    for (const rt of [...resolved, ...pulled]) {
      if (rt.trait.name) idByName.set(rt.trait.name, rt.trait.id);
    }
    for (const rt of pulled) {
      const owner = rt.trait.name ? ownerOf.get(rt.trait.name) : void 0;
      const subs = owner ? ownerSubs.get(owner) : void 0;
      if (!subs) continue;
      rt.trait = rewriteListenSources(rt.trait, subs, idByName);
    }
    refResolverLog.info("sibling-pull", {
      pulled: pulled.map((p) => ({
        trait: p.trait.name,
        owner: p.trait.name ? ownerOf.get(p.trait.name) : void 0,
        parent: p.trait.name ? parentOf.get(p.trait.name) : void 0,
        linkedEntity: p.linkedEntity ?? p.trait.linkedEntity
      }))
    });
    resolved.push(...pulled);
    const pulledNames = new Set(
      pulled.map((p) => p.trait.name).filter((n) => Boolean(n))
    );
    return { errors, auxEntities, pulledNames };
  }
  /**
   * Resolve a single trait reference.
   */
  async resolveTraitRef(traitRef, imports, chain, embedCtx, aliasEntityRenames) {
    if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
      const { trait: resolvedInline, errors } = resolveConfigRefEmitNames(traitRef);
      if (errors.length > 0) {
        return { success: false, errors };
      }
      return {
        success: true,
        data: {
          trait: resolvedInline,
          source: { type: "inline" }
        },
        warnings: []
      };
    }
    if (typeof traitRef !== "string" && "ref" in traitRef) {
      const refObj = traitRef;
      return this.resolveTraitRefString(
        refObj.ref,
        imports,
        chain,
        refObj.config,
        refObj.linkedEntity,
        refObj.name,
        refObj.events,
        refObj.listens,
        refObj.refId,
        refObj.typeArgs,
        embedCtx,
        aliasEntityRenames
      );
    }
    if (typeof traitRef === "string") {
      return this.resolveTraitRefString(
        traitRef,
        imports,
        chain,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        void 0,
        embedCtx,
        aliasEntityRenames
      );
    }
    return {
      success: false,
      errors: [`Unknown trait reference format: ${JSON.stringify(traitRef)}`]
    };
  }
  /**
   * Resolve a trait reference string.
   */
  async resolveTraitRefString(ref, imports, chain, config, linkedEntity, overrideName, eventRenames, listensOverride, refId, typeArgs, embedCtx, aliasEntityRenames) {
    const parsed = parseImportedTraitRef(ref);
    const localName = overrideName ?? (parsed ? parsed.traitName : ref);
    const resolvedConfig = config && embedCtx ? resolveEmbedderConfigForward(config, localName, embedCtx, this.schemaConfig) : config;
    if (parsed) {
      const imported = imports.orbitals.get(parsed.alias);
      if (!imported) {
        return {
          success: false,
          errors: [
            `Unknown import alias in trait reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
          ]
        };
      }
      let trait = null;
      let embedScope = { imports, alias: parsed.alias };
      const idEntry = refId ? imports.idIndex?.get(refId) : void 0;
      if (idEntry && idEntry.kind === "trait") {
        trait = idEntry.node;
      } else {
        for (const o of importedOrbitals(imported)) {
          const entry = findTraitEntryInOrbital(o, parsed.traitName);
          if (!entry) continue;
          const entryResolved = await this.resolveTraitEntry(
            imported,
            entry,
            parsed.traitName,
            chain,
            imports,
            resolvedConfig
          );
          if (!entryResolved.success) return entryResolved;
          trait = entryResolved.data.trait;
          embedScope = entryResolved.data.embedScope;
          break;
        }
      }
      if (!trait) {
        const available = importedOrbitals(imported).flatMap((o) => this.listTraitsInOrbital(o));
        return {
          success: false,
          errors: [
            `Trait "${parsed.traitName}" not found in imported orbital "${parsed.alias}". Available traits: ${available.join(", ") || "none"}`
          ]
        };
      }
      if (embedCtx && trait.config) {
        const homeResolvedConfig = resolveConfigForwards(
          trait.config,
          embedderChain(localName, embedCtx.traitRefs, embedCtx.embedGraph),
          embedCtx.orbitalConfig,
          homeOrbitalConfigForTrait(imported, parsed.traitName),
          imported.schemaConfig,
          this.schemaConfig
        );
        if (homeResolvedConfig) {
          trait = { ...trait, config: homeResolvedConfig };
        }
      }
      const baseTrait = overrideName ? { ...trait, name: overrideName } : trait;
      const { trait: configResolvedTrait, errors: configRefErrors, resolvedKnobs } = resolveConfigRefEmitNames(baseTrait, resolvedConfig);
      if (configRefErrors.length > 0) {
        return { success: false, errors: configRefErrors };
      }
      const reboundTrait = applyLinkedEntityRename(configResolvedTrait, linkedEntity, this.entityIdsInScope);
      const aliasSubs = aliasEntityRenames?.get(parsed.alias);
      const aliasReboundTrait = aliasSubs ? applyAliasEntityRenames(reboundTrait, aliasSubs, this.entityIdsInScope) : reboundTrait;
      const renamedTrait = applyEventRenames(aliasReboundTrait, eventRenames);
      const foldedTrait = foldEventRenameOntoDeclaredKnobs(
        renamedTrait,
        resolvedKnobValues(baseTrait, configResolvedTrait, resolvedKnobs),
        eventRenames
      );
      const renamedConfig = applyEventRenamesToCallSiteConfig(resolvedConfig, reboundTrait.config, eventRenames);
      const configFoldedTrait = foldCallSiteConfigOntoTrait(foldedTrait, renamedConfig);
      const finalTrait = listensOverride !== void 0 ? { ...configFoldedTrait, listens: listensOverride } : configFoldedTrait;
      if (listensOverride !== void 0) {
        refResolverLog.info("listens-override:imported", {
          trait: finalTrait.name,
          ref,
          atomListens: trait.listens?.length ?? 0,
          callSiteListens: listensOverride.length
        });
      }
      return {
        success: true,
        data: {
          trait: finalTrait,
          source: { type: "imported", alias: parsed.alias, traitName: parsed.traitName },
          config: renamedConfig,
          linkedEntity,
          embedScope,
          ...typeArgs !== void 0 ? { typeArgs } : {}
        },
        warnings: []
      };
    }
    const localTrait = (refId && this.localTraitsById.get(refId)) ?? this.localTraits.get(ref);
    if (localTrait) {
      const baseLocal = overrideName ? { ...localTrait, name: overrideName } : localTrait;
      const { trait: configResolvedLocal, errors: localConfigRefErrors, resolvedKnobs: localResolvedKnobs } = resolveConfigRefEmitNames(baseLocal, resolvedConfig);
      if (localConfigRefErrors.length > 0) {
        return { success: false, errors: localConfigRefErrors };
      }
      const reboundLocal = applyLinkedEntityRename(configResolvedLocal, linkedEntity, this.entityIdsInScope);
      const renamedLocalTrait = applyEventRenames(reboundLocal, eventRenames);
      const foldedLocalTrait = foldEventRenameOntoDeclaredKnobs(
        renamedLocalTrait,
        resolvedKnobValues(baseLocal, configResolvedLocal, localResolvedKnobs),
        eventRenames
      );
      const renamedLocalConfig = applyEventRenamesToCallSiteConfig(resolvedConfig, reboundLocal.config, eventRenames);
      const configFoldedLocalTrait = foldCallSiteConfigOntoTrait(foldedLocalTrait, renamedLocalConfig);
      const finalLocalTrait = listensOverride !== void 0 ? { ...configFoldedLocalTrait, listens: listensOverride } : configFoldedLocalTrait;
      if (listensOverride !== void 0) {
        refResolverLog.info("listens-override:local", {
          trait: finalLocalTrait.name,
          ref,
          atomListens: localTrait.listens?.length ?? 0,
          callSiteListens: listensOverride.length
        });
      }
      return {
        success: true,
        data: {
          trait: finalLocalTrait,
          source: { type: "local", name: ref },
          config: renamedLocalConfig,
          linkedEntity,
          ...typeArgs !== void 0 ? { typeArgs } : {}
        },
        warnings: []
      };
    }
    return {
      success: false,
      errors: [
        `Trait "${ref}" not found. For imported traits, use format "Alias.traits.TraitName". Local traits available: ${Array.from(this.localTraits.keys()).join(", ") || "none"}`
      ]
    };
  }
  /**
   * List trait names in an orbital.
   */
  listTraitsInOrbital(orbital) {
    const names = [];
    for (const traitRef of orbital.traits) {
      if (typeof traitRef !== "string" && "stateMachine" in traitRef) {
        names.push(traitRef.name);
      }
    }
    return names;
  }
  /**
   * Resolve page references.
   */
  async resolvePages(pageRefs, imports, chain) {
    const errors = [];
    const resolved = [];
    for (const pageRef of pageRefs) {
      const result = await this.resolvePageRef(pageRef, imports, chain);
      if (!result.success) {
        errors.push(...result.errors);
      } else {
        resolved.push(result.data);
      }
    }
    if (errors.length > 0) {
      return { success: false, errors };
    }
    return { success: true, data: resolved, warnings: [] };
  }
  /**
   * Resolve a single page reference.
   */
  async resolvePageRef(pageRef, imports, chain) {
    if (!isPageReference(pageRef)) {
      return {
        success: true,
        data: {
          page: pageRef,
          source: { type: "inline" },
          pathOverridden: false
        },
        warnings: []
      };
    }
    if (isPageReferenceString(pageRef)) {
      return this.resolvePageRefString(pageRef, imports, chain);
    }
    if (isPageReferenceObject(pageRef)) {
      return this.resolvePageRefObject(pageRef, imports, chain);
    }
    return {
      success: false,
      errors: [`Unknown page reference format: ${JSON.stringify(pageRef)}`]
    };
  }
  /**
   * Resolve a page reference string.
   */
  async resolvePageRefString(ref, imports, chain, refId) {
    const parsed = parsePageRef(ref);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid page reference format: ${ref}. Expected "Alias.pages.PageName"`]
      };
    }
    const imported = imports.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in page reference: ${parsed.alias}. Available aliases: ${Array.from(imports.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    let page = null;
    const idEntry = refId ? imports.idIndex?.get(refId) : void 0;
    if (idEntry && idEntry.kind === "page") {
      page = { ...idEntry.node };
    } else {
      for (const o of importedOrbitals(imported)) {
        const entry = findPageEntryInOrbital(o, parsed.pageName);
        if (!entry) continue;
        const entryResolved = await this.resolvePageEntry(imported, entry, chain);
        if (!entryResolved.success) return entryResolved;
        page = entryResolved.data.page;
        break;
      }
    }
    if (!page) {
      const available = importedOrbitals(imported).flatMap((o) => this.listPagesInOrbital(o));
      return {
        success: false,
        errors: [
          `Page "${parsed.pageName}" not found in imported orbital "${parsed.alias}". Available pages: ${available.join(", ") || "none"}`
        ]
      };
    }
    return {
      success: true,
      data: {
        page,
        source: { type: "imported", alias: parsed.alias, pageName: parsed.pageName },
        pathOverridden: false
      },
      warnings: []
    };
  }
  /**
   * Resolve a page reference object, applying every call-site override —
   * `path` (direct assignment), `linkedEntity` (rebind `primaryEntity` and
   * every page-level trait pointer that named the old entity), and `traits`
   * (full replacement) — mirroring the compiler's `apply_overrides_to_page`
   * (`phases/inline/page.rs:21-93`). `Page.traits` is a `PageTraitRef[]`
   * pointer list (a trait's own body lives in the orbital's `traits {}`, not
   * on the page), so unlike Rust's `TraitReference` the `linkedEntity` rebind
   * only ever touches each pointer's OWN `linkedEntity` field — there is no
   * inline trait body here to walk with `renameEntitiesInTrait`.
   */
  async resolvePageRefObject(refObj, imports, chain) {
    const baseResult = await this.resolvePageRefString(refObj.ref, imports, chain, refObj.refId);
    if (!baseResult.success) {
      return baseResult;
    }
    const resolved = baseResult.data;
    if (refObj.path) {
      const originalPath = resolved.page.path;
      resolved.page = {
        ...resolved.page,
        path: refObj.path
      };
      resolved.pathOverridden = true;
      resolved.originalPath = originalPath;
    }
    if (refObj.linkedEntity) {
      const oldEntity = resolved.page.primaryEntity;
      const newEntity = refObj.linkedEntity;
      const rebindPointers = (pointers) => oldEntity && oldEntity !== newEntity ? pointers?.map((pt) => pt.linkedEntity === oldEntity ? { ...pt, linkedEntity: newEntity } : pt) : pointers;
      resolved.page = {
        ...resolved.page,
        primaryEntity: newEntity,
        traits: rebindPointers(resolved.page.traits)
      };
    }
    if (refObj.traits) {
      resolved.page = { ...resolved.page, traits: refObj.traits.map(toPageTraitRef) };
    }
    return {
      success: true,
      data: resolved,
      warnings: baseResult.warnings
    };
  }
  /**
   * List page names in an orbital.
   */
  listPagesInOrbital(orbital) {
    const pages = orbital.pages;
    if (!pages) return [];
    const names = [];
    for (const pageRef of pages) {
      if (typeof pageRef !== "string" && !("ref" in pageRef)) {
        names.push(pageRef.name);
      }
    }
    return names;
  }
  /**
   * Flatten every reference-form orbital (`orbital X = Alias.orbitals.Y
   * { … }`, docs/Almadar_Orbital_Import.md §4) in a schema BEFORE the
   * ordinary per-orbital {@link resolve} pass runs — the interpreter-path
   * twin of the (not-yet-landed) compiler's inline-phase flatten. After this
   * call, no returned `OrbitalDefinition` carries `.reference`.
   */
  async resolveOrbitalImports(schema, opts) {
    const errors = [];
    const flattened = [];
    const chain = { push: () => null, pop: () => {
    }, clone() {
      return this;
    } };
    const visiting = /* @__PURE__ */ new Set();
    const consumerEntityIds = consumerEntityIdsOf(schema.orbitals);
    for (const [name, id] of await this.precomputeReferenceFormPrimaryIds(schema, chain)) {
      if (!consumerEntityIds.has(name)) consumerEntityIds.set(name, id);
    }
    const listenSourceTargets = await this.precomputeReferenceFormListenTargets(schema.orbitals, chain);
    const consumerRoster = identityEntitiesOf(schema.orbitals);
    for (const orbital of schema.orbitals) {
      if (!orbital.reference) {
        if (!opts?.skipTypeParamSentinels && isInlineEntity(orbital.entity)) {
          resolveOrbitalTypeParamSentinels(
            orbital.traits.filter(isInlineTrait).map((t) => ({ trait: t })),
            orbital.entity,
            (orbital.auxiliaryEntities ?? []).filter(isInlineEntity),
            orbital.types
          );
        }
        flattened.push(orbital);
        continue;
      }
      const result = await this.resolveOrbitalRefChain(
        orbital,
        void 0,
        chain,
        visiting,
        consumerEntityIds,
        consumerRoster,
        listenSourceTargets
      );
      if (!result.success) {
        errors.push(...result.errors);
        continue;
      }
      if (!opts?.skipTypeParamSentinels && isInlineEntity(result.data.entity)) {
        resolveOrbitalTypeParamSentinels(
          result.data.traits.filter(isInlineTrait).map((t) => ({ trait: t })),
          result.data.entity,
          (result.data.auxiliaryEntities ?? []).filter(isInlineEntity),
          result.data.types
        );
      }
      flattened.push(result.data);
    }
    if (errors.length > 0) return { success: false, errors };
    reconcileOrbitalRefEventIdRenames(schema, flattened);
    return { success: true, data: flattened, warnings: [] };
  }
  /**
   * (B) The materialized primary name → derived id of every REFERENCE-FORM
   * orbital this schema declares (`orbital X = Alias.orbitals.Y { … }`),
   * computed order-free — WITHOUT requiring any other orbital in `schema`
   * to have resolved first, and without recursively flattening the
   * upstream. Each reference's alias is loaded via the ordinary
   * `resolveImports` path, which the loader caches by absolute path (see
   * `ExternalOrbitalLoader`'s `LoaderCache`) — the REAL flatten
   * (`resolveOrbitalRefChain`) moments later is then a cache hit, no
   * duplicate work — and its named orbital looked up directly in the
   * loaded (un-flattened) schema: an upstream's OWN primary entity id/name
   * never change through resolution (only a CONSUMER-side CLONE gets a
   * fresh derived id), so this shallow load's entity is exactly what the
   * real flatten pass would see too. Uses the SAME `materializedPrimaryEntity`
   * pair `materializeOrbitalRef` computes for itself — one owner. JS twin
   * of the compiled path's `precompute_reference_form_primary_ids`.
   *
   * Best-effort: a reference this pre-pass cannot resolve (unknown alias,
   * malformed reference string, upstream orbital not found, load failure)
   * is silently skipped here — the REAL `resolveOrbitalRefChain` call
   * moments later still surfaces the actual error for it.
   */
  async precomputeReferenceFormPrimaryIds(schema, chain) {
    const out = /* @__PURE__ */ new Map();
    for (const orbital of schema.orbitals) {
      const ref = orbital.reference;
      if (!ref) continue;
      const parsed = parseOrbitalRef(ref.ref);
      if (!parsed) continue;
      const importsResult = await this.resolveImports(orbital.uses ?? [], void 0, chain);
      if (!importsResult.success) continue;
      const imported = importsResult.data.orbitals.get(parsed.alias);
      if (!imported) continue;
      const upstream = importedOrbitals(imported).find((o) => o.name === parsed.orbitalName);
      if (!upstream || !isInlineEntity(upstream.entity)) continue;
      const { name, id } = materializedPrimaryEntity(upstream.entity, upstream.id, ref, orbital.name);
      out.set(name, id);
    }
    return out;
  }
  /**
   * G1 (`docs/Almadar_Compiler_Gaps.md` §82): every reference-form
   * orbital's OWN materialized trait set, keyed by {@link siblingImportKey}
   * (upstream alias + upstream orbital name), mapped to that import's local
   * orbital name + every kept trait's final name/id ({@link
   * materializedTraitSubs}). Computed order-free — WITHOUT requiring any
   * other orbital in `schema` to have resolved first — same precompute
   * shape as {@link precomputeReferenceFormPrimaryIds}, but for LISTEN-
   * SOURCE rewriting: consulted by {@link rewriteListenSources} so a source
   * naming ANY reference-form orbital of this schema resolves regardless of
   * declaration order. Unlike the entity-id precompute, this resolves the
   * upstream's OWN traits via the ordinary `this.resolve()` pass (the same
   * call {@link materializeOrbitalRef} makes on its own upstream, with the
   * same `schemaConfig` save/restore) rather than reading the raw unresolved
   * schema — the JS identity model has no pre-resolve declaration id for a
   * COMPOSED trait reference (unlike the compiled path's V4 dual-carry
   * `TraitReference::Reference.id`), so a shallow read would silently
   * under-report every composed trait's id.
   *
   * Best-effort: a reference this pre-pass cannot resolve (unknown alias,
   * malformed reference string, upstream orbital not found, itself still
   * reference-form, load/resolve failure) is silently skipped here — the
   * REAL `resolveOrbitalRefChain`/`materializeOrbitalRef` call moments
   * later still surfaces the actual error for it.
   */
  async precomputeReferenceFormListenTargets(orbitals, chain) {
    const out = /* @__PURE__ */ new Map();
    for (const orbital of orbitals) {
      const ref = orbital.reference;
      if (!ref) continue;
      const parsed = parseOrbitalRef(ref.ref);
      if (!parsed) continue;
      const importsResult = await this.resolveImports(orbital.uses ?? [], void 0, chain);
      if (!importsResult.success) continue;
      const imported = importsResult.data.orbitals.get(parsed.alias);
      if (!imported) continue;
      const upstream = importedOrbitals(imported).find((o) => o.name === parsed.orbitalName);
      if (!upstream || upstream.reference) continue;
      const savedSchemaConfig = this.schemaConfig;
      this.schemaConfig = imported.schemaConfig;
      let resolvedUpstream;
      try {
        resolvedUpstream = await this.resolve(upstream, imported.sourcePath, chain);
      } finally {
        this.schemaConfig = savedSchemaConfig;
      }
      if (!resolvedUpstream.success) continue;
      const traitSubs = materializedTraitSubs(upstream.id, resolvedUpstream.data.traits, ref, orbital.name);
      out.set(siblingImportKey(parsed.alias, parsed.orbitalName), { localName: orbital.name, traitSubs });
    }
    return out;
  }
  /**
   * Resolve one reference-form orbital, recursing into the upstream first
   * when IT is also reference-form ("already fully inlined because inline
   * recursed into every loaded orbital" — docs/Almadar_Orbital_Import.md §9
   * step 3 — the JS resolver needs its own cycle guard since it has no
   * file-level inline-order pass to lean on).
   */
  async resolveOrbitalRefChain(orbital, sourcePath, chain, visiting, consumerEntityIds, consumerRoster, listenSourceTargets) {
    const ref = orbital.reference;
    if (!ref) return { success: true, data: orbital, warnings: [] };
    const parsed = parseOrbitalRef(ref.ref);
    if (!parsed) {
      return {
        success: false,
        errors: [`Invalid orbital reference format: ${ref.ref}. Expected "Alias.orbitals.OrbitalName"`]
      };
    }
    const importsResult = await this.resolveImports(orbital.uses ?? [], sourcePath, chain);
    if (!importsResult.success) return { success: false, errors: importsResult.errors };
    const imported = importsResult.data.orbitals.get(parsed.alias);
    if (!imported) {
      return {
        success: false,
        errors: [
          `Unknown import alias in orbital reference: ${parsed.alias}. Available aliases: ${Array.from(importsResult.data.orbitals.keys()).join(", ") || "none"}`
        ]
      };
    }
    const candidates = importedOrbitals(imported);
    let upstream = candidates.find((o) => o.name === parsed.orbitalName);
    if (!upstream) {
      return {
        success: false,
        errors: [
          `Orbital "${parsed.orbitalName}" not found in imported behavior "${parsed.alias}". Available orbitals: ${candidates.map((o) => o.name).join(", ") || "none"}`
        ]
      };
    }
    const cycleKey = `${imported.sourcePath}::${parsed.orbitalName}`;
    if (visiting.has(cycleKey)) {
      return {
        success: false,
        errors: [
          `Circular orbital reference: "${orbital.name}" imports "${parsed.alias}.orbitals.${parsed.orbitalName}" while that orbital is itself being resolved`
        ]
      };
    }
    visiting.add(cycleKey);
    if (upstream.reference) {
      const nestedListenSourceTargets = await this.precomputeReferenceFormListenTargets(candidates, chain);
      const nested = await this.resolveOrbitalRefChain(
        upstream,
        imported.sourcePath,
        chain,
        visiting,
        consumerEntityIds,
        consumerRoster,
        nestedListenSourceTargets
      );
      if (!nested.success) {
        visiting.delete(cycleKey);
        return nested;
      }
      upstream = nested.data;
    }
    visiting.delete(cycleKey);
    const materialized = await this.materializeOrbitalRef(
      upstream,
      ref,
      orbital.name,
      orbital.id,
      imported.sourcePath,
      chain,
      candidates,
      consumerEntityIds,
      consumerRoster,
      imported.schemaConfig,
      parsed.alias,
      listenSourceTargets
    );
    if (!materialized.success) return materialized;
    const siblingTraits = orbital.traits ?? [];
    return {
      success: true,
      data: {
        ...materialized.data,
        ...siblingTraits.length > 0 ? { traits: [...materialized.data.traits, ...siblingTraits] } : {},
        uses: orbital.uses
      },
      warnings: materialized.warnings
    };
  }
  /**
   * Materialize a reference-form orbital: flatten the (already-inlined)
   * upstream orbital's whole trait/entity/page closure into the consumer,
   * unconditionally prefixed by `localName` (docs/Almadar_Orbital_Import.md
   * §4.3). JS twin of the (not-yet-landed) compiler's
   * `resolve_orbital_reference`.
   */
  async materializeOrbitalRef(upstream, ref, localName, localId, sourcePath, chain, aliasOrbitals, consumerEntityIds, consumerRoster, importedSchemaConfig, alias, listenSourceTargets) {
    const savedSchemaConfig = this.schemaConfig;
    this.schemaConfig = importedSchemaConfig;
    let resolvedUpstream;
    try {
      resolvedUpstream = await this.resolve(upstream, sourcePath, chain);
    } finally {
      this.schemaConfig = savedSchemaConfig;
    }
    if (!resolvedUpstream.success) {
      return {
        success: false,
        errors: resolvedUpstream.errors.map(
          (e) => `Orbital reference "${localName}" (${ref.ref}): ${e}`
        )
      };
    }
    const {
      entity: upstreamEntity,
      traits: upstreamTraits,
      pages: upstreamPages,
      // (C) the RESOLVED aux-entity set — includes whatever sibling-pull
      // surfaced (`pullSiblingTraits`'s aux-entity carry), never just
      // `upstream.auxiliaryEntities` (the pre-resolve INPUT, which misses
      // every un-rebound composed atom's own entity).
      auxiliaryEntities: upstreamAuxiliaryEntities
    } = resolvedUpstream.data;
    const onlySet = ref.only ? new Set(ref.only) : void 0;
    const omitSet = new Set(ref.omit ?? []);
    const keptTraits = upstreamTraits.filter(
      (rt) => onlySet ? onlySet.has(rt.trait.name) : !omitSet.has(rt.trait.name)
    );
    const keptNames = new Set(keptTraits.map((rt) => rt.trait.name));
    const removedNames = new Set(
      upstreamTraits.map((rt) => rt.trait.name).filter((n) => !keptNames.has(n))
    );
    const errors = [];
    for (const rt of keptTraits) {
      for (const embed of traitEmbedNamesOf(rt.trait)) {
        if (removedNames.has(embed)) {
          errors.push(
            `Orbital "${localName}" omits trait "${embed}" but surviving trait "${rt.trait.name}" still embeds it via @trait.${embed} (ORB_O_OMIT_EMBEDDED_TRAIT)`
          );
        }
      }
    }
    if (errors.length > 0) return { success: false, errors };
    let auxEntities = upstreamAuxiliaryEntities ?? [];
    const subs = /* @__PURE__ */ new Map();
    for (const rt of keptTraits) subs.set(rt.trait.name, `${localName}${rt.trait.name}`);
    const keptFinalNames = new Set(subs.values());
    const { name: primaryFinalName, id: primaryId } = materializedPrimaryEntity(
      upstreamEntity,
      upstream.id,
      ref,
      localName
    );
    subs.set(upstreamEntity.name, primaryFinalName);
    for (const e of auxEntities) subs.set(e.name, `${localName}${e.name}`);
    const fieldSubs = new Map(Object.entries(ref.fields ?? {}));
    const closure = /* @__PURE__ */ new Set([upstreamEntity.name, ...auxEntities.map((e) => e.name)]);
    const upstreamUniverse = /* @__PURE__ */ new Set();
    const upstreamUniverseEntities = /* @__PURE__ */ new Map();
    const primaryEntityNames = /* @__PURE__ */ new Set();
    for (const o of aliasOrbitals) {
      if (o.name === upstream.name) continue;
      if (isInlineEntity(o.entity)) primaryEntityNames.add(o.entity.name);
      for (const entity of inlineEntityRefsOf(o)) {
        if (!closure.has(entity.name)) {
          upstreamUniverse.add(entity.name);
          upstreamUniverseEntities.set(entity.name, entity);
        }
      }
    }
    const entityIds = /* @__PURE__ */ new Map();
    if (ref.entities) {
      for (const [upstreamKey, consumerName] of Object.entries(ref.entities)) {
        if (!upstreamUniverse.has(upstreamKey)) {
          errors.push(
            `Orbital "${localName}" declares entities { ${upstreamKey}: ${consumerName} } but "${upstreamKey}" is not an out-of-orbital entity of upstream alias "${upstream.name}" (ORB_O_ENTITY_UNKNOWN_KEY)`
          );
          continue;
        }
        const consumerId = consumerEntityIds.get(consumerName);
        if (consumerId === void 0) {
          errors.push(
            `Orbital "${localName}" entities { ${upstreamKey}: ${consumerName} } targets "${consumerName}", which the consumer schema does not declare (ORB_O_ENTITY_TARGET_UNKNOWN)`
          );
          continue;
        }
        subs.set(upstreamKey, consumerName);
        entityIds.set(consumerName, consumerId);
      }
      if (errors.length > 0) return { success: false, errors };
    }
    const referenced = /* @__PURE__ */ new Set();
    for (const rt of keptTraits) {
      if (rt.trait.linkedEntity && upstreamUniverse.has(rt.trait.linkedEntity)) {
        referenced.add(rt.trait.linkedEntity);
      }
      for (const name of collectEntityNamesInTrait(rt.trait, upstreamUniverse)) referenced.add(name);
      if (rt.trait.sourceEntityDefinition) {
        for (const name of relationTargetsOfEntity(rt.trait.sourceEntityDefinition, upstreamUniverse)) {
          referenced.add(name);
        }
      }
    }
    for (const name of relationTargetsOfEntity(upstreamEntity, upstreamUniverse)) referenced.add(name);
    for (const aux of auxEntities) {
      for (const name of relationTargetsOfEntity(aux, upstreamUniverse)) referenced.add(name);
    }
    for (const rp of upstreamPages) {
      if (rp.page.primaryEntity && upstreamUniverse.has(rp.page.primaryEntity)) {
        referenced.add(rp.page.primaryEntity);
      }
      for (const tr of rp.page.traits ?? []) {
        if (tr.linkedEntity && upstreamUniverse.has(tr.linkedEntity)) referenced.add(tr.linkedEntity);
      }
    }
    for (const name of referenced) {
      if (subs.has(name)) continue;
      const definition = upstreamUniverseEntities.get(name);
      const auxOnly = definition !== void 0 && !primaryEntityNames.has(name);
      if (definition && auxOnly && !definition.identity) {
        auxEntities = [...auxEntities, definition];
        subs.set(name, `${localName}${name}`);
        continue;
      }
      errors.push(
        `Orbital "${localName}" references out-of-orbital entity "${name}" with no "${name}: <consumer entity>" mapping in entities {} (ORB_O_ENTITY_UNMAPPED)`
      );
    }
    if (errors.length > 0) return { success: false, errors };
    const roleFields = /* @__PURE__ */ new Map();
    for (const entity of identityEntitiesOf(aliasOrbitals)) {
      for (const field of entity.fields) {
        if (!field.name) continue;
        const vocab = roleVocabularyOf(entity, field.name);
        if (vocab) roleFields.set(field.name, vocab);
      }
    }
    const consumerRoleFields = /* @__PURE__ */ new Map();
    for (const entity of consumerRoster) {
      for (const field of entity.fields) {
        if (!field.name) continue;
        const vocab = roleVocabularyOf(entity, field.name);
        if (!vocab) continue;
        let values = consumerRoleFields.get(field.name);
        if (!values) {
          values = /* @__PURE__ */ new Set();
          consumerRoleFields.set(field.name, values);
        }
        for (const value of vocab) values.add(value);
      }
    }
    for (const [literal, targets] of Object.entries(ref.roles ?? {})) {
      const matchingFields = [...roleFields.entries()].filter(([, vocab]) => vocab.includes(literal)).map(([field]) => field).sort();
      if (matchingFields.length === 0) {
        errors.push(
          `Orbital "${localName}" declares roles { ${literal}: ... } but upstream alias "${upstream.name}"'s roster declares no role field with literal "${literal}" in its vocabulary (ORB_O_ROLE_UNKNOWN_UPSTREAM)`
        );
        continue;
      }
      for (const field of matchingFields) {
        const allowed = consumerRoleFields.get(field);
        for (const target of targets) {
          if (allowed?.has(target)) continue;
          errors.push(
            `Orbital "${localName}" roles { ${literal}: ${target} } targets "${target}", which is not a member of consumer field "${field}" on the roster (values: ${allowed ? [...allowed].sort().join(", ") : "(none)"}) (ORB_O_ROLE_TARGET_NOT_MEMBER)`
          );
        }
      }
    }
    if (errors.length > 0) return { success: false, errors };
    const declaredConfig = upstream.config;
    if (ref.config) {
      for (const key of Object.keys(ref.config)) {
        if (!declaredConfig || !(key in declaredConfig)) {
          errors.push(
            `Orbital "${localName}" overrides config "${key}" but upstream orbital "${upstream.name}" does not declare that knob (ORB_O_CONFIG_UNKNOWN_KEY)`
          );
        }
      }
      if (errors.length > 0) return { success: false, errors };
    }
    const configOverrideValues = {};
    for (const [key, field] of Object.entries(ref.config ?? {})) {
      if (field.default !== void 0) configOverrideValues[key] = field.default;
    }
    const foldedConfig = declaredConfig ? overrideDeclaredKnobs(declaredConfig, configOverrideValues) : void 0;
    const idByName = /* @__PURE__ */ new Map();
    const materializedTraits = keptTraits.map((rt) => {
      const finalName = subs.get(rt.trait.name);
      const finalId = asTraitId(deriveMaterializedId(rt.trait.id, upstream.id, rt.trait.name, localName, "trait"));
      idByName.set(finalName, finalId);
      return { ...rt.trait, name: finalName, id: finalId };
    });
    const embeddersOf = /* @__PURE__ */ new Map();
    for (const t of materializedTraits) {
      for (const embed of traitEmbedNamesOf(t)) {
        const embedFinal = subs.get(embed);
        if (!embedFinal) continue;
        let embedders = embeddersOf.get(embedFinal);
        if (!embedders) {
          embedders = [];
          embeddersOf.set(embedFinal, embedders);
        }
        embedders.push(t.name);
      }
    }
    const parentConfigs = new Map(
      materializedTraits.map((t) => [t.name, t.config])
    );
    const transitiveEmbedderChain = (name) => {
      const chain2 = [];
      const seenNames = /* @__PURE__ */ new Set([name]);
      let cur = embeddersOf.get(name)?.[0];
      while (cur !== void 0 && !seenNames.has(cur)) {
        seenNames.add(cur);
        const cfg = parentConfigs.get(cur);
        if (cfg) chain2.push(cfg);
        cur = embeddersOf.get(cur)?.[0];
      }
      return chain2;
    };
    entityIds.set(primaryFinalName, primaryId);
    for (const e of auxEntities) {
      entityIds.set(
        subs.get(e.name),
        asEntityId(deriveMaterializedId(e.id, upstream.id, e.name, localName, "entity"))
      );
    }
    const upstreamPathSet = new Set(upstreamPages.map((rp) => rp.page.path));
    for (const path of [...Object.keys(ref.pages ?? {}), ...Object.keys(ref.mounts ?? {})]) {
      if (!upstreamPathSet.has(path)) {
        errors.push(
          `Orbital "${localName}" names page path "${path}" but upstream orbital "${upstream.name}" has no page at that path (ORB_O_PAGE_UNKNOWN_PATH)`
        );
      }
    }
    if (errors.length > 0) return { success: false, errors };
    const pathMap = new Map(
      upstreamPages.map((rp) => [rp.page.path, ref.pages?.[rp.page.path] ?? rp.page.path])
    );
    const orbitalRename = upstream.name !== localName ? { from: upstream.name, to: localName } : void 0;
    const finalTraits = materializedTraits.map((trait) => {
      let next = renameTraitEmbeds(trait, subs);
      next = renameEntitiesInTrait(next, subs, entityIds);
      if (next.linkedEntity !== void 0) {
        const linkedEntityId = entityIds.get(next.linkedEntity);
        if (linkedEntityId !== void 0) next = { ...next, linkedEntityId };
      }
      next = rewriteEntityRefIds(next, subs, entityIds);
      next = renameSourceEntityDefinition(next, subs, entityIds);
      next = rewriteRoleLiteralsInTrait(next, roleFields, ref.roles ?? {});
      if (fieldSubs.size > 0) next = rewriteEntityFieldsInTrait(next, fieldSubs);
      next = rewriteListenSources(next, subs, idByName, orbitalRename, alias, listenSourceTargets);
      if (pathMap.size > 0) {
        next = rewriteTraitNavigateTargets(next, pathMap);
      }
      const schemaOnlyKeys = /* @__PURE__ */ new Set();
      for (const embedderName of embeddersOf.get(trait.name) ?? []) {
        const embedderConfig = parentConfigs.get(embedderName);
        next = resolveForwardedSiblingConfigFrom(
          next,
          embedderConfig ? [embedderConfig] : [],
          void 0,
          void 0,
          schemaOnlyKeys
        );
      }
      next = resolveForwardedSiblingConfigFrom(
        next,
        transitiveEmbedderChain(trait.name),
        void 0,
        void 0,
        schemaOnlyKeys
      );
      next = resolveForwardedSiblingConfigFrom(next, [], foldedConfig, importedSchemaConfig, schemaOnlyKeys);
      if (fieldSubs.size > 0 && next.config) {
        next = { ...next, config: rewriteEntityFieldsInConfig(next.config, fieldSubs) };
      }
      if (pathMap.size > 0) {
        next = rewriteTraitConfigPagePaths(next, pathMap, schemaOnlyKeys);
      }
      next = applyEventRenames(next, ref.events, keptFinalNames);
      return next;
    });
    const renamedPrimaryEntity = rewriteRoleLiteralsInEntity(
      renameEntityRelationTargets(
        rewriteEntityFieldsInEntity(
          {
            ...upstreamEntity,
            name: primaryFinalName,
            id: primaryId
          },
          fieldSubs
        ),
        subs,
        entityIds
      ),
      roleFields,
      ref.roles ?? {}
    );
    const extendResult = applyExtendFields(
      renamedPrimaryEntity,
      ref.extend,
      fieldSubs,
      consumerEntityIds,
      localName
    );
    if (extendResult.errors.length > 0) return { success: false, errors: extendResult.errors };
    const primaryEntity = extendResult.entity;
    const finalAuxEntities = auxEntities.map((e) => {
      const finalName = subs.get(e.name);
      return rewriteRoleLiteralsInEntity(
        renameEntityRelationTargets(
          rewriteEntityFieldsInEntity(
            {
              ...e,
              name: finalName,
              id: entityIds.get(finalName)
            },
            fieldSubs
          ),
          subs,
          entityIds
        ),
        roleFields,
        ref.roles ?? {}
      );
    });
    const finalPages = upstreamPages.map((rp) => {
      const original = rp.page;
      const mountNames = ref.mounts?.[original.path] ?? [];
      const finalPageTraits = [
        ...(original.traits ?? []).filter((tr) => !removedNames.has(tr.ref)).map((tr) => {
          const finalRef = subs.get(tr.ref) ?? tr.ref;
          const refId = idByName.get(finalRef);
          return {
            ...tr,
            ref: finalRef,
            ...refId !== void 0 ? { refId } : {},
            ...tr.linkedEntity !== void 0 ? { linkedEntity: subs.get(tr.linkedEntity) ?? tr.linkedEntity } : {}
          };
        }),
        ...mountNames.map((name) => ({ ref: name }))
      ];
      return {
        ...original,
        // Gap (E) — unconditionally prefixed, same as traits/entities (§4.3):
        // two imports of the same upstream orbital with distinct `pages {}`
        // path maps must not collide on page NAME (`Duplicate page name`).
        // `id` is the page-scope sibling of `deriveMaterializedId` (never
        // `mintId`) — fresh and pairwise-disjoint across imports, same as
        // trait/entity ids above.
        name: `${localName}${original.name}`,
        id: asPageId(deriveMaterializedId(original.id, upstream.id, original.name, localName, "page")),
        path: pathMap.get(original.path) ?? original.path,
        ...original.primaryEntity !== void 0 ? { primaryEntity: subs.get(original.primaryEntity) ?? original.primaryEntity } : {},
        traits: finalPageTraits
      };
    });
    const forwardedKnobs = /* @__PURE__ */ new Set();
    for (const rt of keptTraits) {
      for (const field of Object.values(rt.trait.config ?? {})) {
        if (typeof field.default === "string" && field.default.startsWith("@config.")) {
          const knob = field.default.slice("@config.".length);
          if (knob.length > 0 && !knob.includes(".")) forwardedKnobs.add(knob);
        }
      }
    }
    const prunedConfig = foldedConfig ? Object.fromEntries(Object.entries(foldedConfig).filter(([key]) => forwardedKnobs.has(key))) : void 0;
    const orbitalId = localId ?? (upstream.id ? asOrbitalId(deriveId(upstream.id, localName)) : void 0);
    refResolverLog.info("orbital-import:materialize", {
      local: localName,
      upstream: upstream.name,
      traits: finalTraits.length,
      pages: finalPages.length
    });
    return {
      success: true,
      data: {
        ...upstream,
        name: localName,
        ...orbitalId ? { id: orbitalId } : {},
        entity: primaryEntity,
        ...finalAuxEntities.length > 0 ? { auxiliaryEntities: finalAuxEntities } : {},
        traits: finalTraits,
        pages: finalPages,
        emits: void 0,
        listens: void 0,
        exposes: void 0,
        ...prunedConfig ? { config: prunedConfig } : { config: void 0 },
        uses: void 0,
        reference: void 0
      },
      warnings: []
    };
  }
  /**
   * Add local traits for resolution.
   */
  addLocalTraits(traits) {
    for (const trait of traits) {
      this.localTraits.set(trait.name, trait);
      if (trait.id) {
        this.localTraitsById.set(trait.id, trait);
      }
    }
  }
  /**
   * Clear loader cache.
   */
  clearCache() {
    this.loader?.clearCache();
  }
};
function isInlineTraitWithStateMachine(tr) {
  return isInlineTrait(tr);
}
function reconcileOrbitalRefEventIdRenames(schema, flattened) {
  if (!schema.ledger) return;
  const renamedRefs = schema.orbitals.filter((o) => o.reference?.events && Object.keys(o.reference.events).length > 0).map((o) => ({ localName: o.name, eventSubs: o.reference.events }));
  if (renamedRefs.length === 0) return;
  const sites = [];
  for (const { localName, eventSubs } of renamedRefs) {
    const newKeys = new Set(Object.values(eventSubs));
    const orbital = flattened.find((o) => o.name === localName);
    if (!orbital) continue;
    for (const tr of orbital.traits) {
      if (!isInlineTraitWithStateMachine(tr)) continue;
      for (const ev of tr.stateMachine?.events ?? []) {
        if (!ev.id || !newKeys.has(ev.key)) continue;
        const entry = schema.ledger.entries[ev.id];
        if (!entry || entry.curName === ev.key) continue;
        sites.push({ orbitalName: orbital.name, traitName: tr.name, oldId: ev.id, newKey: ev.key });
      }
    }
  }
  if (sites.length === 0) return;
  const candidateIds = new Set(sites.map((s) => s.oldId));
  const owners = /* @__PURE__ */ new Map();
  for (const orbital of flattened) {
    for (const tr of orbital.traits) {
      if (!isInlineTraitWithStateMachine(tr)) continue;
      for (const ev of tr.stateMachine?.events ?? []) {
        if (ev.id && candidateIds.has(ev.id)) {
          const ownerKey = `${orbital.name}\0${tr.name}`;
          const set = owners.get(ev.id) ?? /* @__PURE__ */ new Set();
          set.add(ownerKey);
          owners.set(ev.id, set);
        }
      }
    }
  }
  const renameInPlace = /* @__PURE__ */ new Map();
  const deriveSites = [];
  const seen = /* @__PURE__ */ new Set();
  for (const site of sites) {
    const ownerCount = owners.get(site.oldId)?.size ?? 1;
    if (ownerCount <= 1) {
      renameInPlace.set(site.oldId, site.newKey);
    } else if (!seen.has(site.oldId)) {
      seen.add(site.oldId);
      deriveSites.push({ site, newId: asEventId(deriveId(site.oldId, site.newKey)) });
    }
  }
  for (const { site, newId } of deriveSites) {
    const orbital = flattened.find((o) => o.name === site.orbitalName);
    if (!orbital) continue;
    orbital.traits = orbital.traits.map((tr) => {
      if (!isInlineTraitWithStateMachine(tr) || tr.name !== site.traitName) return tr;
      let next = tr;
      if (next.stateMachine?.events) {
        next = {
          ...next,
          stateMachine: {
            ...next.stateMachine,
            events: next.stateMachine.events.map((ev) => ev.id === site.oldId ? { ...ev, id: newId } : ev),
            transitions: (next.stateMachine.transitions ?? []).map(
              (t) => t.eventId === site.oldId ? { ...t, eventId: newId } : t
            )
          }
        };
      }
      if (next.emits) {
        next = {
          ...next,
          emits: next.emits.map(
            (e) => typeof e !== "string" && e.eventId === site.oldId ? { ...e, eventId: newId } : e
          )
        };
      }
      if (next.listens) {
        next = {
          ...next,
          listens: next.listens.map((l) => {
            const eventId = l.eventId === site.oldId ? newId : l.eventId;
            const triggersId = l.triggersId === site.oldId ? newId : l.triggersId;
            return eventId === l.eventId && triggersId === l.triggersId ? l : { ...l, eventId, triggersId };
          })
        };
      }
      return next;
    });
  }
  const at = (/* @__PURE__ */ new Date()).toISOString();
  let ledger = schema.ledger;
  for (const [oldId, newKey] of renameInPlace) {
    ledger = ledgerRename(ledger, oldId, newKey, at);
  }
  for (const { site, newId } of deriveSites) {
    const src = ledger.entries[site.oldId];
    if (!src) continue;
    const ownerTrait = flattened.find((o) => o.name === site.orbitalName)?.traits.find((tr) => isInlineTraitWithStateMachine(tr) && tr.name === site.traitName);
    const newEntry = {
      id: newId,
      kind: src.kind,
      bakedName: src.bakedName,
      curName: site.newKey,
      renames: [...src.renames, { from: src.curName, to: site.newKey, at }],
      owner: src.owner,
      ...ownerTrait?.id !== void 0 ? { parent: ownerTrait.id } : {}
    };
    ledger = { ...ledger, entries: { ...ledger.entries, [newId]: newEntry } };
  }
  schema.ledger = ledger;
}
async function resolveSchema(schema, options) {
  const resolver = new ReferenceResolver({ ...options, schemaConfig: schema.config });
  const errors = [];
  const warnings = [];
  const resolved = [];
  const flattenResult = await resolver.resolveOrbitalImports(schema);
  if (!flattenResult.success) {
    return { success: false, errors: flattenResult.errors };
  }
  const orbitals = flattenResult.data;
  for (const orbital of orbitals) {
    const inlineTraits = orbital.traits.filter(
      (t) => typeof t !== "string" && "stateMachine" in t
    );
    resolver.addLocalTraits(inlineTraits);
  }
  resolver.seedSchemaEntityIds(orbitals);
  for (const orbital of orbitals) {
    const result = await resolver.resolve(orbital);
    if (!result.success) {
      errors.push(`Orbital "${orbital.name}": ${result.errors.join(", ")}`);
    } else {
      resolved.push(result.data);
      resolver.noteResolvedEntityIds(result.data);
      warnings.push(...result.warnings.map((w) => `Orbital "${orbital.name}": ${w}`));
    }
  }
  if (errors.length > 0) {
    return { success: false, errors };
  }
  resolver.uniquifyCrossOrbitalPulledSiblings(resolved);
  return { success: true, data: resolved, warnings };
}

// src/UsesIntegration.ts
async function preprocessSchema(schema, options) {
  const namespaceEvents = options.namespaceEvents ?? true;
  const resolveResult = await resolveSchema(schema, options);
  if (!resolveResult.success) {
    return { success: false, errors: resolveResult.errors };
  }
  const resolved = resolveResult.data;
  const warnings = resolveResult.warnings;
  const preprocessedOrbitals = [];
  const entitySharing = {};
  const eventNamespaces = {};
  for (const resolvedOrbital of resolved) {
    const orbitalName = resolvedOrbital.name;
    const persistence = resolvedOrbital.entitySource?.persistence ?? resolvedOrbital.entity.persistence ?? "persistent";
    entitySharing[orbitalName] = {
      entityName: resolvedOrbital.entity.name,
      persistence,
      isShared: persistence !== "runtime",
      sourceAlias: resolvedOrbital.entitySource?.alias,
      collectionName: resolvedOrbital.entity.collection
    };
    eventNamespaces[orbitalName] = {};
    for (const resolvedTrait of resolvedOrbital.traits) {
      const traitName = resolvedTrait.trait.name;
      const namespace = {
        emits: {},
        listens: {}
      };
      if (namespaceEvents && resolvedTrait.source.type === "imported") {
        const emits = resolvedTrait.trait.emits ?? [];
        for (const emit of emits) {
          const eventName = typeof emit === "string" ? emit : emit.event;
          namespace.emits[eventName] = `${orbitalName}.${traitName}.${eventName}`;
        }
        const listens = resolvedTrait.trait.listens ?? [];
        for (const listen of listens) {
          namespace.listens[listen.event] = listen.event;
        }
      }
      eventNamespaces[orbitalName][traitName] = namespace;
    }
    const preprocessedOrbital = {
      name: orbitalName,
      description: resolvedOrbital.original.description,
      visual_prompt: resolvedOrbital.original.visual_prompt,
      // The orbital's skin (registry key or inline ThemeDefinition) — dropped
      // by this field-by-field rebuild until 2026-09-01, so `/api/schema`
      // served theme-less orbitals and OrbitalThemeProvider no-opped.
      theme: resolvedOrbital.original.theme,
      // Resolved entity (always inline now)
      entity: resolvedOrbital.entity,
      // Resolved traits (inline definitions)
      traits: (resolvedOrbital.traits || []).map((rt) => {
        if (rt.config || rt.linkedEntity) {
          return {
            ref: rt.trait.name,
            config: rt.config,
            linkedEntity: rt.linkedEntity,
            // Include the resolved trait definition for runtime
            _resolved: rt.trait
          };
        }
        return rt.trait;
      }),
      // Resolved pages (inline definitions with path overrides applied)
      pages: resolvedOrbital.pages.map((rp) => rp.page),
      // Preserve other fields
      exposes: resolvedOrbital.original.exposes,
      domainContext: resolvedOrbital.original.domainContext,
      design: resolvedOrbital.original.design,
      // Gap #22: pass through auxiliary entities so OrbitalServerRuntime's
      // mock-seed branch registers SearchResult / FilterTarget / PagedItem
      // alongside the molecule's primary entity — and DERIVE them for
      // no-rebind imported traits. L1-emitted registry .orbs carry no
      // auxiliaryEntities (only the Rust inline phase adds them, and the
      // runtime path never runs it), so a trait imported without `-> Entity`
      // stays bound to its atom's own entity while that entity was never
      // registered or seeded here — hard zero rows.
      auxiliaryEntities: deriveAuxiliaryEntities(resolvedOrbital)
    };
    preprocessedOrbitals.push(preprocessedOrbital);
  }
  const preprocessedSchema = {
    ...schema,
    orbitals: preprocessedOrbitals
  };
  return {
    success: true,
    data: {
      schema: preprocessedSchema,
      entitySharing,
      eventNamespaces,
      warnings
    }
  };
}
function deriveAuxiliaryEntities(resolvedOrbital) {
  const combined = [...resolvedOrbital.original.auxiliaryEntities ?? []];
  const seen = /* @__PURE__ */ new Set([resolvedOrbital.entity.name]);
  for (const aux of combined) {
    if (typeof aux !== "string" && "name" in aux && typeof aux.name === "string") {
      seen.add(aux.name);
    }
  }
  for (const rt of resolvedOrbital.traits) {
    if (rt.source.type !== "imported" || rt.linkedEntity) continue;
    const imported = resolvedOrbital.imports.orbitals.get(rt.source.alias);
    const entity = imported?.orbital?.entity;
    if (!entity || typeof entity === "string" || !("fields" in entity) || !entity.name) continue;
    if (seen.has(entity.name)) continue;
    seen.add(entity.name);
    combined.push(entity);
  }
  return combined.length > 0 ? combined : void 0;
}
function getIsolatedCollectionName(orbitalName, entitySharing) {
  const info = entitySharing[orbitalName];
  if (!info) {
    throw new Error(`Unknown orbital: ${orbitalName}`);
  }
  if (info.persistence === "runtime") {
    return `${orbitalName}_${info.entityName}`;
  }
  return info.collectionName || info.entityName.toLowerCase() + "s";
}
function getNamespacedEvent(orbitalName, traitName, eventName, eventNamespaces) {
  const orbitalNs = eventNamespaces[orbitalName];
  if (!orbitalNs) return eventName;
  const traitNs = orbitalNs[traitName];
  if (!traitNs) return eventName;
  return traitNs.emits[eventName] || eventName;
}
function isNamespacedEvent(eventName) {
  return eventName.includes(".");
}
function parseNamespacedEvent(eventName) {
  const parts = eventName.split(".");
  if (parts.length === 3) {
    return { orbital: parts[0], trait: parts[1], event: parts[2] };
  }
  if (parts.length === 2) {
    return { trait: parts[0], event: parts[1] };
  }
  return { event: eventName };
}

// src/PersistenceAdapter.ts
var InMemoryPersistence = class {
  data = /* @__PURE__ */ new Map();
  idCounter = 0;
  /**
   * Seed the store with pre-existing rows.
   *
   * Accepts either a plain `Record<entityType, EntityRow[]>` or an iterable
   * of `[entityType, EntityRow[]]` entries. Rows without an `id` get one
   * generated at insert time; rows with an `id` keep it (so re-seeding
   * after a schema rebuild preserves identities used in render bindings).
   */
  seed(seedData) {
    const entries = Symbol.iterator in Object(seedData) ? seedData : Object.entries(seedData);
    for (const [entityType, rows] of entries) {
      if (!this.data.has(entityType)) {
        this.data.set(entityType, /* @__PURE__ */ new Map());
      }
      const collection = this.data.get(entityType);
      for (const row of rows) {
        const id = row.id || `${entityType}-${++this.idCounter}`;
        collection.set(id, { ...row, id });
      }
    }
  }
  async create(entityType, data) {
    const id = data.id || `${entityType}-${++this.idCounter}`;
    if (!this.data.has(entityType)) {
      this.data.set(entityType, /* @__PURE__ */ new Map());
    }
    this.data.get(entityType).set(id, { ...data, id });
    return { id };
  }
  async update(entityType, id, data) {
    const collection = this.data.get(entityType);
    if (collection?.has(id)) {
      const existing = collection.get(id);
      collection.set(id, { ...existing, ...data });
    }
  }
  async delete(entityType, id) {
    this.data.get(entityType)?.delete(id);
  }
  async getById(entityType, id) {
    return this.data.get(entityType)?.get(id) || null;
  }
  async list(entityType) {
    const collection = this.data.get(entityType);
    return collection ? Array.from(collection.values()) : [];
  }
  /**
   * Snapshot the entire store as a plain object (entityType → rows).
   * Useful for feeding a fresh render-time binding layer with the
   * current persistence view.
   */
  snapshot() {
    const out = {};
    for (const [entityType, collection] of this.data) {
      out[entityType] = Array.from(collection.values());
    }
    return out;
  }
};

export { EffectExecutor, EventBus, HANDLER_MANIFEST, InMemoryPersistence, LIFECYCLE_EVENTS, MockPersistenceAdapter, StateMachineManager, TickScheduler, buildEmitsFromTraits, buildSourceMatcher, collectDeclaredConfigDefaults, collectDeclaredEntityDefaults, createInitialTraitState, createMockPersistence, createTestExecutor, createTickScheduler, createUnifiedLoader, eventRouteKey, findEntityAmongOrbitals, findInitialState, findTransition, formatPayloadValidationError, getIsolatedCollectionName, getNamespacedEvent, isBrowser, isElectron, isNamespacedEvent, isNode, isValidDurationString, normalizeEventKey, parseDurationString, parseListenSource, parseNamespacedEvent, parseOrbitalTraits, preprocessSchema, processEvent, runTraitCascade, selectDispatchCandidates, validateEventPayload, validatePayloadShapes };
